/**
 * AdminPage — System administration dashboard
 * Tabs: System Health | Users | Teams | Audit Log
 * Protected: only accessible to users with role === 'admin'
 */
import { useState, useEffect, useCallback, Fragment } from 'react';
import {
  Shield, Activity, Users, Server, Database, Key,
  CheckCircle, XCircle, AlertTriangle, RefreshCw,
  ChevronLeft, ChevronRight, Search, MoreVertical,
  Trash2, UserCheck, UserX, Crown, Eye, EyeOff,
  Cpu, Clock, Zap, TrendingUp, MessageSquare, Bell,
  Lock, Unlock, Terminal, DollarSign, BarChart2, Download,
  Mail, Pencil, Ban, UserMinus, Settings, MapPin, CreditCard,
  UsersRound, Plus, X, ChevronDown, ChevronUp, Send, Receipt, FlaskConical, Mic, Radio,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext.jsx';
import api from '../services/api.js';
import toast from 'react-hot-toast';
import { formatDistanceToNow, format } from 'date-fns';
import ModalCloseButton from '../components/shared/ModalCloseButton.jsx';
// 2026-09-28 — League demo mode (fictional league for sales demos).
import LeagueDemoPanel from '../components/admin/LeagueDemoPanel.jsx';
import RecordingsTab from '../components/admin/RecordingsTab.jsx';
import StatsAiTab from '../components/admin/StatsAiTab.jsx';
import PressBoxAdminTab from '../components/admin/PressBoxAdminTab.jsx';

// ─── Design tokens (Zen Sporty) ───────────────────────────────────────────────
const T = {
  bg:       'var(--bg)',
  surface:  'var(--surface)',
  s2:       'var(--surface-2)',
  s3:       'var(--surface-3)',
  ice:      'var(--ice)',
  goal:     'var(--goal)',
  penalty:  'var(--penalty)',
  puck:     'var(--puck)',
  fire:     'var(--fire)',
  zen:      'var(--zen)',
  text:     'var(--text)',
  text2:    'var(--text-2)',
  text3:    'var(--text-3)',
  border:   'var(--border)',
  radius:   'var(--card-radius)',
  // 2026-10-24 — names the admin tabs (Press Box, Stats AI, Recordings) read
  // but this object never defined, so their dimmed text fell back to the
  // default colour: aliases of the tokens above
  textDim:  'var(--text-2)',
  s1:       'var(--surface)',
  surface2: 'var(--surface-2)',
};

// ─── Shared components ────────────────────────────────────────────────────────
const ROLE_COLORS = {
  admin:   'var(--penalty)',
  coach:   'var(--ice)',
  manager: 'var(--puck)',
  player:  'var(--goal)',
};

const Card = ({ children, style = {}, glow }) => (
  <div style={{
    background: T.surface,
    border: `1px solid ${glow ? `${glow}30` : T.border}`,
    borderRadius: T.radius,
    boxShadow: glow ? `0 0 24px ${glow}12, var(--shadow-card)` : 'var(--shadow-card)',
    ...style,
  }}>
    {children}
  </div>
);

const Label = ({ children, style = {} }) => (
  <span style={{
    fontFamily: 'var(--font-display)',
    fontSize: 10, fontWeight: 700, letterSpacing: '0.18em',
    textTransform: 'uppercase', color: T.text2, ...style,
  }}>{children}</span>
);

const Badge = ({ children, color = T.ice }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 8px', borderRadius: 6,
    background: `${color}18`, border: `1px solid ${color}35`,
    fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700,
    letterSpacing: '0.1em', textTransform: 'uppercase', color,
  }}>{children}</span>
);

const Pill = ({ on }) => (
  <span style={{
    display: 'inline-block', width: 8, height: 8, borderRadius: 4,
    background: on ? T.goal : T.penalty,
    boxShadow: `0 0 6px ${on ? T.goal : T.penalty}`,
  }} />
);

const StatOrb = ({ icon: Icon, label, value, sub, color = T.ice }) => (
  <Card style={{ padding: '20px 24px', textAlign: 'center' }} glow={color}>
    <div style={{
      width: 40, height: 40, borderRadius: 12, margin: '0 auto 12px',
      background: `${color}18`, border: `1px solid ${color}30`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon size={18} color={color} />
    </div>
    <div style={{
      fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 700,
      letterSpacing: '-0.01em', color: T.text, lineHeight: 1,
    }}>{value}</div>
    <Label style={{ display: 'block', marginTop: 4 }}>{label}</Label>
    {sub && <div style={{ fontSize: 11, color: T.text2, marginTop: 4 }}>{sub}</div>}
  </Card>
);

const TabButton = ({ active, onClick, icon: Icon, label, count, urgent }) => (
  <button onClick={onClick} style={{
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 18px', borderRadius: 10,
    background: active ? `${T.ice}18` : 'transparent',
    border: `1px solid ${active ? `${T.ice}35` : 'transparent'}`,
    color: active ? T.ice : T.text2,
    fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
    letterSpacing: '0.1em', textTransform: 'uppercase',
    cursor: 'pointer', transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  }}>
    <Icon size={14} />
    {label}
    {count !== undefined && (
      // Apr 2026 — `urgent` paints the badge red instead of the
      // default text-color. Used by the Events tab to flag unresolved
      // critical/error events even when the tab isn't active.
      <span style={{
        background: urgent ? T.penalty : (active ? T.ice : T.text2),
        color: urgent ? '#fff' : (active ? '#000' : T.surface),
        borderRadius: 10, padding: '1px 6px',
        fontSize: 10, fontWeight: 800,
      }}>{count}</span>
    )}
  </button>
);

const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, backdropFilter: 'blur(4px)',
  }}>
    <Card style={{ padding: 32, maxWidth: 400, width: '90%' }} glow={T.fire}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <AlertTriangle size={24} color={T.fire} />
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: T.text }}>
          Confirm Action
        </span>
      </div>
      <p style={{ color: T.text2, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>{message}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{
          padding: '8px 18px', borderRadius: 8, background: T.s3,
          border: `1px solid ${T.border}`, color: T.text2,
          fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
        }}>Cancel</button>
        <button onClick={onConfirm} style={{
          padding: '8px 18px', borderRadius: 8,
          background: `${T.fire}20`, border: `1px solid ${T.fire}50`,
          color: T.fire,
          fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
        }}>Delete</button>
      </div>
    </Card>
  </div>
);

// ─── TAB 1: System Health ─────────────────────────────────────────────────────
function HealthTab({ stats, loading, onRefresh }) {
  if (loading) return <LoadingGrid />;
  if (!stats)  return null;

  const { system, users, teams, games, messages, notifications, sessions } = stats;
  const enc = system.encryption;

  const formatUptime = (s) => {
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600),
          m = Math.floor((s % 3600) / 60);
    return [d && `${d}d`, h && `${h}h`, `${m}m`].filter(Boolean).join(' ');
  };

  return (
    <div>
      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        <StatOrb icon={Users}        label="Users"       value={users.total}        sub={`${users.active} active`}    color={T.ice} />
        <StatOrb icon={Shield}       label="Teams"       value={teams.total}        sub="registered"                  color={T.zen} />
        <StatOrb icon={Activity}     label="Games"       value={games.total}        sub={`${games.completed} done`}   color={T.goal} />
        <StatOrb icon={MessageSquare}label="Messages"    value={messages.total}     sub="chat total"                  color={T.puck} />
        <StatOrb icon={Bell}         label="Notifs"      value={notifications.total}sub={`${notifications.read} read`}color={T.zen} />
        <StatOrb icon={Key}          label="Sessions"    value={sessions.active}    sub="active tokens"               color={T.ice} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Encryption status */}
        <Card style={{ padding: 24 }} glow={enc.keyLoaded ? T.goal : T.fire}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Lock size={16} color={enc.keyLoaded ? T.goal : T.fire} />
            <Label>Encryption</Label>
            <div style={{ marginLeft: 'auto' }}>
              {enc.keyLoaded
                ? <Badge color={T.goal}>KEY LOADED</Badge>
                : <Badge color={T.fire}>NO KEY</Badge>}
            </div>
          </div>
          {[
            ['Algorithm',   enc.algorithm],
            ['IV Bytes',    `${enc.ivBytes} bytes (random per value)`],
            ['Auth Tag',    `${enc.tagBytes} bytes (GCM integrity)`],
            ['Key Status',  enc.keyLoaded ? '✓ 32-byte Fernet-style key loaded' : '✗ ENCRYPTION_KEY missing'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 13, color: T.text2 }}>{k}</span>
              <span style={{ fontSize: 13, color: T.text, fontFamily: 'var(--font-mono)' }}>{v}</span>
            </div>
          ))}
        </Card>

        {/* System info */}
        <Card style={{ padding: 24 }} glow={T.ice}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Server size={16} color={T.ice} />
            <Label>System</Label>
          </div>
          {[
            ['Node.js',    system.nodeVersion],
            ['PostgreSQL', system.postgres],
            ['PG Version', system.pgVersionNum],
            ['Uptime',     formatUptime(system.uptimeSeconds)],
            ['Heap',       `${system.memoryMB} MB`],
            ['New Users',  `${users.newToday} today`],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 13, color: T.text2 }}>{k}</span>
              <span style={{ fontSize: 13, color: T.text, fontFamily: 'var(--font-mono)' }}>{v}</span>
            </div>
          ))}
        </Card>

        {/* User breakdown */}
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Users size={16} color={T.ice} />
            <Label>User Breakdown</Label>
          </div>
          {[
            ['Total users',    users.total,    T.text],
            ['Active',         users.active,   T.goal],
            ['Verified email', users.verified, T.ice],
            ['Admins',         users.admins,   T.puck],
            ['New (24h)',      users.newToday, T.zen],
          ].map(([k, v, c]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 13, color: T.text2 }}>{k}</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: c, fontFamily: 'var(--font-display)' }}>{v}</span>
            </div>
          ))}
        </Card>

        {/* Games breakdown */}
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Activity size={16} color={T.ice} />
            <Label>Activity</Label>
          </div>
          {[
            ['Total games',     games.total,            T.text],
            ['Completed',       games.completed,        T.goal],
            ['Upcoming',        games.upcoming,         T.ice],
            ['Chat messages',   messages.total,         T.zen],
            ['Notifications',   notifications.total,    T.puck],
            ['Active sessions', sessions.active,        T.fire],
          ].map(([k, v, c]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 13, color: T.text2 }}>{k}</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: c, fontFamily: 'var(--font-display)' }}>{v}</span>
            </div>
          ))}
        </Card>

      </div>
    </div>
  );
}

// ─── TAB 2: Users ─────────────────────────────────────────────────────────────
function UsersTab({ currentUserId }) {
  const [users,      setUsers]      = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [updating,   setUpdating]   = useState({});
  const [confirm,    setConfirm]    = useState(null); // { userId, action, label }
  const [editEmail,  setEditEmail]  = useState(null); // { userId, name, email } | null
  const [deleteTarget, setDeleteTarget] = useState(null); // { userId, name }
  const [banTarget,    setBanTarget]    = useState(null); // { userId, name, email }
  const [banReason,    setBanReason]    = useState('');
  // Apr 2026 — "Manage Teams" modal target. Set via the UsersRound
  // button on each row. Null = modal closed. { userId, name } shape.
  const [manageTarget, setManageTarget] = useState(null);
  // Apr 2026 — expandable user details. Click anywhere on a user
  // row (except action buttons) to inline-expand a panel showing
  // their team memberships + season memberships + session info.
  // Lazy-loaded on first expand and cached so a second collapse/
  // expand cycle is instant. Cache keyed by userId; set to
  // 'loading' while a fetch is in flight, the data object once
  // resolved, or 'error' if it failed.
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [expandedData,   setExpandedData]   = useState({});

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      // Server now handles search filtering across all pages (up to 500 fetched, decrypted,
      // filtered, then re-paginated). Pass the search term so it can filter server-side.
      const limit = 25;
      const params = new URLSearchParams({ page, limit });
      if (roleFilter) params.set('role', roleFilter);
      if (search) params.set('search', search);
      const { data } = await api.get(`/admin/users?${params}`);
      setUsers(data.users);
      setPagination(data.pagination);
    } catch { toast.error('Failed to load users'); }
    setLoading(false);
  }, [roleFilter, search]);

  useEffect(() => { load(1); }, [load]);

  const update = async (userId, patch, label) => {
    setUpdating(u => ({ ...u, [userId]: true }));
    try {
      const { data } = await api.put(`/admin/users/${userId}`, patch);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...data } : u));
      toast.success(`${label} updated`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    }
    setUpdating(u => ({ ...u, [userId]: false }));
  };

  const saveEmail = async (e) => {
    e.preventDefault();
    if (!editEmail) return;
    const newEmail = editEmail.email.trim().toLowerCase();
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      return toast.error('Enter a valid email address');
    }
    setUpdating(u => ({ ...u, [editEmail.userId]: true }));
    try {
      const { data } = await api.put(`/admin/users/${editEmail.userId}`, { email: newEmail });
      setUsers(prev => prev.map(u => u.id === editEmail.userId ? { ...u, ...data } : u));
      toast.success('Email updated');
      setEditEmail(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update email');
    }
    setUpdating(u => ({ ...u, [editEmail.userId]: false }));
  };

  const deleteUser = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/users/${deleteTarget.userId}`);
      setUsers(prev => prev.filter(u => u.id !== deleteTarget.userId));
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const banUser = async (e) => {
    e.preventDefault();
    if (!banTarget) return;
    try {
      await api.post('/admin/banned-emails', { email: banTarget.email, reason: banReason.trim() || null });
      // Also deactivate in local state
      setUsers(prev => prev.map(u => u.id === banTarget.userId ? { ...u, isActive: false } : u));
      toast.success(`${banTarget.email} banned`);
      setBanTarget(null);
      setBanReason('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to ban email');
    }
  };

  // Apr 2026 — toggle inline-expand on a user row.
  // Lazy-loads memberships on first expand; cached afterward so a
  // second expand is instant. Closing doesn't clear the cache (we
  // assume the cost of holding a few small JSON blobs is cheaper
  // than a re-fetch latency hit).
  const toggleExpand = async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    if (expandedData[userId] && expandedData[userId] !== 'error') return;
    setExpandedData(prev => ({ ...prev, [userId]: 'loading' }));
    try {
      // Apr 2026 — fetch memberships + login history in parallel.
      // Both feed the same expanded panel; doing them in parallel
      // halves the latency. Login history is allowed to fail
      // independently — if security_logs is unreadable for some
      // reason (e.g. a bad encrypted blob), we still show
      // memberships rather than 500'ing the whole panel.
      const [memResp, histResp] = await Promise.allSettled([
        api.get(`/admin/users/${userId}/memberships`),
        api.get(`/admin/users/${userId}/login-history?limit=50`),
      ]);
      const merged = {
        ...(memResp.status === 'fulfilled' ? memResp.value.data : {}),
        loginHistory: histResp.status === 'fulfilled'
          ? histResp.value.data.entries
          : null,
      };
      setExpandedData(prev => ({ ...prev, [userId]: merged }));
    } catch (err) {
      setExpandedData(prev => ({ ...prev, [userId]: 'error' }));
      toast.error(err.response?.data?.error || 'Failed to load user details');
    }
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T.text2 }} />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name or email…"
            style={{
              width: '100%', padding: '9px 12px 9px 34px',
              background: T.s2, border: `1px solid ${T.border}`, borderRadius: 10,
              color: T.text, fontSize: 13, outline: 'none',
            }}
          />
        </div>
        <select
          value={roleFilter}
          onChange={e => { setRoleFilter(e.target.value); load(1); }}
          style={{
            padding: '9px 14px', background: T.s2, border: `1px solid ${T.border}`,
            borderRadius: 10, color: T.text, fontSize: 13, cursor: 'pointer',
          }}
        >
          <option value="">All roles</option>
          <option value="admin">Admin</option>
          <option value="player">Player</option>
          <option value="coach">Coach</option>
          <option value="spare">Spare</option>
        </select>
        <button onClick={() => load(pagination.page)} style={{
          padding: '9px 14px', background: T.s2, border: `1px solid ${T.border}`,
          borderRadius: 10, color: T.text2, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <RefreshCw size={13} /> Refresh
        </button>
        {/* Apr 2026 — Copy emails for the CURRENT page of results.
            Useful for admins who need to bulk-message users, export
            for a marketing tool, etc. Honors active filters/search,
            so admins can search "coach" then copy just coach emails.

            Two modes:
              • Click → copies comma-separated (good for "To:" line)
              • Shift+Click → copies newline-separated (good for spreadsheet
                pasting, one email per row)
            Tooltip explains the modifier. The button shows the current
            count so admins know how many will be copied. */}
        <button
          onClick={(e) => {
            const emails = users
              .map(u => u.email)
              // Defensive — admin endpoint returns "(encrypted)" for
              // users whose email_encrypted couldn't be decrypted.
              // Don't copy those; they aren't real addresses.
              .filter(e => e && !e.includes('(encrypted)'));
            if (emails.length === 0) {
              toast.error('No emails to copy');
              return;
            }
            // Newline-separated when Shift held; comma-separated otherwise.
            const sep = e.shiftKey ? '\n' : ', ';
            const text = emails.join(sep);
            // Modern clipboard API. Falls back to a hidden textarea
            // for older browsers (still occasionally seen on the
            // Capacitor wrapper). Fire-and-forget — toast on result.
            const writeFallback = () => {
              try {
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                return true;
              } catch { return false; }
            };
            const ok = (s) => toast.success(`Copied ${emails.length} email${emails.length === 1 ? '' : 's'} (${e.shiftKey ? 'newline' : 'comma'}-separated)`);
            const fail = () => toast.error('Failed to copy to clipboard');
            if (navigator.clipboard?.writeText) {
              navigator.clipboard.writeText(text).then(ok).catch(() => writeFallback() ? ok() : fail());
            } else {
              writeFallback() ? ok() : fail();
            }
          }}
          title="Click: copy as comma-separated (To: line). Shift+Click: copy as newline-separated (spreadsheet rows)."
          disabled={users.length === 0}
          style={{
            padding: '9px 14px', background: T.ice + '22',
            border: `1px solid ${T.ice}55`,
            borderRadius: 10, color: T.ice,
            cursor: users.length === 0 ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 600,
            opacity: users.length === 0 ? 0.4 : 1,
          }}>
          <Mail size={13} /> Copy {users.length} email{users.length === 1 ? '' : 's'}
        </button>
      </div>

      {/* Table */}
      <Card>
        {loading ? <LoadingGrid rows={5} /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  {['User', 'Role', 'Status', 'Teams', 'Sessions', 'Joined', 'Last login', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left' }}>
                      <Label>{h}</Label>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isExpanded = expandedUserId === u.id;
                  const detail     = expandedData[u.id];
                  return (
                  <Fragment key={u.id}>
                  <tr style={{
                    borderBottom: isExpanded ? 'none' : `1px solid ${T.border}`,
                    background: u.id === currentUserId ? `${T.ice}06` : 'transparent',
                    transition: 'background 0.1s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = `${T.ice}08`}
                  onMouseLeave={e => e.currentTarget.style.background = u.id === currentUserId ? `${T.ice}06` : 'transparent'}
                  onClick={() => toggleExpand(u.id)}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {/* Apr 2026 — chevron indicator for expandable
                            row state. Doesn't need its own click
                            handler since the whole <tr> is clickable;
                            stopPropagation on the inner action buttons
                            below ensures clicking those doesn't toggle. */}
                        <span style={{ color: T.text2, display: 'inline-flex' }}>
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </span>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{u.name}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2, marginLeft: 22 }}>
                        <span style={{ fontSize: 12, color: T.text2, fontFamily: 'var(--font-mono)' }}>{u.email}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditEmail({ userId: u.id, name: u.name, email: u.email }); }}
                          title="Edit email address"
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 18, height: 18, borderRadius: 4,
                            background: 'transparent', border: 'none',
                            color: T.text2, cursor: 'pointer', flexShrink: 0,
                            opacity: 0.5,
                          }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = T.ice; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.color = T.text2; }}
                        >
                          <Pencil size={10} />
                        </button>
                      </div>
                      {u.id === currentUserId && (
                        <span style={{ fontSize: 10, color: T.puck, fontFamily: 'var(--font-display)', letterSpacing: '0.1em' }}>YOU</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <Badge color={ROLE_COLORS[u.role] || T.ice}>{u.role}</Badge>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Pill on={u.isActive} />
                        <span style={{ fontSize: 12, color: u.isActive ? T.goal : T.penalty }}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {!u.isVerified && (
                        <div style={{ fontSize: 11, color: T.puck, marginTop: 2 }}>Unverified email</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', color: T.text, fontSize: 14 }}>{u.teamCount}</td>
                    <td style={{ padding: '12px 16px', color: T.text, fontSize: 14 }}>{u.activeSessions}</td>
                    <td style={{ padding: '12px 16px', color: T.text2, fontSize: 12 }}>
                      {formatDistanceToNow(new Date(u.createdAt), { addSuffix: true })}
                    </td>
                    {/* v2.5.84 — Last login column. Null for users who
                        have never logged in (e.g. invitee never accepted
                        the invite). formatDistanceToNow on null throws,
                        so we guard explicitly and show an em-dash. */}
                    <td style={{ padding: '12px 16px', color: T.text2, fontSize: 12 }}>
                      {u.lastLogin
                        ? formatDistanceToNow(new Date(u.lastLogin), { addSuffix: true })
                        : <span style={{ color: T.text3 || T.text2, fontStyle: 'italic' }}>Never</span>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {/* Toggle active */}
                        <button
                          disabled={updating[u.id] || u.id === currentUserId}
                          onClick={(e) => { e.stopPropagation(); update(u.id, { isActive: !u.isActive }, u.isActive ? "Deactivated" : "Activated"); }}
                          title={u.isActive ? 'Deactivate' : 'Activate'}
                          style={{
                            padding: '6px', borderRadius: 7,
                            background: u.isActive ? `${T.penalty}15` : `${T.goal}15`,
                            border: `1px solid ${u.isActive ? T.penalty : T.goal}30`,
                            color: u.isActive ? T.penalty : T.goal,
                            cursor: updating[u.id] || u.id === currentUserId ? 'not-allowed' : 'pointer',
                            opacity: u.id === currentUserId ? 0.4 : 1,
                          }}
                        >
                          {u.isActive ? <UserX size={13} /> : <UserCheck size={13} />}
                        </button>
                        {/* Verify email */}
                        {!u.isVerified && (
                          <button
                            disabled={updating[u.id]}
                            onClick={(e) => { e.stopPropagation(); update(u.id, { isVerified: true }, "Email verified"); }}
                            title="Mark email verified"
                            style={{
                              padding: '6px', borderRadius: 7,
                              background: `${T.ice}15`, border: `1px solid ${T.ice}30`,
                              color: T.ice, cursor: 'pointer',
                            }}
                          >
                            <CheckCircle size={13} />
                          </button>
                        )}
                        {/* Promote to admin */}
                        {u.role !== 'admin' && (
                          <button
                            disabled={updating[u.id]}
                            onClick={(e) => { e.stopPropagation(); update(u.id, { role: "admin" }, "Promoted to admin"); }}
                            title="Promote to admin"
                            style={{
                              padding: '6px', borderRadius: 7,
                              background: `${T.fire}15`, border: `1px solid ${T.fire}30`,
                              color: T.fire, cursor: 'pointer',
                            }}
                          >
                            <Crown size={13} />
                          </button>
                        )}
                        {/* Ban email */}
                        {u.id !== currentUserId && (
                          <button
                            disabled={updating[u.id]}
                            onClick={(e) => { e.stopPropagation(); setBanTarget({ userId: u.id, name: u.name, email: u.email }); setBanReason(''); }}
                            title="Ban this email address"
                            style={{
                              padding: '6px', borderRadius: 7,
                              background: 'rgba(255,149,0,0.12)', border: '1px solid rgba(255,149,0,0.25)',
                              color: 'var(--puck)', cursor: 'pointer',
                            }}
                          >
                            <Ban size={13} />
                          </button>
                        )}
                        {/* Apr 2026 — Manage Teams: lets admin assign
                            this user to a team + seasons, or remove them
                            from a team. Exists primarily to repair orphaned
                            signups (user registered but invite never
                            consumed) without waiting for next login.
                            Available for all users (including other admins)
                            since admin access is already required to see
                            this row at all. */}
                        <button
                          disabled={updating[u.id]}
                          onClick={(e) => { e.stopPropagation(); setManageTarget({ userId: u.id, name: u.name, email: u.email }); }}
                          title="Manage team memberships"
                          style={{
                            padding: '6px', borderRadius: 7,
                            background: `${T.ice}15`, border: `1px solid ${T.ice}30`,
                            color: T.ice, cursor: 'pointer',
                          }}
                        >
                          <UsersRound size={13} />
                        </button>
                        {/* Delete user */}
                        {u.id !== currentUserId && (
                          <button
                            disabled={updating[u.id]}
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget({ userId: u.id, name: u.name }); }}
                            title="Permanently delete user"
                            style={{
                              padding: '6px', borderRadius: 7,
                              background: `${T.penalty}12`, border: `1px solid ${T.penalty}25`,
                              color: T.penalty, cursor: 'pointer',
                            }}
                          >
                            <UserMinus size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Apr 2026 — expandable details panel. Renders
                      only when isExpanded === true. The colSpan
                      matches the header column count (8 as of
                      v2.5.84 — added Last login). Background shaded
                      slightly to visually anchor it to its parent
                      row. Lazy-loaded via toggleExpand which hits
                      /admin/users/:id/memberships and caches the
                      result. */}
                  {isExpanded && (
                    <tr style={{
                      borderBottom: `1px solid ${T.border}`,
                      background: `${T.ice}04`,
                    }}>
                      <td colSpan={8} style={{ padding: '14px 18px 18px 38px' }}>
                        <UserDetailPanel
                          user={u}
                          detail={detail}
                          onManage={() => setManageTarget({ userId: u.id, name: u.name, email: u.email })}
                        />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      <Pagination pagination={pagination} onPage={load} />

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:60,padding:16 }}
          onClick={() => setDeleteTarget(null)}>
          <div style={{ background:T.s1,border:`1px solid ${T.penalty}40`,borderRadius:16,padding:28,maxWidth:400,width:'100%' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:16 }}>
              <AlertTriangle size={22} style={{ color:T.penalty,flexShrink:0 }} />
              <span style={{ fontFamily:'var(--font-display)',fontSize:17,fontWeight:700,color:T.text,letterSpacing:'0.04em' }}>Delete Account</span>
            </div>
            <p style={{ fontSize:14,color:T.text2,lineHeight:1.6,marginBottom:8 }}>
              Permanently delete <strong style={{ color:T.text }}>{deleteTarget.name}</strong>? This removes all their data — teams, availability, sessions — and cannot be undone.
            </p>
            <p style={{ fontSize:12,color:T.penalty,marginBottom:24 }}>⚠ This action is irreversible.</p>
            <div style={{ display:'flex',gap:10,justifyContent:'flex-end' }}>
              <button onClick={() => setDeleteTarget(null)} style={{ padding:'8px 18px',borderRadius:9,background:T.s2,border:`1px solid ${T.border}`,color:T.text2,cursor:'pointer',fontWeight:600,fontSize:13 }}>Cancel</button>
              <button onClick={deleteUser} style={{ padding:'8px 18px',borderRadius:9,background:`${T.penalty}20`,border:`1px solid ${T.penalty}50`,color:T.penalty,cursor:'pointer',fontFamily:'var(--font-display)',fontSize:12,fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase' }}>Delete Forever</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Ban Email Modal ── */}
      {banTarget && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:60,padding:16 }}
          onClick={() => setBanTarget(null)}>
          <div style={{ background:T.s1,border:`1px solid rgba(255,149,0,0.35)`,borderRadius:16,padding:28,maxWidth:420,width:'100%' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:16 }}>
              <Ban size={20} style={{ color:'var(--puck)',flexShrink:0 }} />
              <span style={{ fontFamily:'var(--font-display)',fontSize:17,fontWeight:700,color:T.text,letterSpacing:'0.04em' }}>Ban Email</span>
            </div>
            <p style={{ fontSize:14,color:T.text2,lineHeight:1.6,marginBottom:20 }}>
              Ban <strong style={{ color:T.text,fontFamily:'var(--font-mono)',fontSize:13 }}>{banTarget.email}</strong>? This prevents this address from registering or logging in. Their existing account will be deactivated.
            </p>
            <form onSubmit={banUser}>
              <label style={{ fontSize:11,fontWeight:700,color:T.text2,letterSpacing:'0.1em',textTransform:'uppercase',display:'block',marginBottom:6 }}>Reason (optional)</label>
              <input type="text" value={banReason} onChange={e => setBanReason(e.target.value)} placeholder="Spam, abuse, etc."
                style={{ width:'100%',padding:'9px 12px',background:T.s2,border:`1px solid ${T.border}`,borderRadius:9,color:T.text,fontSize:13,outline:'none',boxSizing:'border-box',marginBottom:20 }} />
              <div style={{ display:'flex',gap:10,justifyContent:'flex-end' }}>
                <button type="button" onClick={() => setBanTarget(null)} style={{ padding:'8px 18px',borderRadius:9,background:T.s2,border:`1px solid ${T.border}`,color:T.text2,cursor:'pointer',fontWeight:600,fontSize:13 }}>Cancel</button>
                <button type="submit" style={{ padding:'8px 18px',borderRadius:9,background:'rgba(255,149,0,0.2)',border:'1px solid rgba(255,149,0,0.5)',color:'var(--puck)',cursor:'pointer',fontFamily:'var(--font-display)',fontSize:12,fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase' }}>Ban Email</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {editEmail && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 500, padding: 16,
        }} onClick={() => !updating[editEmail.userId] && setEditEmail(null)}>
          <div style={{
            background: T.s1, border: `1px solid ${T.border}`,
            borderRadius: 16, padding: 28, width: '100%', maxWidth: 420,
            position: 'relative',
          }} onClick={e => e.stopPropagation()}>
            <ModalCloseButton onClose={() => setEditEmail(null)} disabled={!!updating[editEmail.userId]} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                background: `${T.ice}15`, border: `1px solid ${T.ice}30`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Mail size={16} style={{ color: T.ice }} />
              </div>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: T.text, letterSpacing: '0.04em' }}>
                  Edit Email Address
                </div>
                <div style={{ fontSize: 12, color: T.text2, marginTop: 2 }}>{editEmail.name}</div>
              </div>
            </div>

            <form onSubmit={saveEmail}>
              <label style={{ fontSize: 12, fontWeight: 600, color: T.text2, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                New Email
              </label>
              <input
                type="email"
                autoFocus
                required
                value={editEmail.email}
                onChange={e => setEditEmail(p => ({ ...p, email: e.target.value }))}
                placeholder="new@email.com"
                style={{
                  width: '100%', padding: '10px 14px',
                  background: T.s2, border: `1px solid ${T.border}`,
                  borderRadius: 10, color: T.text, fontSize: 14,
                  fontFamily: 'var(--font-mono)', outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.border = `1px solid ${T.ice}`}
                onBlur={e => e.target.style.border = `1px solid ${T.border}`}
              />
              <p style={{ fontSize: 11, color: T.text2, marginTop: 8, lineHeight: 1.5 }}>
                The email will be marked as verified automatically. The user must use this address to log in.
              </p>
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button type="button" onClick={() => setEditEmail(null)}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: 10, border: `1px solid ${T.border}`,
                    background: T.s2, color: T.text2, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                  }}>
                  Cancel
                </button>
                <button type="submit" disabled={updating[editEmail.userId]}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
                    background: T.ice, color: '#000', cursor: 'pointer',
                    fontWeight: 700, fontSize: 13,
                    fontFamily: 'var(--font-display)', letterSpacing: '0.06em',
                    opacity: updating[editEmail.userId] ? 0.6 : 1,
                  }}>
                  {updating[editEmail.userId] ? 'Saving…' : 'Save Email'}
                </button>
              </div>
            </form>
            <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
          </div>
        </div>
      )}
      {/* Apr 2026 — Manage Teams modal. Lets admin assign the user
          to a team + seasons or remove them from a team. Used to
          repair orphaned signups (where the invite never got
          consumed for whatever reason) without waiting for the
          user's next login. */}
      {manageTarget && (
        <ManageTeamsModal
          target={manageTarget}
          onClose={() => setManageTarget(null)}
        />
      )}
    </div>
  );
}

// ─── UserDetailPanel (Apr 2026) ─────────────────────────────────────────
// Inline-expanded details for a user row in the Admin Users table.
// Renders three groupings: account stats (sessions, joined date),
// team memberships (with seasons per team), and pending invites
// addressed to this user's email (a quick "ghost row" diagnosis).
//
// Receives `detail` from the parent's lazy-loaded cache, which is one of:
//   • undefined / null  — never loaded yet (caller should always pass
//     something but be defensive)
//   • 'loading'         — fetch in flight; show a small spinner
//   • 'error'           — fetch failed; show a retry hint
//   • { user, teams, pendingInvites, orphanSeasons } — payload
//     from /admin/users/:id/memberships (same shape used by the
//     ManageTeamsModal so we can share the endpoint).
//
// `onManage` is a click handler that opens the full ManageTeamsModal
// — surfaced here as a "Manage" shortcut so the admin doesn't have
// to find the action button in the row above.
function UserDetailPanel({ user, detail, onManage }) {
  if (detail === 'loading' || detail == null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: T.text2, fontSize: 12 }}>
        <div className="spinner" style={{ width: 14, height: 14 }} />
        Loading user details…
      </div>
    );
  }
  if (detail === 'error') {
    return (
      <div style={{ color: T.penalty, fontSize: 12 }}>
        Failed to load details. Click the row again to retry.
      </div>
    );
  }
  const teams           = detail.teams || [];
  const pendingInvites  = detail.pendingInvites || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Quick-stats row */}
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, color: T.text2 }}>
        <div>
          <span style={{ fontWeight: 700, color: T.text }}>{teams.length}</span> team{teams.length === 1 ? '' : 's'}
        </div>
        <div>
          <span style={{ fontWeight: 700, color: T.text }}>{user.activeSessions}</span> active session{user.activeSessions === 1 ? '' : 's'}
        </div>
        <div>
          Joined{' '}
          <span style={{ fontWeight: 700, color: T.text }}>
            {format(new Date(user.createdAt), 'MMM d, yyyy')}
          </span>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={(e) => { e.stopPropagation(); onManage(); }}
            style={{
              padding: '5px 12px', borderRadius: 7,
              background: `${T.ice}15`, border: `1px solid ${T.ice}30`,
              color: T.ice, cursor: 'pointer', fontSize: 11, fontWeight: 700,
              fontFamily: 'var(--font-display)', letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}>
            Manage Teams →
          </button>
        </div>
      </div>

      {/* Teams list — empty state when user has no memberships */}
      <div>
        <div style={{
          fontSize: 10, fontWeight: 700, color: T.text2,
          letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8,
        }}>
          Teams ({teams.length})
        </div>
        {teams.length === 0 ? (
          <div style={{ fontSize: 12, color: T.text2, fontStyle: 'italic' }}>
            Not a member of any team yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {teams.map(t => (
              <div key={t.teamMemberId} style={{
                padding: 10,
                background: T.s2,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{t.teamName}</span>
                  {t.memberRole !== 'player' && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', color: T.ice,
                      padding: '1px 5px', background: `${T.ice}15`,
                      border: `1px solid ${T.ice}30`, borderRadius: 3,
                    }}>{t.memberRole}</span>
                  )}
                  {!t.isPrimary && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', color: T.puck,
                      padding: '1px 5px', background: 'rgba(255,149,0,0.12)',
                      border: '1px solid rgba(255,149,0,0.25)', borderRadius: 3,
                    }}>Spare</span>
                  )}
                  {t.position && (
                    <span style={{ fontSize: 11, color: T.text2 }}>
                      · {t.position}
                    </span>
                  )}
                </div>
                {t.seasons && t.seasons.length > 0 && (
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {t.seasons.map(s => (
                      <span key={s.seasonMemberId} style={{
                        fontSize: 10, padding: '1px 6px', borderRadius: 3,
                        background: `${T.ice}10`, color: T.ice,
                        border: `1px solid ${T.ice}25`, fontWeight: 600,
                      }}>
                        {s.name}
                        {s.isActive === false && (
                          <span style={{ color: T.text2, marginLeft: 3 }}>(inactive)</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending invites — only if there are any. Surfaces ghost-row
          state inline so the admin can see at a glance whether the
          user has unconsumed invites. */}
      {pendingInvites.length > 0 && (
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, color: T.puck,
            letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8,
          }}>
            ⚠ Pending Invites ({pendingInvites.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {pendingInvites.map(inv => (
              <div key={inv.inviteId} style={{
                padding: '6px 10px',
                background: 'rgba(255,149,0,0.06)',
                border: '1px solid rgba(255,149,0,0.2)',
                borderRadius: 6,
                fontSize: 12, color: T.text,
              }}>
                <strong>{inv.teamName}</strong>
                <span style={{ marginLeft: 6, color: T.text2, fontSize: 11 }}>
                  {inv.memberRole}{inv.isSpare ? ' · spare' : ''}
                </span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: T.text2 }}>
            Use Manage Teams above to assign and clear these.
          </div>
        </div>
      )}

      {/* Apr 2026 — Login history. Last 50 auth events for this user
          pulled from security_logs. Decrypted server-side; shows the
          IP address, browser/OS, and timestamp for every login,
          failed login, logout, password change, etc. Useful when
          investigating suspicious account activity ("did this user
          actually log in from Russia or is that an attacker?") or
          troubleshooting access complaints ("they say they can't log
          in — let's see what their last attempt looked like").

          Login history is loaded in parallel with memberships when
          the row is expanded (see toggleExpand). If the request
          failed, `loginHistory` is null and we show an inline error;
          if it succeeded but returned empty, we show a "no recent
          activity" empty state. */}
      <div>
        <div style={{
          fontSize: 10, fontWeight: 700, color: T.text2,
          letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8,
        }}>
          Recent Logins ({detail.loginHistory?.length || 0})
        </div>
        {detail.loginHistory === null ? (
          <div style={{ fontSize: 12, color: T.penalty, fontStyle: 'italic' }}>
            Failed to load login history.
          </div>
        ) : detail.loginHistory.length === 0 ? (
          <div style={{ fontSize: 12, color: T.text2, fontStyle: 'italic' }}>
            No recorded logins yet (this user may have never logged in).
          </div>
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 4,
            maxHeight: 320, overflowY: 'auto',
            background: T.s2, borderRadius: 8,
            border: `1px solid ${T.border}`,
            padding: 6,
          }}>
            {detail.loginHistory.map(evt => {
              // Visual status: success=green, failure/blocked=red, info=muted.
              // Icons keep the table scannable.
              const statusColor =
                evt.status === 'failure' || evt.status === 'blocked' ? T.penalty :
                evt.status === 'success' ? T.goal :
                T.text2;
              // Compact human label: "Logged in" instead of "login_success".
              const PRETTY = {
                login_success:            'Logged in',
                login_failed:             'Failed login attempt',
                login_blocked:            'Login blocked',
                logout:                   'Logged out',
                token_refreshed:          'Session refreshed',
                password_reset_requested: 'Password reset requested',
                password_reset_completed: 'Password reset completed',
                password_changed:         'Password changed',
              };
              const label = PRETTY[evt.eventType] || evt.eventType.replace(/_/g, ' ');
              return (
                <div key={evt.id} style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: 10,
                  alignItems: 'center',
                  padding: '6px 10px',
                  borderRadius: 6,
                  background: evt.status === 'failure' || evt.status === 'blocked'
                    ? 'rgba(255,68,68,0.06)' : 'transparent',
                  fontSize: 11,
                }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: statusColor, flexShrink: 0,
                  }} />
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ color: T.text, fontWeight: 600, marginBottom: 2 }}>
                      {label}
                    </div>
                    <div style={{
                      display: 'flex', gap: 8, flexWrap: 'wrap',
                      color: T.text2, fontSize: 10.5,
                    }}>
                      {/* IP address — the headline forensic detail. Shown
                          in a code-style pill so it stands out. */}
                      {evt.ip ? (
                        <span style={{
                          fontFamily: 'var(--font-mono)',
                          padding: '1px 6px',
                          background: T.s3,
                          borderRadius: 4,
                          color: T.text,
                        }}>
                          {evt.ip}
                        </span>
                      ) : (
                        <span style={{ fontStyle: 'italic' }}>(no IP)</span>
                      )}
                      {/* Browser / OS / device — only show what we have.
                          UA-parser fills these in when the server
                          recorded them. */}
                      {(evt.browserFamily || evt.osFamily || evt.deviceType) && (
                        <span>
                          {[evt.browserFamily, evt.osFamily, evt.deviceType]
                            .filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{
                    textAlign: 'right', flexShrink: 0,
                    color: T.text2, fontSize: 10,
                  }}>
                    <div>{formatDistanceToNow(new Date(evt.createdAt), { addSuffix: true })}</div>
                    <div style={{
                      fontFamily: 'var(--font-mono)',
                      marginTop: 1,
                      fontSize: 9,
                    }}>
                      {format(new Date(evt.createdAt), 'MMM d HH:mm')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Feature flags (v2.5.118) — per-user toggles. The
          userName / userEmail props are display-only — they
          appear in the panel header so the admin can see at a
          glance which user they're editing. Defends against
          mistakenly toggling a flag on the wrong row, an issue
          reported in v2.5.124. */}
      <FeatureFlagsSection
        userId={user.id}
        userName={user.name}
        userEmail={user.email}
      />
    </div>
  );
}

// ─── FeatureFlagsSection (v2.5.118) ───────────────────────────────
// Per-user feature-flag toggles surfaced in the admin user-detail
// panel. Reads GET /admin/users/:userId/feature-flags which returns:
//   { feature_flags: { leagues: true, ... },
//     known: { leagues: { label, description }, ... } }
// `known` is the server's allowlist of flag keys so the UI shows
// every recognized flag (even ones not yet enabled for this user)
// with a label and a description. Toggling fires PATCH with just
// the changed key — the backend treats unspecified keys as
// untouched, so other flags aren't affected by an unrelated edit.
function FeatureFlagsSection({ userId, userName, userEmail }) {
  const [state, setState] = useState('loading'); // 'loading' | 'error' | {flags, known}
  const [saving, setSaving] = useState(null);    // key currently being saved
  // v2.5.119 — if the admin is toggling their OWN flags, we need to
  // re-fetch /auth/me so the layouts (which read user.featureFlags
  // from AuthContext) pick up the change without a page reload.
  const { user: currentUser, loadUser } = useAuth();
  const isSelf = currentUser?.id === userId;

  async function load() {
    setState('loading');
    try {
      const { data } = await api.get(`/admin/users/${userId}/feature-flags`);
      setState({
        flags: data.feature_flags || {},
        known: data.known || {},
      });
    } catch (e) {
      console.error('Load feature flags failed:', e);
      setState('error');
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  async function toggle(key, current) {
    // v2.5.125 — defensive: capture userId at call time so any
    // late re-render or stale closure can't redirect the PATCH to
    // the wrong target. Earlier reports showed the toggle UI
    // appearing to succeed (green pip) while the DB row for the
    // admin's own account stayed at feature_flags={}, suggesting
    // the PATCH may have hit a different user. Console-log the
    // resolved target so future repros are auditable.
    const targetUserId = userId;
    const targetEmail  = userEmail || '(unknown)';
    if (!targetUserId) {
      toast.error('Cannot toggle: no user selected');
      return;
    }
    console.info('[admin] feature-flag toggle',
      { key, from: current, to: !current, targetUserId, targetEmail });
    setSaving(key);
    // Optimistic update — flip locally first so the toggle feels
    // instant, then revert on error. The PATCH returns the
    // canonical feature_flags object so we re-sync at the end.
    setState(prev => prev && typeof prev === 'object'
      ? { ...prev, flags: { ...prev.flags, [key]: !current } }
      : prev);
    try {
      const { data } = await api.patch(
        `/admin/users/${targetUserId}/feature-flags`,
        { flags: { [key]: !current } }
      );
      console.info('[admin] feature-flag toggle response',
        { targetUserId, returned: data?.feature_flags });
      setState(prev => prev && typeof prev === 'object'
        ? { ...prev, flags: data.feature_flags || {} }
        : prev);
      // v2.5.119 — when toggling our OWN flag, refresh the
      // AuthContext so the layout nav updates immediately (no
      // page reload required). loadUser() re-fetches /auth/me
      // which returns the now-updated featureFlags. Fire-and-
      // forget; if it fails, the user's still-fresh next page
      // load will pick it up.
      if (currentUser?.id === targetUserId) {
        loadUser().catch(() => {});
        toast.success(
          !current
            ? `${key} enabled for you · log out and back in if nav doesn't update`
            : `${key} disabled for you · log out and back in if nav doesn't update`
        );
      } else {
        toast.success(`${key} ${!current ? 'enabled' : 'disabled'} for ${targetEmail}`);
      }
    } catch (e) {
      // Revert on failure.
      setState(prev => prev && typeof prev === 'object'
        ? { ...prev, flags: { ...prev.flags, [key]: current } }
        : prev);
      toast.error(e?.response?.data?.error || 'Could not update flag');
    } finally {
      setSaving(null);
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: T.text2,
        letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8,
      }}>
        Feature flags
        {/* v2.5.125 — show target identification. Without this,
            an admin can't tell at a glance whose flags they're
            editing if multiple users were just viewed. */}
        {userName && (
          <span style={{
            marginLeft: 8, fontSize: 11, fontWeight: 600,
            color: T.text2, textTransform: 'none', letterSpacing: 0,
          }}>
            for <span style={{ color: T.text }}>{userName}</span>
            {userEmail && (
              <span style={{ color: T.text3, fontFamily: 'var(--font-mono)' }}> · {userEmail}</span>
            )}
            {isSelf && (
              <span style={{
                marginLeft: 8, padding: '1px 6px', borderRadius: 4,
                background: `${T.ice}18`, color: T.ice, fontSize: 9,
                fontWeight: 800, letterSpacing: '0.1em',
              }}>YOU</span>
            )}
          </span>
        )}
      </div>
      {state === 'loading' && (
        <div style={{ fontSize: 12, color: T.text2 }}>
          <div className="spinner" style={{ width: 12, height: 12, display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }} />
          Loading…
        </div>
      )}
      {state === 'error' && (
        <div style={{ fontSize: 12, color: T.penalty }}>
          Failed to load flags.{' '}
          <button type="button" onClick={load} style={{
            background: 'transparent', border: 'none', color: T.ice, cursor: 'pointer',
            textDecoration: 'underline', fontSize: 12,
          }}>Retry</button>
        </div>
      )}
      {state && typeof state === 'object' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Object.entries(state.known).length === 0 ? (
            <div style={{ fontSize: 12, color: T.text3, fontStyle: 'italic' }}>
              No feature flags defined.
            </div>
          ) : (
            Object.entries(state.known).map(([key, meta]) => {
              const enabled = state.flags[key] === true;
              const isSaving = saving === key;
              // 2026-10-18 — numeric flags (pressBoxTeamLimit): a small
              // number box + Save instead of the on/off toggle.
              if (meta.type === 'number') {
                return (
                  <NumberFlagRow key={key} flagKey={key} meta={meta}
                    value={state.flags[key]} userId={userId}
                    onSaved={(flags) => setState(prev => (prev && typeof prev === 'object' ? { ...prev, flags } : prev))} />
                );
              }
              return (
                <div key={key} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px',
                  background: `${T.s1}80`,
                  border: `1px solid ${T.border}`, borderRadius: 8,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>
                      {meta.label || key}
                      <span style={{
                        marginLeft: 6, fontSize: 9, fontFamily: 'var(--font-mono)',
                        color: T.text3, fontWeight: 500,
                      }}>
                        {key}
                      </span>
                    </div>
                    {meta.description && (
                      <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>
                        {meta.description}
                      </div>
                    )}
                  </div>
                  <button type="button"
                    onClick={() => toggle(key, enabled)}
                    disabled={isSaving}
                    style={{
                      position: 'relative',
                      width: 40, height: 22, borderRadius: 11,
                      border: 'none', cursor: isSaving ? 'wait' : 'pointer',
                      background: enabled ? T.goal : `${T.text3}40`,
                      transition: 'background 120ms',
                      flexShrink: 0,
                    }}
                    aria-pressed={enabled}
                    aria-label={`Toggle ${meta.label || key}`}>
                    <span style={{
                      position: 'absolute', top: 2, left: enabled ? 20 : 2,
                      width: 18, height: 18, borderRadius: '50%',
                      background: '#fff', transition: 'left 120ms',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─── NumberFlagRow (2026-10-18) ─────────────────────────────────────────
// A numeric feature flag (pressBoxTeamLimit): a number box + Save in the
// flags list. PATCHes the same endpoint; the server validates the range.
function NumberFlagRow({ flagKey, meta, value, userId, onSaved }) {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(value == null ? '' : String(value)); }, [value]);
  const save = async () => {
    const n = Number(draft);
    if (!Number.isInteger(n) || n < (meta.min ?? 0) || n > (meta.max ?? 1000)) {
      toast.error(`Must be a whole number between ${meta.min ?? 0} and ${meta.max ?? 1000}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/users/${userId}/feature-flags`, { flags: { [flagKey]: n } });
      onSaved(data.feature_flags || {});
      toast.success(`${meta.label || flagKey} set to ${n}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not update flag');
    } finally { setBusy(false); }
  };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
      background: `${T.s1}80`, border: `1px solid ${T.border}`, borderRadius: 8,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>
          {meta.label || flagKey}
          <span style={{ marginLeft: 6, fontSize: 9, fontFamily: 'var(--font-mono)', color: T.text3, fontWeight: 500 }}>{flagKey}</span>
        </div>
        {meta.description && <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{meta.description}</div>}
      </div>
      <input type="number" min={meta.min ?? 0} max={meta.max ?? 1000} value={draft}
        onChange={(e) => setDraft(e.target.value)} disabled={busy}
        style={{ width: 70, padding: '6px 8px', background: T.s1, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 12 }}
        aria-label={meta.label || flagKey} />
      <button type="button" onClick={save} disabled={busy || String(value ?? '') === draft}
        style={{
          padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.border}`,
          background: 'transparent', color: T.ice, fontSize: 11, fontWeight: 700,
          cursor: busy ? 'wait' : 'pointer', opacity: String(value ?? '') === draft ? 0.5 : 1,
        }}>
        Save
      </button>
    </div>
  );
}

// ─── ManageTeamsModal (Apr 2026) ────────────────────────────────────────
// Admin surface for repairing / adjusting a single user's team and
// season memberships. Reads /admin/users/:id/memberships, shows:
//   • each team they're on with its seasons and a Remove button
//   • any pending invites for their email (surfacing the "ghost
//     INVITED row" so admin knows what's there)
//   • an "Add to Team" form with team + seasons + role pickers
// All mutations re-fetch on success so the UI stays in sync.
function ManageTeamsModal({ target, onClose }) {
  const [loading,    setLoading]    = useState(true);
  const [data,       setData]       = useState(null); // { user, teams, pendingInvites, orphanSeasons }
  const [teamsList,  setTeamsList]  = useState([]);    // /admin/teams result
  const [addTeamId,   setAddTeamId]   = useState('');
  const [addSeasons,  setAddSeasons]  = useState([]);  // array of season_id strings currently checked
  const [seasonsForAddTeam, setSeasonsForAddTeam] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);
  const [addMemberRole, setAddMemberRole] = useState('player');
  const [addIsSpare,    setAddIsSpare]    = useState(false);
  const [adding,        setAdding]        = useState(false);
  const [removingTeamId, setRemovingTeamId] = useState(null);

  // Load current memberships. Split from useEffect so the
  // post-mutation refetch can call it without re-wiring deps.
  const loadMemberships = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${target.userId}/memberships`);
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load memberships');
      setData(null);
    }
    setLoading(false);
  }, [target.userId]);

  useEffect(() => { loadMemberships(); }, [loadMemberships]);

  // Fetch the full teams list once — used for the "Add to Team"
  // dropdown. Limited to 100 by the backend; for a tool running at
  // small-league scale that's plenty. If we ever have > 100 teams,
  // swap this for a searchable combobox with a debounced server
  // filter.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/teams?limit=100');
        setTeamsList(data.teams || []);
      } catch {
        // Non-fatal — the add flow just won't have a pre-populated
        // picker. Admin can still manually type the team UUID if
        // needed (not exposed in UI currently; acceptable).
        setTeamsList([]);
      }
    })();
  }, []);

  // When the admin picks a team in the "Add to Team" dropdown,
  // fetch that team's seasons so they can pick which ones to
  // attach. Clear any previously-selected seasons since they'd
  // belong to a different team's universe.
  useEffect(() => {
    if (!addTeamId) {
      setSeasonsForAddTeam([]);
      setAddSeasons([]);
      return;
    }
    setLoadingSeasons(true);
    setAddSeasons([]);
    (async () => {
      try {
        const { data } = await api.get(`/admin/teams/${addTeamId}/seasons`);
        setSeasonsForAddTeam(data.seasons || []);
      } catch {
        setSeasonsForAddTeam([]);
      } finally {
        setLoadingSeasons(false);
      }
    })();
  }, [addTeamId]);

  const toggleSeason = (sid) => {
    setAddSeasons(prev =>
      prev.includes(sid) ? prev.filter(s => s !== sid) : [...prev, sid]
    );
  };

  const submitAdd = async () => {
    if (!addTeamId) return toast.error('Pick a team first');
    setAdding(true);
    try {
      const { data: res } = await api.post(`/admin/users/${target.userId}/memberships`, {
        teamId:     addTeamId,
        memberRole: addMemberRole,
        isSpare:    addIsSpare,
        seasonIds:  addSeasons,
      });
      toast.success(res.message || 'Added');
      // Reset the add form for the "one more assignment" case.
      setAddTeamId('');
      setAddSeasons([]);
      setAddMemberRole('player');
      setAddIsSpare(false);
      await loadMemberships();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add');
    }
    setAdding(false);
  };

  const submitRemove = async (teamId, teamName) => {
    if (!window.confirm(
      `Remove ${target.name} from "${teamName}"?\n\n` +
      `This deletes their team membership AND all of their seasons on this team. ` +
      `Attendance history is preserved via the tombstone system.`
    )) return;
    setRemovingTeamId(teamId);
    try {
      await api.delete(`/admin/users/${target.userId}/memberships/${teamId}`, {
        data: { confirm: 'REMOVE' },
      });
      toast.success(`Removed from "${teamName}"`);
      await loadMemberships();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove');
    }
    setRemovingTeamId(null);
  };

  // Teams the user is NOT already on — used to filter the add-team
  // dropdown so the admin doesn't accidentally pick a team the user
  // is already a member of (backend would still accept it as a
  // no-op-merge, but hiding it is cleaner UX).
  const currentTeamIds = new Set((data?.teams || []).map(t => t.teamId));
  const assignableTeams = teamsList.filter(t => !currentTeamIds.has(t.id));

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 500, padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: T.s1 || 'var(--surface)', border: `1px solid ${T.border}`,
        borderRadius: 16, padding: 0, width: '100%', maxWidth: 640,
        // Jul 2026 — plain 90vh is taller than the visible viewport on
        // mobile Safari (vh includes the area the URL bar can occupy),
        // clipping this modal's header/footer. --mobile-h is set by
        // useCapacitorPlugins.js to the true visible height on native/
        // mobile; falls back to 100dvh elsewhere (desktop-identical).
        maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', display: 'flex', flexDirection: 'column',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800,
              color: T.text, letterSpacing: '0.04em',
            }}>
              <UsersRound size={18} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8, color: T.ice }} />
              Manage Teams
            </div>
            <div style={{ fontSize: 13, color: T.text2, marginTop: 4 }}>
              {target.name} · <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{target.email}</span>
            </div>
          </div>
          <button onClick={onClose}
            style={{
              background: 'transparent', border: 'none', color: T.text2,
              cursor: 'pointer', padding: 4, display: 'flex',
            }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflow: 'auto', flex: 1 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div className="spinner mx-auto" />
              <p style={{ color: T.text2, marginTop: 12, fontSize: 13 }}>Loading memberships…</p>
            </div>
          )}

          {!loading && data && (
            <>
              {/* Current teams */}
              <div style={{ marginBottom: 22 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: T.text2,
                  letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10,
                }}>
                  Current Teams ({data.teams.length})
                </div>
                {data.teams.length === 0 && (
                  <div style={{
                    padding: 14, background: T.s2, border: `1px solid ${T.border}`,
                    borderRadius: 10, color: T.text2, fontSize: 13, textAlign: 'center',
                  }}>
                    Not a member of any team yet.
                  </div>
                )}
                {data.teams.map(t => (
                  <div key={t.teamMemberId} style={{
                    padding: 14, background: T.s2, border: `1px solid ${T.border}`,
                    borderRadius: 10, marginBottom: 8,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
                          {t.teamName}
                          {t.memberRole !== 'player' && (
                            <span style={{
                              marginLeft: 8, fontSize: 10, fontWeight: 700,
                              letterSpacing: '0.08em', textTransform: 'uppercase',
                              color: T.ice, padding: '2px 6px',
                              background: `${T.ice}15`, border: `1px solid ${T.ice}30`,
                              borderRadius: 4,
                            }}>{t.memberRole}</span>
                          )}
                          {!t.isPrimary && (
                            <span style={{
                              marginLeft: 6, fontSize: 10, fontWeight: 700,
                              letterSpacing: '0.08em', textTransform: 'uppercase',
                              color: T.puck, padding: '2px 6px',
                              background: 'rgba(255,149,0,0.12)', border: '1px solid rgba(255,149,0,0.25)',
                              borderRadius: 4,
                            }}>Spare</span>
                          )}
                        </div>
                        {t.leagueName && (
                          <div style={{ fontSize: 12, color: T.text2, marginTop: 2 }}>{t.leagueName}</div>
                        )}
                        {t.seasons.length > 0 ? (
                          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {t.seasons.map(s => (
                              <span key={s.seasonMemberId} style={{
                                fontSize: 11, padding: '2px 7px', borderRadius: 4,
                                background: `${T.ice}10`, color: T.ice,
                                border: `1px solid ${T.ice}25`, fontWeight: 600,
                              }}>
                                {s.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: 11, color: T.text2, fontStyle: 'italic', marginTop: 6 }}>
                            No season assigned
                          </div>
                        )}
                      </div>
                      <button
                        disabled={removingTeamId === t.teamId}
                        onClick={() => submitRemove(t.teamId, t.teamName)}
                        style={{
                          padding: '6px 12px', borderRadius: 8,
                          background: `${T.penalty}15`, border: `1px solid ${T.penalty}30`,
                          color: T.penalty, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                          opacity: removingTeamId === t.teamId ? 0.6 : 1, flexShrink: 0,
                        }}>
                        {removingTeamId === t.teamId ? '…' : 'Remove'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pending invites — purely informational so the admin
                  understands "there's a ghost invite here that will
                  be consumed when you assign them to this team". */}
              {data.pendingInvites && data.pendingInvites.length > 0 && (
                <div style={{ marginBottom: 22 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: T.puck,
                    letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10,
                  }}>
                    ⚠ Pending Invites ({data.pendingInvites.length})
                  </div>
                  <div style={{
                    fontSize: 12, color: T.text2, marginBottom: 10, lineHeight: 1.5,
                  }}>
                    This user has active invites that were never accepted. Assigning
                    them to the corresponding team below will automatically mark these
                    invites as accepted and clear the "INVITED" ghost row on the
                    Players page.
                  </div>
                  {data.pendingInvites.map(inv => (
                    <div key={inv.inviteId} style={{
                      padding: 10, background: 'rgba(255,149,0,0.06)',
                      border: '1px solid rgba(255,149,0,0.2)', borderRadius: 8,
                      marginBottom: 6, display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
                    }}>
                      <div style={{ fontSize: 13, color: T.text }}>
                        <strong>{inv.teamName}</strong>
                        <span style={{
                          marginLeft: 6, fontSize: 10, fontWeight: 700,
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                          color: T.text2,
                        }}>
                          {inv.memberRole}{inv.isSpare ? ' · Spare' : ''}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setAddTeamId(inv.teamId);
                          setAddMemberRole(inv.memberRole || 'player');
                          setAddIsSpare(!!inv.isSpare);
                        }}
                        style={{
                          padding: '4px 10px', borderRadius: 6,
                          background: `${T.ice}15`, border: `1px solid ${T.ice}30`,
                          color: T.ice, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                        }}>
                        Fill Add Form
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add to Team */}
              <div>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: T.text2,
                  letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10,
                }}>
                  <Plus size={12} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />
                  Add to Team
                </div>
                <div style={{
                  padding: 14, background: T.s2, border: `1px solid ${T.border}`,
                  borderRadius: 10,
                }}>
                  {/* Team dropdown */}
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.text2, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                    Team
                  </label>
                  <select
                    value={addTeamId}
                    onChange={e => setAddTeamId(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 10px', background: T.surface,
                      border: `1px solid ${T.border}`, borderRadius: 8,
                      color: T.text, fontSize: 13, marginBottom: 12,
                    }}>
                    <option value="">— Select a team —</option>
                    {assignableTeams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>

                  {/* Role + spare */}
                  <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: T.text2, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                        Role
                      </label>
                      <select
                        value={addMemberRole}
                        onChange={e => setAddMemberRole(e.target.value)}
                        style={{
                          width: '100%', padding: '8px 10px', background: T.surface,
                          border: `1px solid ${T.border}`, borderRadius: 8,
                          color: T.text, fontSize: 13,
                        }}>
                        <option value="player">Player</option>
                        <option value="coach">Coach</option>
                        {/* Apr 30 2026 (App_Change_List #1) — Manager
                            role removed from the picker. Existing
                            managers in the DB keep their permissions
                            (backend still recognises the role); we
                            just stop offering it as a new selection
                            and display existing managers as "Coach"
                            in the UI. Two-role model: Coach + Player. */}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: T.text2, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                        Classification
                      </label>
                      <select
                        value={addIsSpare ? 'spare' : 'full_time'}
                        onChange={e => setAddIsSpare(e.target.value === 'spare')}
                        style={{
                          width: '100%', padding: '8px 10px', background: T.surface,
                          border: `1px solid ${T.border}`, borderRadius: 8,
                          color: T.text, fontSize: 13,
                        }}>
                        <option value="full_time">Full-Time</option>
                        <option value="spare">Spare</option>
                      </select>
                    </div>
                  </div>

                  {/* Seasons multi-select */}
                  {addTeamId && (
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: T.text2, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                        Seasons (optional)
                      </label>
                      {loadingSeasons ? (
                        <div style={{ fontSize: 12, color: T.text2, padding: 8 }}>
                          Loading seasons…
                        </div>
                      ) : seasonsForAddTeam.length === 0 ? (
                        <div style={{ fontSize: 12, color: T.text2, padding: 8, fontStyle: 'italic' }}>
                          This team has no seasons.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {seasonsForAddTeam.map(s => (
                            <label key={s.id} style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
                              background: addSeasons.includes(s.id) ? `${T.ice}12` : 'transparent',
                              border: `1px solid ${addSeasons.includes(s.id) ? `${T.ice}30` : 'transparent'}`,
                            }}>
                              <input
                                type="checkbox"
                                checked={addSeasons.includes(s.id)}
                                onChange={() => toggleSeason(s.id)}
                                style={{ cursor: 'pointer' }} />
                              <span style={{ fontSize: 13, color: T.text }}>
                                {s.name}
                                {s.isActive === false && (
                                  <span style={{ marginLeft: 6, fontSize: 10, color: T.text2 }}>
                                    (inactive)
                                  </span>
                                )}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    disabled={adding || !addTeamId}
                    onClick={submitAdd}
                    style={{
                      width: '100%', padding: '10px 0', borderRadius: 9,
                      background: T.ice, color: '#000', border: 'none',
                      cursor: adding || !addTeamId ? 'not-allowed' : 'pointer',
                      fontWeight: 700, fontSize: 13,
                      fontFamily: 'var(--font-display)', letterSpacing: '0.06em',
                      opacity: adding || !addTeamId ? 0.5 : 1,
                    }}>
                    {adding ? 'Adding…' : 'Assign to Team'}
                  </button>
                </div>
              </div>
            </>
          )}
          <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
        </div>
      </div>
    </div>
  );
}

// ─── TAB 3: Teams ─────────────────────────────────────────────────────────────
function TeamsTab() {
  const [inspect, setInspect] = useState(null);   // team row whose settings are open (2026-10-21)
  const [teams,      setTeams]      = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [confirm,    setConfirm]    = useState(null);

  // v2.5.262 — team recycle bin. Deletes everywhere are now SOFT
  // (teams.deleted_at); this tab grows a second view listing them,
  // with Restore / permanent Purge / per-coach bulk purge. Deleted
  // teams auto-purge after 90 days (daily cron) — the bin shows the
  // countdown so it's clear how long a restore stays possible.
  const [view,       setView]       = useState('active'); // 'active' | 'bin'
  const [binTeams,   setBinTeams]   = useState([]);
  const [binLoading, setBinLoading] = useState(false);
  const [binCoach,   setBinCoach]   = useState('');       // coach email filter ('' = all)
  const [binConfirm, setBinConfirm] = useState(null);     // {type:'purge',team} | {type:'bulk',coach,teams}

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/teams?page=${page}&limit=25`);
      setTeams(data.teams);
      setPagination(data.pagination);
    } catch { toast.error('Failed to load teams'); }
    setLoading(false);
  }, []);

  const loadBin = useCallback(async () => {
    setBinLoading(true);
    try {
      const { data } = await api.get('/admin/teams-deleted');
      setBinTeams(data.teams);
    } catch { toast.error('Failed to load recycle bin'); }
    setBinLoading(false);
  }, []);

  // Load both on mount: the bin fetch is cheap (deleted teams are few)
  // and keeps the "Recycle Bin (N)" count badge honest from the start.
  useEffect(() => { load(1); loadBin(); }, [load, loadBin]);

  const deleteTeam = async (teamId) => {
    try {
      await api.delete(`/admin/teams/${teamId}`, { data: { confirm: 'DELETE' } });
      toast.success('Team moved to the recycle bin');
      setTeams(prev => prev.filter(t => t.id !== teamId));
      setConfirm(null);
      loadBin(); // it now lives in the bin — refresh the badge/list
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
      setConfirm(null);
    }
  };

  const restoreTeam = async (team) => {
    try {
      await api.post(`/admin/teams/${team.id}/restore`);
      toast.success(`"${team.name}" restored`);
      setBinTeams(prev => prev.filter(t => t.id !== team.id));
      load(1); // it's live again — refresh the active list
    } catch (err) {
      toast.error(err.response?.data?.error || 'Restore failed');
    }
  };

  const purgeTeam = async (team) => {
    try {
      await api.delete(`/admin/teams/${team.id}/purge`, { data: { confirm: 'DELETE' } });
      toast.success(`"${team.name}" permanently deleted`);
      setBinTeams(prev => prev.filter(t => t.id !== team.id));
      setBinConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Permanent delete failed');
      setBinConfirm(null);
    }
  };

  const bulkPurge = async (teamsToPurge) => {
    try {
      const ids = teamsToPurge.map(t => t.id);
      const { data } = await api.post('/admin/teams/purge-bulk', { teamIds: ids, confirm: 'DELETE' });
      toast.success(data.message);
      setBinTeams(prev => prev.filter(t => !ids.includes(t.id)));
      setBinCoach('');
      setBinConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk delete failed');
      setBinConfirm(null);
    }
  };

  // Days until the daily cron reclaims a binned team (floor 0 — an
  // overdue team purges on the next 03:00 tick).
  const daysLeft = (purgeAt) =>
    Math.max(0, Math.ceil((new Date(purgeAt).getTime() - Date.now()) / 86400000));

  // ── 2026-07-27 (per Gilles) — Raw Stats Export (super-admin only;
  // moved here from the coach Stats page). Pick a team + season and
  // download the flat CSV of every games/roster/availability row the
  // stat calculations derive from. ──
  const [rsxTeams,    setRsxTeams]    = useState([]);   // full team list for the picker
  const [rsxTeamId,   setRsxTeamId]   = useState('');
  const [rsxSeasons,  setRsxSeasons]  = useState([]);
  const [rsxSeasonId, setRsxSeasonId] = useState('all');
  const [rsxBusy,     setRsxBusy]     = useState(false);

  useEffect(() => {
    // The paginated `teams` state above only holds the current page —
    // the picker needs every team (same limit=100 fetch the Users
    // tab's add-membership modal uses).
    api.get('/admin/teams?limit=100')
      .then(({ data }) => setRsxTeams(data.teams || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setRsxSeasonId('all');
    if (!rsxTeamId) { setRsxSeasons([]); return; }
    api.get(`/admin/teams/${rsxTeamId}/seasons`)
      .then(({ data }) => setRsxSeasons(data.seasons || []))
      .catch(() => setRsxSeasons([]));
  }, [rsxTeamId]);

  const rsxExport = async () => {
    if (!rsxTeamId) { toast.error('Pick a team first'); return; }
    setRsxBusy(true);
    try {
      const res = await api.get(`/admin/teams/${rsxTeamId}/export-raw`, {
        params: { seasonId: rsxSeasonId },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      const teamName = rsxTeams.find(t => t.id === rsxTeamId)?.name || 'team';
      a.href = url;
      a.download = `gameday-raw-stats-${teamName.replace(/[^a-z0-9-_]+/gi, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Raw stats CSV downloaded');
    } catch {
      toast.error('Raw export failed');
    }
    setRsxBusy(false);
  };

  const binCoaches = [...new Set(binTeams.map(t => t.coachEmail).filter(e => e && e !== '—'))];
  const binShown   = binCoach ? binTeams.filter(t => t.coachEmail === binCoach) : binTeams;

  // Per-team Budget module toggle. Optimistic-ish: update on success.
  const toggleBudget = async (team) => {
    const next = !team.budgetEnabled;
    try {
      await api.put(`/admin/teams/${team.id}/budget-enabled`, { enabled: next });
      setTeams(prev => prev.map(t => t.id === team.id ? { ...t, budgetEnabled: next } : t));
      toast.success(`Budget ${next ? 'enabled' : 'disabled'} for ${team.name}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update Budget setting');
    }
  };

  // Shared pill style for the Active / Recycle Bin view toggle.
  const viewPill = (active) => ({
    padding: '8px 16px', borderRadius: 999, cursor: 'pointer',
    background: active ? `${T.ice}18` : T.s2,
    border: `1px solid ${active ? `${T.ice}55` : T.border}`,
    color: active ? T.ice : T.text2,
    fontFamily: 'var(--font-display)', fontSize: 11,
    fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
  });

  return (
    <div>
      {confirm && (
        <ConfirmModal
          message={`Move "${confirm.name}" to the recycle bin? The team disappears for all its members immediately. You can restore it from the Recycle Bin for 90 days; after that it is permanently deleted.`}
          onConfirm={() => deleteTeam(confirm.id)}
          onCancel={() => setConfirm(null)}
        />
      )}
      {binConfirm?.type === 'purge' && (
        <ConfirmModal
          message={`PERMANENTLY delete "${binConfirm.team.name}" and all its games, players, chat, and stats? This cannot be undone.`}
          onConfirm={() => purgeTeam(binConfirm.team)}
          onCancel={() => setBinConfirm(null)}
        />
      )}
      {binConfirm?.type === 'bulk' && (
        <ConfirmModal
          message={`PERMANENTLY delete ${binConfirm.teams.length} deleted team${binConfirm.teams.length === 1 ? '' : 's'} coached by ${binConfirm.coach} (${binConfirm.teams.map(t => `"${t.name}"`).join(', ')})? This cannot be undone.`}
          onConfirm={() => bulkPurge(binConfirm.teams)}
          onCancel={() => setBinConfirm(null)}
        />
      )}

      {/* Active / Recycle Bin toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <button onClick={() => setView('active')} style={viewPill(view === 'active')}>
          Active
        </button>
        <button onClick={() => { setView('bin'); loadBin(); }} style={viewPill(view === 'bin')}>
          🗑 Recycle Bin ({binTeams.length})
        </button>
      </div>

      {view === 'bin' ? (
      <Card>
        {binLoading ? <LoadingGrid rows={3} /> : binTeams.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: T.text2, fontSize: 13 }}>
            The recycle bin is empty. Deleted teams land here and can be
            restored for 90 days before they're permanently removed.
          </div>
        ) : (
          <div>
            {/* Coach filter + per-coach bulk purge */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14, alignItems: 'center' }}>
              <select
                value={binCoach}
                onChange={e => setBinCoach(e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 8, background: T.s2, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
              >
                <option value="">All coaches</option>
                {binCoaches.map(email => (
                  <option key={email} value={email}>{email}</option>
                ))}
              </select>
              {binCoach && binShown.length > 0 && (
                <button
                  onClick={() => setBinConfirm({ type: 'bulk', coach: binCoach, teams: binShown })}
                  style={{
                    padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                    background: `${T.fire}12`, border: `1px solid ${T.fire}30`, color: T.fire,
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontFamily: 'var(--font-display)', fontSize: 10,
                    fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                  }}
                >
                  <Trash2 size={12} /> Delete all {binShown.length} permanently
                </button>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                    {['Team', 'Coach', 'Members', 'Games', 'Deleted', 'Auto-purge', ''].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left' }}>
                        <Label>{h}</Label>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {binShown.map(t => (
                    <tr key={t.id}
                      style={{ borderBottom: `1px solid ${T.border}` }}
                      onMouseEnter={e => e.currentTarget.style.background = `${T.ice}06`}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{t.name}</div>
                        {t.leagueName && <div style={{ fontSize: 12, color: T.text2 }}>{t.leagueName}</div>}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 13, color: T.text }}>{t.coachName}</div>
                        <div style={{ fontSize: 11, color: T.text2, fontFamily: 'var(--font-mono)' }}>{t.coachEmail}</div>
                      </td>
                      <td style={{ padding: '12px 16px', color: T.text, fontSize: 14, fontWeight: 600 }}>{t.memberCount}</td>
                      <td style={{ padding: '12px 16px', color: T.text, fontSize: 14 }}>{t.gameCount}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 12, color: T.text2 }}>
                          {formatDistanceToNow(new Date(t.deletedAt), { addSuffix: true })}
                        </div>
                        <div style={{ fontSize: 11, color: T.text3 }}>by {t.deletedBy}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          fontSize: 11, padding: '3px 9px', borderRadius: 999,
                          fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '0.05em',
                          background: daysLeft(t.purgeAt) <= 7 ? `${T.fire}15` : T.s2,
                          border: `1px solid ${daysLeft(t.purgeAt) <= 7 ? `${T.fire}40` : T.border}`,
                          color: daysLeft(t.purgeAt) <= 7 ? T.fire : T.text2,
                        }}>
                          {daysLeft(t.purgeAt)}d left
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => restoreTeam(t)}
                            title="Restore this team — it reappears for all its members with everything intact"
                            style={{
                              padding: '6px 10px', borderRadius: 7, cursor: 'pointer',
                              background: `${T.goal}12`, border: `1px solid ${T.goal}30`, color: T.goal,
                              display: 'flex', alignItems: 'center', gap: 5,
                              fontFamily: 'var(--font-display)', fontSize: 10,
                              fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                            }}
                          >
                            <RefreshCw size={12} /> Restore
                          </button>
                          <button
                            onClick={() => setBinConfirm({ type: 'purge', team: t })}
                            title="Permanently delete — cannot be undone"
                            style={{
                              padding: '6px 10px', borderRadius: 7, cursor: 'pointer',
                              background: `${T.fire}12`, border: `1px solid ${T.fire}30`, color: T.fire,
                              display: 'flex', alignItems: 'center', gap: 5,
                              fontFamily: 'var(--font-display)', fontSize: 10,
                              fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                            }}
                          >
                            <Trash2 size={12} /> Forever
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
      ) : (
      <>
      {/* ── Raw Stats Export (2026-07-27) — pick team + season, download
          the reconciliation CSV. Super-admin only by virtue of living
          here (admin.js is requireRole('admin') global). ── */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <div style={{ minWidth: 180 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>🗄 Raw Stats Export</div>
            <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>
              Every raw game, roster & RSVP row behind a team's stats — for reconciliation.
            </div>
          </div>
          <select
            value={rsxTeamId}
            onChange={e => setRsxTeamId(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, background: T.s2, border: `1px solid ${T.border}`, color: T.text, fontSize: 13, minWidth: 180 }}
          >
            <option value="">Select a team…</option>
            {rsxTeams.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <select
            value={rsxSeasonId}
            onChange={e => setRsxSeasonId(e.target.value)}
            disabled={!rsxTeamId}
            style={{ padding: '8px 10px', borderRadius: 8, background: T.s2, border: `1px solid ${T.border}`, color: T.text, fontSize: 13, minWidth: 150, opacity: rsxTeamId ? 1 : 0.5 }}
          >
            <option value="all">All seasons</option>
            {rsxSeasons.map(s => (
              <option key={s.id} value={s.id}>{s.name}{s.isActive ? ' (active)' : ''}</option>
            ))}
          </select>
          <button
            onClick={rsxExport}
            disabled={rsxBusy || !rsxTeamId}
            style={{
              padding: '8px 16px', borderRadius: 8,
              background: `${T.ice}15`, border: `1px solid ${T.ice}40`, color: T.ice,
              cursor: rsxBusy || !rsxTeamId ? 'not-allowed' : 'pointer',
              opacity: rsxTeamId ? 1 : 0.5,
              display: 'flex', alignItems: 'center', gap: 6,
              fontFamily: 'var(--font-display)', fontSize: 11,
              fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            }}>
            <Download size={13} /> {rsxBusy ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </Card>
      <Card>
        {loading ? <LoadingGrid rows={5} /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  {['Team', 'Coach', 'Schedule', 'Members', 'Games', 'Created', 'Budget', ''].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left' }}>
                      <Label>{h}</Label>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teams.map(t => (
                  <tr key={t.id}
                    style={{ borderBottom: `1px solid ${T.border}` }}
                    onMouseEnter={e => e.currentTarget.style.background = `${T.ice}06`}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{t.name}</div>
                      {t.leagueName && <div style={{ fontSize: 12, color: T.text2 }}>{t.leagueName}</div>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 13, color: T.text }}>{t.coachName}</div>
                      <div style={{ fontSize: 11, color: T.text2, fontFamily: 'var(--font-mono)' }}>{t.coachEmail}</div>
                    </td>
                    <td style={{ padding: '12px 16px', color: T.text2, fontSize: 13 }}>
                      {t.gameDay ? `${t.gameDay} ${t.gameTime?.slice(0,5)}` : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', color: T.text, fontSize: 14, fontWeight: 600 }}>{t.memberCount}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 14, color: T.text }}>{t.gameCount}</div>
                      <div style={{ fontSize: 11, color: T.goal }}>{t.gamesCompleted} done</div>
                    </td>
                    <td style={{ padding: '12px 16px', color: T.text2, fontSize: 12 }}>
                      {formatDistanceToNow(new Date(t.createdAt), { addSuffix: true })}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        onClick={() => toggleBudget(t)}
                        title={t.budgetEnabled ? 'Budget module ON — click to disable' : 'Budget module OFF — click to enable'}
                        style={{
                          padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
                          background: t.budgetEnabled ? `${T.goal}18` : T.s2,
                          border: `1px solid ${t.budgetEnabled ? `${T.goal}55` : T.border}`,
                          color: t.budgetEnabled ? T.goal : T.text2,
                          fontFamily: 'var(--font-display)', fontSize: 10,
                          fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                        }}
                      >
                        {t.budgetEnabled ? 'On' : 'Off'}
                      </button>
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => setInspect(t)}
                        title="View every setting on this team (read-only; Override to change)"
                        style={{
                          padding: '6px 10px', borderRadius: 7, marginRight: 6,
                          background: 'none', border: `1px solid ${T.border}`, color: T.text2,
                          cursor: 'pointer', fontFamily: 'var(--font-display)', fontSize: 10,
                          fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                        }}
                      >
                        ⚙ Settings
                      </button>
                      <button
                        onClick={() => setConfirm(t)}
                        title="Delete team"
                        style={{
                          padding: '6px 10px', borderRadius: 7,
                          background: `${T.fire}12`, border: `1px solid ${T.fire}30`,
                          color: T.fire, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 5,
                          fontFamily: 'var(--font-display)', fontSize: 10,
                          fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                        }}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Pagination pagination={pagination} onPage={load} />
      </>
      )}
      {inspect && <TeamSettingsInspector team={inspect} onClose={() => setInspect(null)} />}
    </div>
  );
}

// ─── Team settings inspector (2026-10-21, per Gilles) ────────────────────────
// Everything about one team, read-only — with an Override switch that lets
// the super admin edit the raw settings JSON (and the Press Box columns).
function TeamSettingsInspector({ team, onClose }) {
  const [data, setData] = useState(null);
  const [override, setOverride] = useState(false);
  const [draft, setDraft] = useState('');
  const [pbDraft, setPbDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/admin/teams/${team.id}/full-settings`);
      setData(d);
      setDraft(JSON.stringify(d.team.settings || {}, null, 2));
      setPbDraft(d.pressBox ? JSON.stringify({
        ai_analysis: d.pressBox.ai_analysis, public_enabled: d.pressBox.public_enabled,
        public_privacy: d.pressBox.public_privacy, publish_mode: d.pressBox.publish_mode,
      }, null, 2) : '');
    } catch (e) { setData({ error: e.response?.data?.error || 'Could not load' }); }
  }, [team.id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    let settings, pressBox;
    try { settings = JSON.parse(draft); } catch { toast.error('The settings JSON does not parse'); return; }
    if (pbDraft.trim()) { try { pressBox = JSON.parse(pbDraft); } catch { toast.error('The Press Box JSON does not parse'); return; } }
    if (!window.confirm(`Override the settings of "${team.name}"?\n\nThis replaces the team's settings outright — a typo here changes how their whole team behaves.`)) return;
    setSaving(true);
    try {
      await api.patch(`/admin/teams/${team.id}/full-settings`, { override: true, settings, ...(pressBox ? { pressBox } : {}) });
      toast.success('Settings overridden');
      setOverride(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setSaving(false); }
  };

  const pre = { background: T.s2, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, fontSize: 12,
    fontFamily: 'var(--font-mono, monospace)', color: T.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: 0, maxHeight: 280, overflow: 'auto' };
  const ta = { ...pre, width: '100%', minHeight: 220, resize: 'vertical' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20, width: 'min(760px, 96vw)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: T.text }}>⚙ {team.name}</h3>
          {data?.team && <span style={{ fontSize: 11, color: T.text2 }}>{data.team.teamType} · {data.team.counts?.members ?? '?'} members · {data.team.counts?.games ?? '?'} games{data.team.teamType === 'press_box' ? ` · ${data.team.counts?.press_box_players ?? 0} roster players` : ''}</span>}
          <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: override ? '#ff8a00' : T.text2, cursor: 'pointer', fontWeight: 700 }}>
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
            Override (edit the raw settings)
          </label>
          <button onClick={onClose} style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, color: T.text2, padding: '4px 10px', cursor: 'pointer' }}>Close</button>
        </div>
        {!data && <div style={{ color: T.text2, fontSize: 13 }}>Loading…</div>}
        {data?.error && <div style={{ color: '#ff6b6b', fontSize: 13 }}>{data.error}</div>}
        {data?.team && (
          <>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.text2, margin: '10px 0 6px' }}>teams.settings</div>
            {override
              ? <textarea style={ta} value={draft} onChange={(e) => setDraft(e.target.value)} spellCheck={false} />
              : <pre style={pre}>{draft || '{}'}</pre>}
            {data.pressBox && (
              <>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.text2, margin: '12px 0 6px' }}>
                  Press Box {override ? '(editable: ai_analysis, public_enabled, public_privacy, publish_mode)' : ''}
                </div>
                {override
                  ? <textarea style={{ ...ta, minHeight: 110 }} value={pbDraft} onChange={(e) => setPbDraft(e.target.value)} spellCheck={false} />
                  : <pre style={pre}>{JSON.stringify(data.pressBox, null, 2)}</pre>}
              </>
            )}
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.text2, margin: '12px 0 6px' }}>Stripe</div>
            <pre style={{ ...pre, maxHeight: 90 }}>{JSON.stringify({ accountId: data.team.stripeAccountId, mode: data.team.stripeMode }, null, 2)}</pre>
            {override && (
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <button onClick={save} disabled={saving}
                  style={{ background: '#ff8a00', border: 'none', borderRadius: 8, color: '#000', fontWeight: 800, padding: '8px 16px', cursor: 'pointer' }}>
                  {saving ? 'Saving…' : 'Save override'}
                </button>
                <button onClick={() => { setOverride(false); load(); }} style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, color: T.text2, padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── TAB 4: Logs (Security · Access · Activity · Summary) ─────────────────────

const STATUS_COLOR = {
  success: '#00e87a',
  failure: '#ff3b3b',
  blocked: '#ff8c1a',
  info:    '#7dd3b8',
};

const EVENT_ICONS = {
  login_success:            '✅',
  login_failed:             '❌',
  login_blocked:            '🚫',
  logout:                   '👋',
  register_success:         '🆕',
  email_verified:           '📧',
  password_reset_requested: '🔑',
  password_reset_completed: '🔐',
  password_changed:         '🔄',
  admin_user_updated:       '🛡️',
  admin_team_deleted:       '🗑️',
  token_refreshed:          '♻️',
};

const ACTION_ICONS = {
  team_created:        '🏟',
  game_created:        '📅',
  game_deleted:        '🗑️',
  game_result_submitted: '🏆',
  teams_balanced:      '⚖️',
  rsvp_submitted:      '✅',
  member_added:        '👤',
  member_removed:      '➖',
  player_left_team:    '🚪',
  admin_team_deleted:  '💥',
};

function DevicePill({ device, browser, os }) {
  if (!device) return null;
  const color = device === 'mobile' ? T.ice : device === 'tablet' ? T.zen : T.puck;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 10, padding: '2px 7px', borderRadius: 99,
      background: `${color}18`, border: `1px solid ${color}25`, color,
      fontFamily: 'var(--font-display)', letterSpacing: '0.05em', textTransform: 'uppercase',
    }}>
      {device === 'mobile' ? '📱' : device === 'tablet' ? '⬛' : '🖥️'} {browser} · {os}
    </span>
  );
}

function IPBadge({ ip }) {
  if (!ip) return null;
  return (
    <span style={{
      fontSize: 11, color: T.text2, fontFamily: 'var(--font-mono)',
      background: T.s2, padding: '1px 6px', borderRadius: 5,
    }}>{ip}</span>
  );
}

function LogFilters({ filters, setFilters, fields }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
      {fields.includes('search') && (
        <input
          type="search"
          placeholder="Search user / action..."
          value={filters.search || ''}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))}
          style={{
            flex: 1, minWidth: 180, padding: '8px 12px', borderRadius: 8,
            background: T.s2, border: `1px solid ${T.border}`, color: T.text, fontSize: 13,
          }}
        />
      )}
      {fields.includes('status') && (
        <select
          value={filters.status || ''}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}
          style={{ padding: '8px 10px', borderRadius: 8, background: T.s2, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
        >
          <option value="">All statuses</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
          <option value="blocked">Blocked</option>
          <option value="info">Info</option>
        </select>
      )}
      {fields.includes('eventType') && (
        <select
          value={filters.eventType || ''}
          onChange={e => setFilters(f => ({ ...f, eventType: e.target.value, page: 1 }))}
          style={{ padding: '8px 10px', borderRadius: 8, background: T.s2, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
        >
          <option value="">All events</option>
          <option value="login_success">Login success</option>
          <option value="login_failed">Login failed</option>
          <option value="login_blocked">Login blocked</option>
          <option value="logout">Logout</option>
          <option value="register_success">Register</option>
          <option value="password_changed">Password changed</option>
          <option value="admin_user_updated">Admin: user updated</option>
          <option value="admin_team_deleted">Admin: team deleted</option>
        </select>
      )}
      {fields.includes('method') && (
        <select
          value={filters.method || ''}
          onChange={e => setFilters(f => ({ ...f, method: e.target.value, page: 1 }))}
          style={{ padding: '8px 10px', borderRadius: 8, background: T.s2, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
        >
          <option value="">All methods</option>
          {['GET','POST','PUT','DELETE','PATCH'].map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      )}
      {fields.includes('action') && (
        <select
          value={filters.action || ''}
          onChange={e => setFilters(f => ({ ...f, action: e.target.value, page: 1 }))}
          style={{ padding: '8px 10px', borderRadius: 8, background: T.s2, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
        >
          <option value="">All actions</option>
          <option value="game_created">Game created</option>
          <option value="game_deleted">Game deleted</option>
          <option value="game_result">Game result</option>
          <option value="teams_balanced">Teams balanced</option>
          <option value="rsvp_submitted">RSVP submitted</option>
          <option value="member_added">Member added</option>
          <option value="member_removed">Member removed</option>
          <option value="team_created">Team created</option>
        </select>
      )}
      <input
        type="date"
        value={filters.from || ''}
        onChange={e => setFilters(f => ({ ...f, from: e.target.value, page: 1 }))}
        style={{ padding: '8px 10px', borderRadius: 8, background: T.s2, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
      />
      <input
        type="date"
        value={filters.to || ''}
        onChange={e => setFilters(f => ({ ...f, to: e.target.value, page: 1 }))}
        style={{ padding: '8px 10px', borderRadius: 8, background: T.s2, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
      />
    </div>
  );
}

// ── Summary dashboard ─────────────────────────────────────────────────────────
function LogSummaryPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/logs/summary')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load log summary'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingGrid rows={6} />;
  if (!data)   return null;

  const total = data.loginStats.successes + data.loginStats.failures + data.loginStats.blocked;
  const failRate = total > 0 ? Math.round((data.loginStats.failures + data.loginStats.blocked) / total * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Login stats strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
        {[
          { label: 'Logins (7d)',        value: data.loginStats.successes, color: T.goal },
          { label: 'Failed (7d)',        value: data.loginStats.failures,  color: T.penalty },
          { label: 'Blocked (7d)',       value: data.loginStats.blocked,   color: T.puck },
        ].map(s => (
          <Card key={s.label} style={{ padding: '16px 20px', textAlign: 'center' }} glow={s.color}>
            <div style={{ fontSize: 28, fontWeight: 900, color: s.color, fontFamily: 'var(--font-display)' }}>{s.value}</div>
            <div style={{ fontSize: 11, color: T.text2, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</div>
          </Card>
        ))}
      </div>

      {/* Failure rate */}
      {total > 0 && (
        <Card style={{ padding: '14px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Login failure rate (7d)</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: failRate > 20 ? T.penalty : T.goal }}>{failRate}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: T.s3 }}>
            <div style={{ height: '100%', borderRadius: 3, width: `${failRate}%`, background: failRate > 20 ? T.penalty : T.goal, transition: 'width 0.4s' }} />
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Top pages */}
        <Card style={{ padding: '16px 0' }}>
          <div style={{ padding: '0 16px 12px', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.ice }}>
            Top Pages (7d)
          </div>
          {data.topPaths.map((p, i) => (
            <div key={i} style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10, borderTop: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: p.method === 'GET' ? `${T.ice}20` : `${T.puck}20`, color: p.method === 'GET' ? T.ice : T.puck, fontFamily: 'var(--font-display)', fontWeight: 700 }}>{p.method}</span>
              <span style={{ flex: 1, fontSize: 12, color: T.text, fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.path}</span>
              <span style={{ fontSize: 12, color: T.text2, flexShrink: 0 }}>{parseInt(p.cnt).toLocaleString()}</span>
            </div>
          ))}
        </Card>

        {/* Top active users */}
        <Card style={{ padding: '16px 0' }}>
          <div style={{ padding: '0 16px 12px', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.zen }}>
            Most Active Users (7d)
          </div>
          {data.topActiveUsers.map((u, i) => (
            <div key={i} style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10, borderTop: `1px solid ${T.border}` }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: `${T.zen}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: T.zen, flexShrink: 0 }}>
                {u.name?.[0] || '?'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                <div style={{ fontSize: 10, color: T.text2 }}>{u.role}</div>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.zen }}>{u.hits}</span>
            </div>
          ))}
        </Card>
      </div>

      {/* Recent security failures */}
      {data.recentFailures.length > 0 && (
        <Card style={{ padding: '16px 0' }}>
          <div style={{ padding: '0 16px 12px', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.penalty }}>
            Recent Security Failures
          </div>
          {data.recentFailures.map((e, i) => (
            <div key={i} style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, borderTop: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{EVENT_ICONS[e.eventType] || '⚠️'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: STATUS_COLOR[e.status] || T.penalty, fontWeight: 600 }}>{e.eventType.replace(/_/g, ' ')}</div>
                <div style={{ fontSize: 11, color: T.text2 }}>
                  {e.user ? e.user.name : 'Anonymous'} · {e.browserFamily} / {e.osFamily} ({e.deviceType})
                </div>
              </div>
              <div style={{ fontSize: 11, color: T.text2, textAlign: 'right', flexShrink: 0 }}>
                {formatDistanceToNow(new Date(e.createdAt), { addSuffix: true })}
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ── Security log ──────────────────────────────────────────────────────────────
function SecurityLogPanel() {
  const [entries,    setEntries]    = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [filters,    setFilters]    = useState({ status: '', eventType: '', from: '', to: '' });

  const load = useCallback(async (page = 1, f = filters) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page, limit: 50 });
      if (f.status)    qs.set('status',    f.status);
      if (f.eventType) qs.set('eventType', f.eventType);
      if (f.from)      qs.set('from',      f.from);
      if (f.to)        qs.set('to',        f.to);
      const { data } = await api.get(`/admin/logs/security?${qs}`);
      setEntries(data.entries);
      setPagination(data.pagination);
    } catch { toast.error('Failed to load security logs'); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(1); }, []);

  const applyFilters = () => load(1, filters);

  return (
    <div>
      <LogFilters filters={filters} setFilters={(fn) => setFilters(fn)} fields={['status', 'eventType']} />
      <button onClick={applyFilters} style={{ marginBottom: 16, padding: '8px 16px', borderRadius: 8, background: T.ice, color: '#000', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
        Apply Filters
      </button>
      <Card>
        {loading ? <LoadingGrid rows={10} /> : (
          <div>
            {entries.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: T.text2 }}>
                <Lock size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                <p>No security events found.</p>
              </div>
            )}
            {entries.map((e, i) => {
              const statusColor = STATUS_COLOR[e.status] || T.text2;
              return (
                <div key={e.id} style={{
                  display: 'grid', gridTemplateColumns: '32px 1fr auto',
                  gap: 14, alignItems: 'start',
                  padding: '14px 20px',
                  borderBottom: i < entries.length - 1 ? `1px solid ${T.border}` : 'none',
                  background: e.status === 'failure' || e.status === 'blocked' ? `${statusColor}06` : 'transparent',
                }}>
                  <div style={{ fontSize: 20, marginTop: 2 }}>{EVENT_ICONS[e.eventType] || '🔒'}</div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <Badge color={statusColor}>{e.status}</Badge>
                      <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{e.eventType.replace(/_/g, ' ')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {e.user
                        ? <span style={{ fontSize: 12, color: T.text }}>{e.user.name} <span style={{ color: T.text2 }}>({e.user.email})</span></span>
                        : <span style={{ fontSize: 12, color: T.text2, fontStyle: 'italic' }}>Anonymous</span>
                      }
                      <IPBadge ip={e.ip} />
                      <DevicePill device={e.deviceType} browser={e.browserFamily} os={e.osFamily} />
                    </div>
                    {e.metadata && (
                      <div style={{ marginTop: 4, fontSize: 11, color: T.text2, fontFamily: 'var(--font-mono)', background: T.s3, padding: '3px 8px', borderRadius: 5, display: 'inline-block' }}>
                        {JSON.stringify(e.metadata)}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 12, color: T.text2 }}>{formatDistanceToNow(new Date(e.createdAt), { addSuffix: true })}</div>
                    <div style={{ fontSize: 10, color: T.text2, fontFamily: 'var(--font-mono)', marginTop: 2 }}>{format(new Date(e.createdAt), 'MMM d HH:mm:ss')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
      <Pagination pagination={pagination} onPage={(p) => load(p)} />
    </div>
  );
}

// ── Access log ────────────────────────────────────────────────────────────────
function AccessLogPanel() {
  const [entries,    setEntries]    = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [filters,    setFilters]    = useState({ method: '', search: '', from: '', to: '' });

  const load = useCallback(async (page = 1, f = filters) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page, limit: 100 });
      if (f.method) qs.set('method', f.method);
      if (f.search) qs.set('path',   f.search);   // 'search' in UI → 'path' query param
      if (f.from)   qs.set('from',   f.from);
      if (f.to)     qs.set('to',     f.to);
      const { data } = await api.get(`/admin/logs/access?${qs}`);
      setEntries(data.entries);
      setPagination(data.pagination);
    } catch { toast.error('Failed to load access logs'); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(1); }, []);

  const METHOD_COLORS = { GET: T.ice, POST: T.goal, PUT: T.puck, DELETE: T.penalty, PATCH: T.zen };

  return (
    <div>
      <LogFilters filters={filters} setFilters={setFilters} fields={['search', 'method']} />
      <button onClick={() => load(1, filters)} style={{ marginBottom: 16, padding: '8px 16px', borderRadius: 8, background: T.ice, color: '#000', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
        Apply Filters
      </button>
      <Card>
        {loading ? <LoadingGrid rows={12} /> : (
          <div>
            {entries.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: T.text2 }}>
                <Activity size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                <p>No access logs found.</p>
              </div>
            )}
            {/* Jul 2026 — fixed-px grid columns (50+70+140+70+60=390px + gaps) already
                exceed a ~390px phone viewport before the 1fr Path column gets any room,
                which forced page-wide horizontal scroll. Scope the overflow to this
                table only, matching the overflowX:auto pattern used elsewhere in this file. */}
            {entries.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: 480 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '50px 70px 1fr 140px 70px 60px', gap: 8, padding: '8px 16px', borderBottom: `1px solid ${T.border}`, fontSize: 10, color: T.text2, fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    <span>Method</span><span>Status</span><span>Path</span><span>User</span><span>Device</span><span>Time</span>
                  </div>
                  {entries.map((e, i) => {
                    const mc = METHOD_COLORS[e.method] || T.text2;
                    const sc = e.statusCode >= 500 ? T.penalty : e.statusCode >= 400 ? T.puck : e.statusCode >= 300 ? T.zen : T.goal;
                    return (
                      <div key={e.id} style={{
                        display: 'grid', gridTemplateColumns: '50px 70px 1fr 140px 70px 60px',
                        gap: 8, alignItems: 'center',
                        padding: '9px 16px',
                        borderBottom: i < entries.length - 1 ? `1px solid ${T.border}` : 'none',
                        fontSize: 12,
                        background: e.statusCode >= 400 ? `${sc}05` : 'transparent',
                      }}>
                        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: mc, fontSize: 11 }}>{e.method}</span>
                        <span style={{ fontWeight: 700, color: sc }}>{e.statusCode}</span>
                        <span style={{ fontFamily: 'var(--font-mono)', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.path}>{e.path}</span>
                        <span style={{ color: T.text2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.user?.name || '—'}</span>
                        <span style={{ color: T.text2, fontSize: 10 }}>{e.deviceType || '—'}</span>
                        <span style={{ color: T.text2, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 10 }}>
                          {format(new Date(e.createdAt), 'HH:mm:ss')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
      <Pagination pagination={pagination} onPage={(p) => load(p)} />
    </div>
  );
}

// ── Activity log ──────────────────────────────────────────────────────────────
function ActivityLogPanel() {
  const [entries,    setEntries]    = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [filters,    setFilters]    = useState({ action: '', from: '', to: '', q: '' });
  // Apr 2026 — distinct action list, fetched once at mount and used
  // to populate the action filter dropdown. Keeps the admin from
  // having to remember every action string.
  const [actionList, setActionList] = useState([]);

  const load = useCallback(async (page = 1, f = filters) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page, limit: 50 });
      if (f.action) qs.set('action', f.action);
      if (f.from)   qs.set('from',   f.from);
      if (f.to)     qs.set('to',     f.to);
      if (f.q)      qs.set('q',      f.q);
      const { data } = await api.get(`/admin/logs/activity?${qs}`);
      setEntries(data.entries);
      setPagination(data.pagination);
    } catch { toast.error('Failed to load activity logs'); }
    setLoading(false);
  }, [filters]);

  // 2026-10-21 (per Gilles) — the current filters, as a file (up to 5000 rows).
  const exportLogs = async (format) => {
    try {
      const qs = new URLSearchParams({ format });
      if (filters.action) qs.set('action', filters.action);
      if (filters.from)   qs.set('from',   filters.from);
      if (filters.to)     qs.set('to',     filters.to);
      if (filters.q)      qs.set('q',      filters.q);
      const res = await api.get(`/admin/logs/activity?${qs}`, { responseType: 'blob', timeout: 120000 });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `activity_log_${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch { toast.error('Export failed'); }
  };

  useEffect(() => { load(1); }, []);

  // Fetch the distinct action list once. If this fails, the dropdown
  // falls back to a free-text input — graceful degrade.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/logs/activity-actions');
        setActionList(data.actions || []);
      } catch {
        setActionList([]);
      }
    })();
  }, []);

  return (
    <div>
      {/* Filter row — action dropdown + free text + date range */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
            Action type
          </label>
          {actionList.length > 0 ? (
            <select
              value={filters.action}
              onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
              style={{
                padding: '8px 10px', background: T.s2, border: `1px solid ${T.border}`,
                borderRadius: 8, color: T.text, fontSize: 13,
              }}>
              <option value="">All actions</option>
              {actionList.map(a => (
                <option key={a.action} value={a.action}>
                  {a.action.replace(/_/g, ' ')} ({a.count})
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={filters.action}
              onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
              placeholder="e.g. invite_accepted"
              style={{ padding: '8px 10px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13 }}
            />
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 200 }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
            Search
          </label>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.text2 }} />
            <input
              type="search"
              value={filters.q}
              onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') load(1, filters); }}
              placeholder="action, team name, resource id…"
              style={{ width: '100%', padding: '8px 10px 8px 30px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>From</label>
          <input type="date" value={filters.from}
            onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
            style={{ padding: '8px 10px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>To</label>
          <input type="date" value={filters.to}
            onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
            style={{ padding: '8px 10px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13 }} />
        </div>
        <button onClick={() => load(1, filters)}
          style={{ padding: '9px 16px', borderRadius: 8, background: T.ice, color: '#000', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
          Apply
        </button>
        {(filters.action || filters.from || filters.to || filters.q) && (
          <button
            onClick={() => { const cleared = { action: '', from: '', to: '', q: '' }; setFilters(cleared); load(1, cleared); }}
            style={{ padding: '9px 14px', borderRadius: 8, background: T.s2, color: T.text2, border: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 13 }}>
            Clear
          </button>
        )}
        <button onClick={() => exportLogs('csv')} title="Download the filtered rows as CSV (opens in Excel), up to 5000"
          style={{ padding: '9px 14px', borderRadius: 8, background: T.s2, color: T.text2, border: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 13 }}>
          ⬇ CSV
        </button>
        <button onClick={() => exportLogs('json')} title="Download the filtered rows as JSON, up to 5000"
          style={{ padding: '9px 14px', borderRadius: 8, background: T.s2, color: T.text2, border: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 13 }}>
          ⬇ JSON
        </button>
      </div>
      <Card>
        {loading ? <LoadingGrid rows={10} /> : (
          <div>
            {entries.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: T.text2 }}>
                <Zap size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                <p>No activity events found.</p>
              </div>
            )}
            {entries.map((e, i) => (
              <div key={e.id} style={{
                display: 'grid', gridTemplateColumns: '36px 1fr auto',
                gap: 14, alignItems: 'start',
                padding: '13px 20px',
                borderBottom: i < entries.length - 1 ? `1px solid ${T.border}` : 'none',
              }}>
                <div style={{ fontSize: 20, marginTop: 2 }}>{ACTION_ICONS[e.action] || '📋'}</div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <Badge color={T.puck}>{e.action.replace(/_/g, ' ')}</Badge>
                    {e.resourceType && <Badge color={T.text2}>{e.resourceType}</Badge>}
                    {e.teamName && <Badge color={T.ice}>{e.teamName}</Badge>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {e.user
                      ? <span style={{ fontSize: 12, color: T.text }}>{e.user.name} <span style={{ color: T.text2 }}>({e.user.role})</span></span>
                      : <span style={{ fontSize: 12, color: T.text2, fontStyle: 'italic' }}>Unknown user</span>
                    }
                    <IPBadge ip={e.ip} />
                  </div>
                  {e.metadata && Object.keys(e.metadata).length > 0 && (
                    <div style={{ marginTop: 5, fontSize: 11, color: T.text2, fontFamily: 'var(--font-mono)', background: T.s3, padding: '3px 8px', borderRadius: 5, display: 'inline-block' }}>
                      {Object.entries(e.metadata).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 12, color: T.text2 }}>{formatDistanceToNow(new Date(e.createdAt), { addSuffix: true })}</div>
                  <div style={{ fontSize: 10, color: T.text2, fontFamily: 'var(--font-mono)', marginTop: 2 }}>{format(new Date(e.createdAt), 'MMM d HH:mm')}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Pagination pagination={pagination} onPage={(p) => load(p)} />
    </div>
  );
}

// ─── NotificationsLogPanel (Apr 2026) ────────────────────────────────────
// Cross-team notification dispatch viewer. Surfaces both email_logs
// and notifications (in-app/push) into one time-ordered stream so
// the admin can answer "did the auto-nudge actually go out?",
// "did Tony get the game-day reminder?", etc.
//
// Data: GET /admin/logs/notifications + filter facets from
// GET /admin/logs/notifications-filters (loaded once at mount).
function NotificationsLogPanel() {
  const [entries,  setEntries]  = useState([]);
  const [dispatches, setDispatches] = useState([]);
  // May 1 2026 — view mode toggle. 'flat' is the legacy per-recipient
  // log; 'grouped' batches recipients by (template/type, team, minute)
  // so an admin sees "Game reminder → The Shatz · 18 sent · 2 failed"
  // with an expandable recipient list rather than 20 individual rows.
  const [viewMode, setViewMode] = useState('grouped');
  const [expanded, setExpanded] = useState(new Set());
  const [loading,  setLoading]  = useState(true);
  const [filters,  setFilters]  = useState({
    teamId: '', channel: '', status: '', trigger: '', template: '', from: '', to: '', q: '',
  });
  // Filter dropdown facets — distinct values from the backend so the
  // admin sees only triggers/templates that actually exist in the DB.
  const [facets, setFacets] = useState({ triggers: [], templates: [], teams: [] });

  const load = useCallback(async (f = filters, mode = viewMode) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit: mode === 'grouped' ? 30 : 100 });
      Object.entries(f).forEach(([k, v]) => { if (v) qs.set(k, v); });
      if (mode === 'grouped') {
        const { data } = await api.get(`/admin/logs/notifications/dispatches?${qs}`);
        setDispatches(data.dispatches || []);
      } else {
        const { data } = await api.get(`/admin/logs/notifications?${qs}`);
        setEntries(data.entries || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load notifications');
    }
    setLoading(false);
  }, [filters, viewMode]);

  // 2026-10-21 (per Gilles) — the current filters, as a file (flat rows).
  const exportLogs = async (format) => {
    try {
      const qs = new URLSearchParams({ format });
      Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
      const res = await api.get(`/admin/logs/notifications?${qs}`, { responseType: 'blob', timeout: 120000 });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `notifications_log_${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch { toast.error('Export failed'); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/logs/notifications-filters');
        setFacets({
          triggers:  data.triggers  || [],
          templates: data.templates || [],
          teams:     data.teams     || [],
        });
      } catch {
        setFacets({ triggers: [], templates: [], teams: [] });
      }
    })();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when the view toggle flips so the right shape lands in
  // state. (filters reload happens via the Apply button.)
  useEffect(() => {
    load(filters, viewMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const toggleExpanded = (key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Status pill palette — sent (green), failed (red), skipped (muted),
  // pending/read (neutral). Anything else falls through to neutral.
  const statusStyle = (s) => {
    switch (s) {
      case 'sent':    return { bg: 'rgba(0,232,122,0.12)', fg: T.goal,    border: 'rgba(0,232,122,0.30)' };
      case 'failed':  return { bg: 'rgba(255,68,68,0.12)', fg: T.penalty, border: 'rgba(255,68,68,0.30)' };
      case 'skipped': return { bg: T.s3,                    fg: T.text2,   border: T.border };
      case 'read':    return { bg: 'rgba(167,139,250,0.12)', fg: '#a78bfa', border: 'rgba(167,139,250,0.30)' };
      default:        return { bg: T.s3, fg: T.text2, border: T.border };
    }
  };

  return (
    <div>
      {/* Filter row — wraps cleanly on narrow viewports */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Team</label>
          <select value={filters.teamId} onChange={e => setFilters(f => ({ ...f, teamId: e.target.value }))}
            style={{ padding: '8px 10px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13 }}>
            <option value="">All teams</option>
            {facets.teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 130 }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Channel</label>
          <select value={filters.channel} onChange={e => setFilters(f => ({ ...f, channel: e.target.value }))}
            style={{ padding: '8px 10px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13 }}>
            <option value="">All</option>
            <option value="email">Email</option>
            <option value="push">Push</option>
            <option value="in_app">In-app</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Status</label>
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            style={{ padding: '8px 10px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13 }}>
            <option value="">All</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="skipped">Skipped</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Trigger</label>
          <select value={filters.trigger} onChange={e => setFilters(f => ({ ...f, trigger: e.target.value }))}
            style={{ padding: '8px 10px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13 }}>
            <option value="">All</option>
            {facets.triggers.map(t => (
              <option key={t.value} value={t.value}>{t.value.replace(/_/g, ' ')} ({t.count})</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Template</label>
          <select value={filters.template} onChange={e => setFilters(f => ({ ...f, template: e.target.value }))}
            style={{ padding: '8px 10px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13 }}>
            <option value="">All</option>
            {facets.templates.map(t => (
              <option key={t.value} value={t.value}>{t.value} ({t.count})</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 200 }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Search</label>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.text2 }} />
            <input type="search" value={filters.q}
              onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') load(filters); }}
              placeholder="template, trigger, team…"
              style={{ width: '100%', padding: '8px 10px 8px 30px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>From</label>
          <input type="date" value={filters.from}
            onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
            style={{ padding: '8px 10px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>To</label>
          <input type="date" value={filters.to}
            onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
            style={{ padding: '8px 10px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13 }} />
        </div>
        <button onClick={() => load(filters)}
          style={{ padding: '9px 16px', borderRadius: 8, background: T.ice, color: '#000', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
          Apply
        </button>
        {Object.values(filters).some(v => v) && (
          <button
            onClick={() => {
              const cleared = { teamId: '', channel: '', status: '', trigger: '', template: '', from: '', to: '', q: '' };
              setFilters(cleared); load(cleared);
            }}
            style={{ padding: '9px 14px', borderRadius: 8, background: T.s2, color: T.text2, border: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 13 }}>
            Clear
          </button>
        )}
        <button onClick={() => exportLogs('csv')} title="Download the filtered rows as CSV (opens in Excel), up to 5000"
          style={{ padding: '9px 14px', borderRadius: 8, background: T.s2, color: T.text2, border: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 13 }}>
          ⬇ CSV
        </button>
        <button onClick={() => exportLogs('json')} title="Download the filtered rows as JSON, up to 5000"
          style={{ padding: '9px 14px', borderRadius: 8, background: T.s2, color: T.text2, border: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 13 }}>
          ⬇ JSON
        </button>
      </div>

      {/* View mode toggle (May 1 2026) — Flat shows every recipient
          row; Grouped collapses sends into dispatch-batches with
          counts and an expandable recipient list. Grouped is the
          default since it's much closer to "did this notification
          actually go out?" — flat is the legacy view kept for
          drill-down. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 11, color: T.text2, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>View:</span>
        <div style={{ display: 'flex', gap: 0, padding: 3, background: T.s2, borderRadius: 8, border: `1px solid ${T.border}` }}>
          {[
            { id: 'grouped', label: 'Grouped by dispatch' },
            { id: 'flat',    label: 'Flat (per recipient)' },
          ].map(opt => (
            <button key={opt.id} onClick={() => setViewMode(opt.id)}
              style={{
                padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                background: viewMode === opt.id ? T.ice : 'transparent',
                color: viewMode === opt.id ? '#000' : T.text2,
                border: 'none', cursor: 'pointer',
              }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <Card>
        {loading ? <LoadingGrid rows={10} /> : viewMode === 'grouped' ? (
          // ── GROUPED MODE ──────────────────────────────────────────
          <div>
            {dispatches.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: T.text2 }}>
                <Bell size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                <p>No dispatches match these filters.</p>
              </div>
            )}
            {dispatches.map((d, di) => {
              const key = `${d.kind}_${d.teamId || 'null'}_${d.template || 'null'}_${d.createdAt}`;
              const isOpen = expanded.has(key);
              const channelIcon = d.kind === 'email' ? '✉' : d.kind === 'push' ? '🔔' : '📥';
              const totalCounted = d.counts.sent + d.counts.failed + d.counts.skipped;
              const dispatchStatus = d.counts.failed > 0 && d.counts.sent === 0
                ? 'failed'
                : d.counts.failed > 0
                  ? 'partial'
                  : 'sent';
              const ds = dispatchStatus === 'failed'
                ? statusStyle('failed')
                : dispatchStatus === 'partial'
                  ? { bg: 'rgba(255,149,0,0.10)', fg: T.puck, border: 'rgba(255,149,0,0.30)' }
                  : statusStyle('sent');
              return (
                <div key={key} style={{
                  borderBottom: di < dispatches.length - 1 ? `1px solid ${T.border}` : 'none',
                }}>
                  <button onClick={() => toggleExpanded(key)}
                    style={{
                      width: '100%', textAlign: 'left',
                      display: 'grid', gridTemplateColumns: '36px 1fr auto',
                      gap: 14, alignItems: 'center',
                      padding: '14px 20px',
                      background: isOpen ? T.s2 : 'transparent',
                      border: 'none', cursor: 'pointer',
                    }}>
                    <div style={{ fontSize: 22 }}>{channelIcon}</div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                          padding: '2px 7px', borderRadius: 4,
                          background: ds.bg, color: ds.fg, border: `1px solid ${ds.border}`,
                        }}>
                          {dispatchStatus === 'sent' ? `✓ ${d.counts.sent} sent` :
                           dispatchStatus === 'failed' ? `✗ ${d.counts.failed} failed` :
                           `⚠ ${d.counts.sent}✓ / ${d.counts.failed}✗${d.counts.skipped ? ` / ${d.counts.skipped} skipped` : ''}`}
                        </span>
                        {d.template && <Badge color={T.ice}>{d.template}</Badge>}
                        {d.trigger && d.trigger !== d.template && <Badge color={T.puck}>{d.trigger.replace(/_/g, ' ')}</Badge>}
                        {d.teamName && <Badge color={T.text2}>🏒 {d.teamName}</Badge>}
                      </div>
                      <div style={{ fontSize: 13, color: T.text, fontWeight: 600, marginBottom: 2 }}>
                        {d.subject || <span style={{ color: T.text2, fontStyle: 'italic' }}>{d.template || 'Notification'}</span>}
                      </div>
                      <div style={{ fontSize: 11, color: T.text2 }}>
                        {totalCounted} recipient{totalCounted !== 1 ? 's' : ''}
                        {d.senderName && <span> · From {d.senderName}</span>}
                        {d.provider && <span> · via {d.provider}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 12, color: T.text2 }}>{formatDistanceToNow(new Date(d.createdAt), { addSuffix: true })}</div>
                      <div style={{ fontSize: 10, color: T.text2, fontFamily: 'var(--font-mono)', marginTop: 2 }}>{format(new Date(d.createdAt), 'MMM d HH:mm')}</div>
                      <div style={{ fontSize: 10, color: T.ice, marginTop: 4, fontWeight: 700 }}>
                        {isOpen ? '▾ Hide recipients' : '▸ Show recipients'}
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div style={{
                      padding: '4px 20px 16px 70px',
                      background: T.s3,
                      borderTop: `1px solid ${T.border}`,
                    }}>
                      {d.recipients.map((rec, ri) => {
                        const rs = statusStyle(rec.status);
                        return (
                          <div key={rec.id} style={{
                            padding: '8px 0',
                            borderBottom: ri < d.recipients.length - 1 ? `1px solid ${T.border}` : 'none',
                            display: 'grid',
                            gridTemplateColumns: '70px 1fr auto',
                            gap: 12,
                            alignItems: 'start',
                          }}>
                            <span style={{
                              fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                              padding: '2px 6px', borderRadius: 4,
                              background: rs.bg, color: rs.fg, border: `1px solid ${rs.border}`,
                              textAlign: 'center', alignSelf: 'center',
                            }}>
                              {rec.status}
                            </span>
                            <div>
                              <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>
                                {rec.recipientName || <span style={{ color: T.text2, fontStyle: 'italic' }}>Unknown recipient</span>}
                              </div>
                              {rec.recipientEmail && (
                                <div style={{ fontSize: 11, color: T.text2, fontFamily: 'var(--font-mono)' }}>
                                  {rec.recipientEmail}
                                </div>
                              )}
                              {rec.error && (
                                <div style={{
                                  marginTop: 4, padding: '6px 8px',
                                  background: 'rgba(255,68,68,0.08)',
                                  border: '1px solid rgba(255,68,68,0.20)',
                                  borderRadius: 6,
                                  fontSize: 11, color: T.penalty,
                                  fontFamily: 'var(--font-mono)',
                                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                }}>
                                  ⚠ {rec.error}
                                </div>
                              )}
                            </div>
                            <div style={{ fontSize: 10, color: T.text2, fontFamily: 'var(--font-mono)', alignSelf: 'center' }}>
                              {format(new Date(rec.createdAt), 'HH:mm:ss')}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            {entries.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: T.text2 }}>
                <Bell size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                <p>No notifications match these filters.</p>
              </div>
            )}
            {entries.map((e, i) => {
              const ss = statusStyle(e.status);
              const channelIcon = e.kind === 'email' ? '✉' : e.kind === 'push' ? '🔔' : '📥';
              return (
                <div key={`${e.kind}_${e.id}`} style={{
                  display: 'grid', gridTemplateColumns: '36px 1fr auto',
                  gap: 14, alignItems: 'start',
                  padding: '13px 20px',
                  borderBottom: i < entries.length - 1 ? `1px solid ${T.border}` : 'none',
                }}>
                  <div style={{ fontSize: 20, marginTop: 2 }}>{channelIcon}</div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                        padding: '2px 7px', borderRadius: 4,
                        background: ss.bg, color: ss.fg, border: `1px solid ${ss.border}`,
                      }}>
                        {e.status}
                      </span>
                      {e.template && <Badge color={T.ice}>{e.template}</Badge>}
                      {e.trigger && <Badge color={T.puck}>{e.trigger.replace(/_/g, ' ')}</Badge>}
                      {e.teamName && <Badge color={T.text2}>{e.teamName}</Badge>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: T.text2 }}>
                      {e.recipientName ? (
                        <span>To: <span style={{ color: T.text, fontWeight: 600 }}>{e.recipientName}</span></span>
                      ) : e.recipientEmail ? (
                        <span>To: <span style={{ color: T.text, fontFamily: 'var(--font-mono)', fontSize: 11 }}>{e.recipientEmail}</span></span>
                      ) : (
                        <span style={{ fontStyle: 'italic' }}>Unknown recipient</span>
                      )}
                      {e.senderName && (
                        <span>· From: <span style={{ color: T.text }}>{e.senderName}</span></span>
                      )}
                      {e.provider && (
                        <span>· {e.provider}</span>
                      )}
                    </div>
                    {/* May 1 2026 — subject and error inline so the
                        flat view surfaces the same diagnostic info as
                        the grouped view. Subject only renders if
                        present (push/in-app may have just a body).
                        Error only renders for failed rows. */}
                    {e.subject && (
                      <div style={{ marginTop: 4, fontSize: 12, color: T.text, fontWeight: 500 }}>
                        “{e.subject}”
                      </div>
                    )}
                    {e.error && (
                      <div style={{
                        marginTop: 4, padding: '6px 8px',
                        background: 'rgba(255,68,68,0.08)',
                        border: '1px solid rgba(255,68,68,0.20)',
                        borderRadius: 6,
                        fontSize: 11, color: T.penalty,
                        fontFamily: 'var(--font-mono)',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        ⚠ {e.error}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 12, color: T.text2 }}>{formatDistanceToNow(new Date(e.createdAt), { addSuffix: true })}</div>
                    <div style={{ fontSize: 10, color: T.text2, fontFamily: 'var(--font-mono)', marginTop: 2 }}>{format(new Date(e.createdAt), 'MMM d HH:mm')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
      <div style={{ marginTop: 12, fontSize: 11, color: T.text2, textAlign: 'center' }}>
        {viewMode === 'grouped'
          ? `Showing ${dispatches.length} most-recent dispatch group${dispatches.length === 1 ? '' : 's'}${dispatches.length >= 30 ? ' — narrow your filters to see older ones' : ''}.`
          : `Showing the most recent ${entries.length} entries${entries.length >= 100 ? ' — narrow your filters to see older ones' : ''}.`}
      </div>
    </div>
  );
}

// ── System Events tab (Apr 2026, mig 049) ────────────────────────────────────
// Operational incidents: silent failures, dispatch errors, payment issues,
// cron exceptions. Distinct from /audit log (deliberate user actions).
//
// The unresolved-count badge on the tab itself comes from the backend's
// `unresolvedCounts` payload — the page polls it every 60s while open so
// admins can see incidents arrive in real time without a manual refresh.
//
// Filtering: by default shows UNRESOLVED only (the "what do I need to fix"
// view). Toggle to show resolved history. Severity + category filters
// stack on top of resolution state.
function SystemEventsTab({ onCountChange }) {
  const [items,             setItems]             = useState([]);
  const [unresolvedCounts,  setUnresolvedCounts]  = useState({ info: 0, warning: 0, error: 0, critical: 0 });
  const [pagination,        setPagination]        = useState({ page: 1, total: 0, totalPages: 1 });
  const [loading,           setLoading]           = useState(true);
  const [showResolved,      setShowResolved]      = useState(false);
  const [severityFilter,    setSeverityFilter]    = useState('');
  const [categoryFilter,    setCategoryFilter]    = useState('');
  const [expandedId,        setExpandedId]        = useState(null);
  const [resolvingId,       setResolvingId]       = useState(null);

  // Severity → display config. Color comes from existing palette tokens
  // so dark/light mode and team-color overrides work automatically.
  const SEVERITY_CONFIG = {
    critical: { color: T.fire,    label: 'Critical', icon: '🚨' },
    error:    { color: T.penalty, label: 'Error',    icon: '⚠️' },
    warning:  { color: T.puck,    label: 'Warning',  icon: '⚡' },
    info:     { color: T.text2,   label: 'Info',     icon: 'ℹ️' },
  };

  const CATEGORY_LABELS = {
    email:        '📧 Email',
    notification: '🔔 Notification',
    balance:      '⚖️ Balance',
    payment:      '💳 Payment',
    cron:         '⏰ Cron',
    auth:         '🔐 Auth',
    database:     '💾 Database',
    integration:  '🔌 Integration',
    system:       '⚙️ System',
  };

  const load = useCallback(async (page = 1, opts = {}) => {
    // Apr 2026 — `silent: true` skips the loading skeleton so the
    // 60s auto-refresh doesn't make the page flicker. Initial loads
    // and explicit user actions get the skeleton; background polls
    // don't.
    if (!opts.silent) setLoading(true);
    try {
      const qs = new URLSearchParams({ page, limit: 50, resolved: String(showResolved) });
      if (severityFilter) qs.set('severity', severityFilter);
      if (categoryFilter) qs.set('category', categoryFilter);
      const { data } = await api.get(`/admin/system-events?${qs}`);
      setItems(data.items || []);
      setPagination({ page: data.page, total: data.total, totalPages: data.totalPages });
      setUnresolvedCounts(data.unresolvedCounts || { info: 0, warning: 0, error: 0, critical: 0 });
      // Bubble unresolved counts up to the page so the tab badge can
      // show the most-severe count even when this panel is open.
      // Always pass a complete object — the parent setter expects all
      // 4 severity keys for the badge math to work.
      if (onCountChange) {
        onCountChange(data.unresolvedCounts || { info: 0, warning: 0, error: 0, critical: 0 });
      }
    } catch {
      // Silent refreshes fail silently too — no point toasting on
      // background polls.
      if (!opts.silent) toast.error('Failed to load system events');
    }
    if (!opts.silent) setLoading(false);
  }, [showResolved, severityFilter, categoryFilter, onCountChange]);

  useEffect(() => { load(1); }, [load]);

  // Auto-refresh while the tab is open. 60s is frequent enough to feel
  // live without hammering the API; the table is cheap to render.
  useEffect(() => {
    const id = setInterval(() => load(pagination.page, { silent: true }), 60_000);
    return () => clearInterval(id);
  }, [load, pagination.page]);

  const resolve = async (eventId, notes = null) => {
    setResolvingId(eventId);
    try {
      await api.patch(`/admin/system-events/${eventId}/resolve`,
        notes ? { notes } : {}
      );
      toast.success('Marked as resolved');
      // Drop locally without a full refetch — feels snappier. The next
      // poll will reconcile if anything else changed.
      setItems(prev => prev.filter(i => i.id !== eventId));
      // Decrement unresolved counts for the matching severity.
      const item = items.find(i => i.id === eventId);
      if (item && unresolvedCounts[item.severity] > 0) {
        const newCounts = { ...unresolvedCounts, [item.severity]: unresolvedCounts[item.severity] - 1 };
        setUnresolvedCounts(newCounts);
        if (onCountChange) onCountChange(newCounts);
      }
    } catch {
      toast.error('Failed to mark resolved');
    }
    setResolvingId(null);
  };

  const reopen = async (eventId) => {
    setResolvingId(eventId);
    try {
      await api.patch(`/admin/system-events/${eventId}/reopen`);
      toast.success('Event reopened');
      load(pagination.page);
    } catch {
      toast.error('Failed to reopen event');
    }
    setResolvingId(null);
  };

  return (
    <div>
      {/* Severity-count summary across the top — at-a-glance health. */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {['critical', 'error', 'warning', 'info'].map(sev => {
          const cfg = SEVERITY_CONFIG[sev];
          const count = unresolvedCounts[sev] || 0;
          const isActive = severityFilter === sev;
          return (
            <button key={sev}
              onClick={() => setSeverityFilter(isActive ? '' : sev)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 14px', borderRadius: 10,
                background: isActive ? `${cfg.color}25` : `${cfg.color}10`,
                border: `1px solid ${isActive ? cfg.color : cfg.color + '35'}`,
                color: cfg.color,
                cursor: 'pointer', fontSize: 13, fontWeight: 700,
                transition: 'all 0.15s',
              }}>
              <span style={{ fontSize: 16 }}>{cfg.icon}</span>
              <span style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>
                {cfg.label}
              </span>
              <span style={{
                background: count > 0 ? cfg.color : T.s3,
                color: count > 0 ? '#000' : T.text2,
                borderRadius: 10, padding: '2px 8px',
                fontSize: 12, fontWeight: 800, minWidth: 24, textAlign: 'center',
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filter row — show resolved toggle + category + clear button */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 8,
          background: showResolved ? T.s2 : 'transparent',
          border: `1px solid ${T.border}`,
          cursor: 'pointer', fontSize: 12, fontWeight: 600, color: T.text,
        }}>
          <input type="checkbox" checked={showResolved}
            onChange={e => setShowResolved(e.target.checked)} />
          Show resolved
        </label>

        <select value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 8,
            background: T.surface, border: `1px solid ${T.border}`,
            color: T.text, fontSize: 12, fontWeight: 600,
            cursor: 'pointer',
          }}>
          <option value="">All categories</option>
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        {(severityFilter || categoryFilter) && (
          <button onClick={() => { setSeverityFilter(''); setCategoryFilter(''); }}
            style={{
              padding: '8px 14px', borderRadius: 8,
              background: 'transparent', border: `1px solid ${T.border}`,
              color: T.text2, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>
            Clear filters
          </button>
        )}

        <span style={{ fontSize: 11, color: T.text2, marginLeft: 'auto' }}>
          {pagination.total} {pagination.total === 1 ? 'event' : 'events'}
        </span>
      </div>

      {/* List */}
      <Card>
        {loading ? (
          <LoadingGrid rows={10} />
        ) : items.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: T.text2 }}>
            <CheckCircle size={40} style={{ margin: '0 auto 14px', opacity: 0.4, color: T.goal }} />
            <p style={{ fontSize: 14, fontWeight: 600, color: T.text, margin: '0 0 6px' }}>
              {showResolved ? 'No resolved events match the filters.' : 'All clear — no unresolved events.'}
            </p>
            <p style={{ fontSize: 12, margin: 0 }}>
              {showResolved
                ? 'Toggle off "Show resolved" to see active issues.'
                : 'System failures and dispatch errors will appear here when they happen.'}
            </p>
          </div>
        ) : (
          <div>
            {items.map((evt, idx) => {
              const cfg = SEVERITY_CONFIG[evt.severity] || SEVERITY_CONFIG.info;
              const isExpanded = expandedId === evt.id;
              const isLast = idx === items.length - 1;
              return (
                <div key={evt.id} style={{
                  borderBottom: !isLast ? `1px solid ${T.border}` : 'none',
                  background: evt.severity === 'critical' ? `${cfg.color}06` : 'transparent',
                }}>
                  {/* Compact summary row */}
                  <div onClick={() => setExpandedId(isExpanded ? null : evt.id)}
                    style={{
                      display: 'grid', gridTemplateColumns: 'auto 1fr auto',
                      gap: 14, alignItems: 'start',
                      padding: '14px 20px',
                      cursor: 'pointer',
                    }}>
                    <div style={{ fontSize: 22, lineHeight: 1, marginTop: 2 }}>
                      {cfg.icon}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <Badge color={cfg.color}>{cfg.label}</Badge>
                        <span style={{
                          fontSize: 11, color: T.text2,
                          padding: '2px 8px', borderRadius: 5,
                          background: T.s3, fontWeight: 600,
                        }}>
                          {CATEGORY_LABELS[evt.category] || evt.category}
                        </span>
                        {evt.resolvedAt && (
                          <Badge color={T.goal}>Resolved</Badge>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: T.text, fontWeight: 500, lineHeight: 1.4 }}>
                        {evt.message}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                        {evt.teamName && (
                          <span style={{ fontSize: 11, color: T.text2 }}>
                            🏒 {evt.teamName}
                          </span>
                        )}
                        {evt.gameDate && (
                          <span style={{ fontSize: 11, color: T.text2 }}>
                            📅 {format(new Date(evt.gameDate), 'MMM d')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 11, color: T.text2 }}>
                        {formatDistanceToNow(new Date(evt.createdAt), { addSuffix: true })}
                      </div>
                      <div style={{ fontSize: 10, color: T.text2, fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                        {format(new Date(evt.createdAt), 'MMM d HH:mm')}
                      </div>
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div style={{
                      padding: '0 20px 16px 56px',
                      background: T.s3 + '40',
                    }}>
                      {evt.metadata && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, color: T.text2, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
                            Metadata
                          </div>
                          <pre style={{
                            fontSize: 11, color: T.text,
                            fontFamily: 'var(--font-mono)',
                            background: T.surface, padding: 10, borderRadius: 6,
                            border: `1px solid ${T.border}`,
                            overflow: 'auto', maxHeight: 240,
                            margin: 0,
                          }}>
                            {JSON.stringify(evt.metadata, null, 2)}
                          </pre>
                        </div>
                      )}

                      {evt.resolvedAt && (
                        <div style={{ fontSize: 12, color: T.text2, marginBottom: 12 }}>
                          Resolved {formatDistanceToNow(new Date(evt.resolvedAt), { addSuffix: true })}
                          {evt.resolvedByEmail && <> by <strong style={{ color: T.text }}>{evt.resolvedByEmail}</strong></>}
                          {evt.resolutionNotes && (
                            <div style={{ marginTop: 6, padding: 8, background: T.surface, borderRadius: 6, fontSize: 12, color: T.text, fontStyle: 'italic' }}>
                              "{evt.resolutionNotes}"
                            </div>
                          )}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 8 }}>
                        {!evt.resolvedAt ? (
                          <button onClick={() => resolve(evt.id)}
                            disabled={resolvingId === evt.id}
                            style={{
                              padding: '7px 14px', borderRadius: 7,
                              background: T.goal, color: '#000',
                              border: 'none', cursor: resolvingId === evt.id ? 'wait' : 'pointer',
                              fontSize: 12, fontWeight: 700,
                              opacity: resolvingId === evt.id ? 0.6 : 1,
                            }}>
                            <CheckCircle size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                            Mark Resolved
                          </button>
                        ) : (
                          <button onClick={() => reopen(evt.id)}
                            disabled={resolvingId === evt.id}
                            style={{
                              padding: '7px 14px', borderRadius: 7,
                              background: 'transparent', color: T.text2,
                              border: `1px solid ${T.border}`, cursor: 'pointer',
                              fontSize: 12, fontWeight: 600,
                            }}>
                            Reopen
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16 }}>
          <button onClick={() => load(Math.max(1, pagination.page - 1))}
            disabled={pagination.page <= 1}
            style={{
              padding: '6px 12px', borderRadius: 6,
              background: T.s2, border: `1px solid ${T.border}`,
              color: T.text2, cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer',
              fontSize: 12, opacity: pagination.page <= 1 ? 0.4 : 1,
            }}>
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontSize: 12, color: T.text2 }}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button onClick={() => load(Math.min(pagination.totalPages, pagination.page + 1))}
            disabled={pagination.page >= pagination.totalPages}
            style={{
              padding: '6px 12px', borderRadius: 6,
              background: T.s2, border: `1px solid ${T.border}`,
              color: T.text2, cursor: pagination.page >= pagination.totalPages ? 'not-allowed' : 'pointer',
              fontSize: 12, opacity: pagination.page >= pagination.totalPages ? 0.4 : 1,
            }}>
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── LogsTab container ─────────────────────────────────────────────────────────
function LogsTab() {
  const [subTab, setSubTab] = useState('summary');

  const subTabs = [
    { id: 'summary',       label: 'Summary',       icon: '📊' },
    { id: 'security',      label: 'Security',      icon: '🔒' },
    { id: 'access',        label: 'Access',        icon: '🌐' },
    { id: 'activity',      label: 'Activity',      icon: '⚡' },
    // Apr 2026 — admin notifications viewer. "Did the email actually
    // go out?" surface, combining email_logs + notifications.
    { id: 'notifications', label: 'Notifications', icon: '📨' },
  ];

  return (
    <div>
      {/* Sub-tab bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, background: T.s2, padding: 4, borderRadius: 12, width: 'fit-content' }}>
        {subTabs.map(st => (
          <button
            key={st.id}
            onClick={() => setSubTab(st.id)}
            style={{
              padding: '7px 16px', borderRadius: 9, border: 'none', cursor: 'pointer',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              background: subTab === st.id ? T.ice : 'transparent',
              color: subTab === st.id ? '#000' : T.text2,
              transition: 'all 0.15s',
            }}
          >
            {st.icon} {st.label}
          </button>
        ))}
      </div>

      {subTab === 'summary'        && <LogSummaryPanel />}
      {subTab === 'security'       && <SecurityLogPanel />}
      {subTab === 'access'         && <AccessLogPanel />}
      {subTab === 'activity'       && <ActivityLogPanel />}
      {subTab === 'notifications'  && <NotificationsLogPanel />}
    </div>
  );
}


// ─── TAB 5: AI Settings ───────────────────────────────────────────────────────
function AiSettingsTab() {
  const PROVIDERS = [
    {
      id: 'anthropic', label: 'Claude (Anthropic)', icon: '🤖',
      color: '#d4a96a', keyPrefix: 'sk-ant-',
      keyHint: 'Starts with sk-ant-api03-…',
      docsUrl: 'https://console.anthropic.com/settings/keys',
      pdfNote: 'PDFs sent as images — works well for most permits',
      models: [
        { id: 'claude-sonnet-4-6',          label: 'Claude Sonnet 4.6  ★ Recommended',     gen: 4 },
        { id: 'claude-opus-4-6',            label: 'Claude Opus 4.6  (most capable)',       gen: 4 },
        { id: 'claude-haiku-4-5-20251001',  label: 'Claude Haiku 4.5  (fastest/cheapest)', gen: 4 },
        { id: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet  (prev. gen)',        gen: 3 },
        { id: 'claude-3-5-haiku-20241022',  label: 'Claude 3.5 Haiku  (prev. gen)',         gen: 3 },
      ],
    },
    {
      id: 'openai', label: 'OpenAI (GPT-4o)', icon: '🧠',
      color: '#74aa9c', keyPrefix: 'sk-',
      keyHint: 'Starts with sk-proj-… or sk-…',
      docsUrl: 'https://platform.openai.com/api-keys',
      pdfNote: 'PDFs sent as images — best for scanned documents',
      models: [
        { id: 'gpt-4o',       label: 'GPT-4o  ★ Recommended',     gen: 2 },
        { id: 'gpt-4o-mini',  label: 'GPT-4o Mini  (faster/cheaper)', gen: 2 },
        { id: 'gpt-4-turbo',  label: 'GPT-4 Turbo  (prev. gen)',   gen: 1 },
      ],
    },
    {
      id: 'gemini', label: 'Google Gemini', icon: '✨',
      color: '#4285f4', keyPrefix: 'AIza',
      keyHint: 'Starts with AIza…',
      docsUrl: 'https://aistudio.google.com/app/apikey',
      pdfNote: '⚡ Native PDF support — best for multi-page permits & invoices',
      models: [
        { id: 'gemini-2.5-flash',      label: 'Gemini 2.5 Flash  ★ Recommended', gen: 2 },
        { id: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite  (cheapest)', gen: 2 },
        { id: 'gemini-1.5-pro',        label: 'Gemini 1.5 Pro  (prev. gen, 1M ctx)', gen: 1 },
        { id: 'gemini-1.5-flash',      label: 'Gemini 1.5 Flash  (prev. gen)',    gen: 1 },
      ],
    },
  ];

  const [config,     setConfig]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, message }
  const [showKey,    setShowKey]    = useState(false);
  const [form,       setForm]       = useState({ provider: 'anthropic', apiKey: '', model: '' });

  useEffect(() => {
    api.get('/admin/ai-config')
      .then(r => {
        setConfig(r.data);
        setForm({
          provider: r.data.provider || 'anthropic',
          apiKey:   '',   // never pre-fill the real key
          model:    r.data.model || '',
        });
      })
      .catch(() => toast.error('Failed to load AI config'))
      .finally(() => setLoading(false));
  }, []);

  const activeProvider = PROVIDERS.find(p => p.id === form.provider);

  // Bug fix: reset model when switching providers so stale model IDs don't persist
  const switchProvider = (providerId) => {
    setForm(f => ({ ...f, provider: providerId, model: '', apiKey: '' }));
    setTestResult(null);
  };

  const save = async () => {
    // Bug fix: if the provider changed, always require a new key for that provider
    const providerChanged = config?.provider !== form.provider;
    const needsKey = !form.apiKey.trim() && (!config?.keySet || providerChanged);
    if (needsKey) {
      toast.error(providerChanged
        ? `Enter an API key for ${activeProvider?.label}`
        : 'Please enter an API key');
      return;
    }
    setSaving(true);
    setTestResult(null);
    try {
      const payload = { provider: form.provider };
      if (form.apiKey.trim()) payload.apiKey = form.apiKey.trim();
      if (form.model)         payload.model  = form.model;
      const { data } = await api.put('/admin/ai-config', payload);
      setConfig(data);
      setForm(f => ({ ...f, apiKey: '' }));
      // FIX #47 (frontend companion to Fix #39): if the backend returned a
      // pricing warning for an unknown model name, surface it to the admin
      // instead of silently swallowing it. Otherwise the admin has no way
      // to know that cost tracking will silently record nulls.
      if (data.warning) {
        toast.success('AI configuration saved!');
        toast(data.warning, { icon: '⚠️', duration: 6000 });
      } else {
        toast.success('AI configuration saved!');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/admin/ai-config/test');
      setTestResult({ ok: true, message: data.message || 'Connection successful!' });
    } catch (err) {
      setTestResult({ ok: false, message: err.response?.data?.error || 'Connection failed' });
    }
    setTesting(false);
  };

  if (loading) return <LoadingGrid rows={4} />;

  return (
    <div style={{ maxWidth: 680 }}>

      {/* Status banner */}
      <Card style={{
        padding: 20, marginBottom: 20,
        border: `1px solid ${config?.keySet ? 'rgba(0,255,136,0.35)' : 'rgba(255,107,53,0.35)'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: config?.keySet ? 'rgba(0,255,136,0.1)' : 'rgba(255,107,53,0.1)',
            border: `1px solid ${config?.keySet ? 'rgba(0,255,136,0.3)' : 'rgba(255,107,53,0.3)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20,
          }}>
            {config?.keySet ? '✅' : '⚠️'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14,
              color: config?.keySet ? T.goal : T.fire, letterSpacing: '0.05em',
            }}>
              {config?.keySet
                ? `${PROVIDERS.find(p => p.id === config.provider)?.label || config.provider} configured`
                : 'No AI provider configured'}
            </div>
            <div style={{ fontSize: 12, color: T.text2, marginTop: 3 }}>
              {config?.keySet
                ? <>Key: <span style={{ fontFamily: 'var(--font-mono)', color: T.text }}>{config.keyMasked}</span>
                    {config.model ? <> · Model: <span style={{ color: T.ice }}>{config.model}</span></> : ''}</>
                : 'Schedule import and AI team balancing require a valid API key'}
            </div>
          </div>
          {config?.keySet && (
            <button onClick={test} disabled={testing}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 9, cursor: 'pointer',
                background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)',
                color: T.ice, fontSize: 12, fontWeight: 700,
                fontFamily: 'var(--font-display)', letterSpacing: '0.08em',
                textTransform: 'uppercase', flexShrink: 0,
              }}>
              {testing
                ? <><div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> Testing…</>
                : <><Zap size={13} /> Test</>}
            </button>
          )}
        </div>

        {/* Test result */}
        {testResult && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 9,
            background: testResult.ok ? 'rgba(0,255,136,0.08)' : 'rgba(255,59,59,0.08)',
            border: `1px solid ${testResult.ok ? 'rgba(0,255,136,0.25)' : 'rgba(255,59,59,0.25)'}`,
            fontSize: 13, color: testResult.ok ? T.goal : T.penalty,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {testResult.ok ? <CheckCircle size={14} /> : <XCircle size={14} />}
            {testResult.message}
          </div>
        )}
      </Card>

      {/* Provider selection */}
      <Card style={{ padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
          <Cpu size={15} color={T.ice} />
          <Label style={{ color: T.ice }}>AI Provider</Label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {PROVIDERS.map(p => {
            const active = form.provider === p.id;
            const isSaved = config?.keySet && config.provider === p.id;
            return (
              <button key={p.id}
                onClick={() => switchProvider(p.id)}
                style={{
                  padding: '14px 10px', borderRadius: 12, cursor: 'pointer', textAlign: 'center',
                  background: active ? `${p.color}18` : T.s2,
                  border: `2px solid ${active ? p.color : T.border}`,
                  transition: 'all 0.15s', position: 'relative',
                }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{p.icon}</div>
                <div style={{
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12,
                  color: active ? p.color : T.text, letterSpacing: '0.05em', lineHeight: 1.2,
                }}>{p.label}</div>
                {isSaved && (
                  <div style={{
                    marginTop: 6, display: 'inline-flex', alignItems: 'center',
                    padding: '2px 8px', borderRadius: 6,
                    background: `${p.color}25`, border: `1px solid ${p.color}50`,
                    fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.1em', textTransform: 'uppercase', color: p.color,
                  }}>ACTIVE</div>
                )}
              </button>
            );
          })}
        </div>
        {/* PDF note for selected provider */}
        {activeProvider?.pdfNote && (
          <div style={{
            marginTop: 12, padding: '8px 12px', borderRadius: 8,
            background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)',
            fontSize: 12, color: T.text2,
          }}>
            📄 {activeProvider.pdfNote}
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: 12, color: T.text2 }}>
          Get your API key at{' '}
          <a href={activeProvider?.docsUrl} target="_blank" rel="noreferrer"
            style={{ color: T.ice, textDecoration: 'none' }}>
            {activeProvider?.docsUrl}
          </a>
        </div>
      </Card>

      {/* API Key input */}
      <Card style={{ padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Key size={15} color={T.ice} />
            <Label style={{ color: T.ice }}>API Key</Label>
          </div>
          {/* "Key saved" badge — inline div avoids Badge's missing style prop */}
          {config?.keySet && config.provider === form.provider && (
            <div style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '2px 8px', borderRadius: 6,
              background: 'rgba(0,255,136,0.1)', border: '1px solid rgba(0,255,136,0.3)',
              fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase', color: T.goal,
            }}>Key saved</div>
          )}
        </div>
        <div style={{ position: 'relative' }}>
          <input
            type={showKey ? 'text' : 'password'}
            value={form.apiKey}
            onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))}
            placeholder={
              config?.keySet && config.provider === form.provider
                ? `Leave blank to keep existing key (${config.keyMasked})`
                : activeProvider?.keyHint || 'Paste your API key here'
            }
            style={{
              width: '100%', padding: '11px 44px 11px 14px',
              background: T.s2, border: `1px solid ${T.border}`,
              borderRadius: 10, color: T.text, fontSize: 14,
              fontFamily: 'var(--font-mono)', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <button
            type="button"
            onClick={() => setShowKey(s => !s)}
            style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: T.text2, padding: 4,
            }}>
            {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <p style={{ fontSize: 12, color: T.text2, marginTop: 8 }}>
          Keys are encrypted at rest (AES-256-GCM) and never exposed in full after saving.
        </p>
      </Card>

      {/* Model selection */}
      <Card style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
          <Zap size={15} color={T.ice} />
          <Label style={{ color: T.ice }}>Model</Label>
          <span style={{ fontSize: 11, color: T.text2, marginLeft: 4 }}>(optional — recommended shown first)</span>
        </div>
        <select
          value={form.model}
          onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
          style={{
            width: '100%', padding: '11px 14px',
            background: T.s2, border: `1px solid ${T.border}`,
            borderRadius: 10, color: T.text, fontSize: 13, cursor: 'pointer',
          }}>
          <option value="">Use provider default (recommended)</option>
          {activeProvider?.models.map(m => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
        <p style={{ fontSize: 12, color: T.text2, marginTop: 8 }}>
          For schedule imports from PDFs like arena permits, the recommended defaults are:{' '}
          <strong style={{ color: T.text }}>Gemini 2.5 Flash</strong> (native PDF) ·{' '}
          <strong style={{ color: T.text }}>Claude Sonnet 4.6</strong> ·{' '}
          <strong style={{ color: T.text }}>GPT-4o</strong>
        </p>
      </Card>

      {/* Save */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={save} disabled={saving}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '13px 0', borderRadius: 12, cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 700, background: T.ice, color: '#000', border: 'none', fontSize: 15,
            fontFamily: 'var(--font-display)', letterSpacing: '0.08em', textTransform: 'uppercase',
            opacity: saving ? 0.7 : 1, transition: 'opacity 0.15s',
          }}>
          {saving
            ? <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Saving…</>
            : <><Key size={16} /> Save Configuration</>}
        </button>
        {config?.keySet && (
          <button onClick={test} disabled={testing}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '13px 24px', borderRadius: 12, cursor: testing ? 'not-allowed' : 'pointer',
              background: 'rgba(0,255,136,0.1)', border: '1px solid rgba(0,255,136,0.3)',
              color: T.goal, fontSize: 14, fontWeight: 700,
              fontFamily: 'var(--font-display)', letterSpacing: '0.08em', textTransform: 'uppercase',
            }}>
            {testing
              ? <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              : <><Zap size={14} /> Test Connection</>}
          </button>
        )}
      </div>

      {/* Dual-engine receipt keys — separate from the single active
          provider above. Receipt scanning can use a Gemini key (OCR)
          AND an Anthropic key (reasoning) at the same time. Stored in
          the DB (encrypted), super-admin only. */}
      <ReceiptAiKeysCard />

      {/* .env fallback note */}
      <div style={{
        marginTop: 24, padding: '14px 18px', borderRadius: 12,
        background: T.s2, border: `1px solid ${T.border}`,
        display: 'flex', gap: 12,
      }}>
        <Terminal size={16} style={{ color: T.text2, flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.7 }}>
          <strong style={{ color: T.text }}>Alternatively</strong>, set keys in{' '}
          <code style={{ fontFamily: 'var(--font-mono)', background: T.s3, padding: '1px 5px', borderRadius: 4 }}>
            /srv/pucksync/backend/.env
          </code>:{' '}
          <code style={{ fontFamily: 'var(--font-mono)', color: T.ice }}>ANTHROPIC_API_KEY</code>,{' '}
          <code style={{ fontFamily: 'var(--font-mono)', color: T.ice }}>OPENAI_API_KEY</code>,{' '}
          <code style={{ fontFamily: 'var(--font-mono)', color: T.ice }}>GEMINI_API_KEY</code>.
          Set the active provider with{' '}
          <code style={{ fontFamily: 'var(--font-mono)', color: T.puck }}>AI_PROVIDER=gemini</code>.
          Settings saved here take precedence over .env values.
        </div>
      </div>

    </div>
  );
}

// ─── Dual-engine receipt API keys (per-provider, DB-stored) ──────────────────
// Manages the multi-provider key map (migration 100) used by the receipt
// scanner: Gemini for OCR + Anthropic for reasoning, both at once. This is
// SEPARATE from the single "active provider" config above (which drives
// schedule import / team balance). Super-admin only — the whole admin
// router is requireRole('admin'). Keys are write-only here (masked on read).
function ReceiptAiKeysCard() {
  const META = {
    gemini:    { label: 'Google Gemini',     icon: '✨', color: '#4285f4', role: 'OCR (reads the receipt — native PDF support)', hint: 'Starts with AIza…' },
    anthropic: { label: 'Claude (Anthropic)', icon: '🤖', color: '#d4a96a', role: 'Reasoning (refines vendor / total / category)', hint: 'Starts with sk-ant-…' },
    openai:    { label: 'OpenAI',            icon: '🧠', color: '#74aa9c', role: 'Fallback OCR if no Gemini key', hint: 'Starts with sk-…' },
  };
  const ORDER = ['gemini', 'anthropic', 'openai'];
  const [rows,    setRows]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts,  setDrafts]  = useState({});   // provider -> typed key
  const [saving,  setSaving]  = useState(null);  // provider currently saving

  const load = () => {
    setLoading(true);
    api.get('/admin/ai-keys')
      .then(r => setRows(r.data.providers || []))
      .catch(() => toast.error('Failed to load receipt AI keys'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const saveKey = async (provider, clear = false) => {
    const apiKey = clear ? '' : (drafts[provider] || '').trim();
    if (!clear && !apiKey) { toast.error('Enter a key first'); return; }
    setSaving(provider);
    try {
      await api.put('/admin/ai-keys', { provider, apiKey });
      setDrafts(d => ({ ...d, [provider]: '' }));
      toast.success(clear ? `${META[provider].label} key cleared` : `${META[provider].label} key saved`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save key');
    }
    setSaving(null);
  };

  const byProvider = {};
  (rows || []).forEach(r => { byProvider[r.provider] = r; });
  const geminiSet = !!byProvider.gemini?.keySet;
  const anthropicSet = !!byProvider.anthropic?.keySet;
  const dualReady = geminiSet && anthropicSet;

  return (
    <Card style={{ padding: 20, marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Receipt size={18} style={{ color: T.ice }} />
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: T.text, letterSpacing: '0.04em' }}>
          RECEIPT SCANNING — DUAL ENGINE
        </div>
      </div>
      <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.6, marginBottom: 16 }}>
        Budget receipt uploads use two engines together: <strong style={{ color: T.text }}>Gemini</strong> reads
        the image/PDF, then <strong style={{ color: T.text }}>Claude</strong> refines the result. Set both for the
        full pipeline; one key alone still works as single-engine. Keys are stored encrypted and only set here.
      </div>

      <div style={{
        marginBottom: 16, padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
        background: dualReady ? 'rgba(0,255,136,0.1)' : 'rgba(255,107,53,0.08)',
        border: `1px solid ${dualReady ? 'rgba(0,255,136,0.3)' : 'rgba(255,107,53,0.25)'}`,
        color: dualReady ? T.goal : T.fire,
      }}>
        {dualReady ? '✅ Dual-engine ready (Gemini + Claude)' :
          geminiSet ? '◐ Single-engine (Gemini only — add a Claude key for reasoning)' :
          anthropicSet ? '◐ Single-engine (Claude only — add a Gemini key for best OCR + PDF)' :
          '⚠️ No receipt-scanning key set yet'}
      </div>

      {loading ? <LoadingGrid rows={3} /> : ORDER.map(p => {
        const m = META[p];
        const row = byProvider[p] || {};
        return (
          <div key={p} style={{ padding: '12px 0', borderTop: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 16 }}>{m.icon}</span>
              <span style={{ fontWeight: 700, fontSize: 13, color: m.color }}>{m.label}</span>
              {row.keySet && (
                <span style={{ fontSize: 11, color: T.goal, marginLeft: 4 }}>
                  ✓ {row.keyMasked}{row.source === 'env' ? ' (from .env)' : ''}
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: T.text2, marginBottom: 8 }}>{m.role}</div>
            {row.envOverride && (
              <div style={{ fontSize: 11, color: T.fire, marginBottom: 8 }}>
                A {AI_ENV_FOR(p)} env var is set on the server and takes precedence over any key saved here.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="password"
                value={drafts[p] || ''}
                onChange={e => setDrafts(d => ({ ...d, [p]: e.target.value }))}
                placeholder={row.dbKeySet ? 'Enter a new key to replace the stored one' : m.hint}
                style={{
                  flex: 1, minWidth: 220, padding: '9px 12px', borderRadius: 8,
                  background: T.s2, border: `1px solid ${T.border}`, color: T.text,
                  fontFamily: 'var(--font-mono)', fontSize: 12,
                }}
              />
              <button onClick={() => saveKey(p)} disabled={saving === p}
                style={{
                  padding: '9px 16px', borderRadius: 8, cursor: saving === p ? 'not-allowed' : 'pointer',
                  background: m.color, border: 'none', color: '#0a0a0a', fontWeight: 700, fontSize: 12,
                }}>
                {saving === p ? 'Saving…' : 'Save'}
              </button>
              {row.dbKeySet && (
                <button onClick={() => saveKey(p, true)} disabled={saving === p}
                  style={{
                    padding: '9px 14px', borderRadius: 8, cursor: 'pointer',
                    background: 'transparent', border: `1px solid ${T.border}`, color: T.text2, fontSize: 12,
                  }}>
                  Clear
                </button>
              )}
            </div>
          </div>
        );
      })}
    </Card>
  );
}
// Map provider -> the env var name (for the "env overrides DB" note).
function AI_ENV_FOR(p) {
  return p === 'gemini' ? 'GEMINI_API_KEY' : p === 'anthropic' ? 'ANTHROPIC_API_KEY' : 'OPENAI_API_KEY';
}

// ─── TAB 6: AI Usage & Costs ─────────────────────────────────────────────────
// Token pricing per 1M tokens (input / output) — update as providers change rates
const MODEL_PRICING = {
  'claude-sonnet-4-6':          { input: 3.00,  output: 15.00, provider: 'anthropic' },
  'claude-opus-4-6':            { input: 15.00, output: 75.00, provider: 'anthropic' },
  'claude-haiku-4-5-20251001':  { input: 0.80,  output: 4.00,  provider: 'anthropic' },
  'claude-3-5-sonnet-20241022': { input: 3.00,  output: 15.00, provider: 'anthropic' },
  'claude-3-5-haiku-20241022':  { input: 0.80,  output: 4.00,  provider: 'anthropic' },
  'gpt-4o':                     { input: 5.00,  output: 15.00, provider: 'openai' },
  'gpt-4o-mini':                { input: 0.15,  output: 0.60,  provider: 'openai' },
  'gpt-4-turbo':                { input: 10.00, output: 30.00, provider: 'openai' },
  'gemini-2.5-flash':           { input: 0.30,  output: 2.50,  provider: 'gemini' },
  'gemini-2.5-flash-lite':      { input: 0.10,  output: 0.40,  provider: 'gemini' },
  'gemini-1.5-pro':             { input: 1.25,  output: 5.00,  provider: 'gemini' },
  'gemini-1.5-flash':           { input: 0.075, output: 0.30,  provider: 'gemini' },
  'gemini-2.0-flash (⚠ deprecated Jun 2026)': { input: 0.10, output: 0.40, provider: 'gemini' },
};

const PROVIDER_COLORS = { anthropic: '#d4a96a', openai: '#74aa9c', gemini: '#4285f4' };
const PROVIDER_COLOR_FALLBACK = '#888888'; // safe hex fallback for unknown providers
const providerColor = (provider) => PROVIDER_COLORS[provider] || PROVIDER_COLOR_FALLBACK;
const FEATURE_LABELS  = {
  import:   '📄 Schedule Import',
  balance:  '⚖️ Team Balance',
  analysis: '🤖 AI Analysis',
  test:     '🔌 Connection Test',
};

function fmtCost(n)   { return n == null ? '—' : n === 0 ? '$0.00' : n < 0.01 ? '<$0.01' : `$${n.toFixed(4)}`; }
function fmtTokens(n) { return n == null ? '—' : n >= 1000000 ? `${(n/1000000).toFixed(2)}M` : n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n); }

function AiUsageTab() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [period,  setPeriod]  = useState('month'); // 'month' | 'all'
  const [subTab,  setSubTab]  = useState('overview');
  const [logPage, setLogPage] = useState(1);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/ai-usage?period=${p}`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load AI usage data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  const changePeriod = (p) => { setPeriod(p); setLogPage(1); };

  const exportCsv = () => {
    if (!data?.recent?.length) return;
    const header = 'Date,Team,Team ID,Provider,Model,Feature,Status,Input Tokens,Output Tokens,Cost (USD),Error';
    const rows = data.recent.map(r => [
      r.createdAt ? format(new Date(r.createdAt), 'yyyy-MM-dd HH:mm') : '',
      `"${(r.teamName || '').replace(/"/g, '""')}"`,
      r.teamId || '',
      r.provider || '',
      r.model || '',
      r.feature || '',
      r.status || '',
      r.inputTokens ?? '',
      r.outputTokens ?? '',
      r.cost != null ? r.cost.toFixed(6) : '',
      r.errorMsg ? `"${r.errorMsg.replace(/"/g, '""')}"` : '',
    ].join(',')).join('\n');
    const blob = new Blob([header + '\n' + rows], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: `gameday-ai-usage-${period}.csv` });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return <LoadingGrid rows={5} />;
  if (!data)   return <p style={{ color: T.text2, padding: 20 }}>No usage data available.</p>;

  const { summary, byProvider, byTeam, byFeature, recent } = data;

  const SUB_TABS = [
    { id: 'overview', label: 'Overview',  icon: '📊' },
    { id: 'teams',    label: 'By Team',   icon: '🏒' },
    { id: 'log',      label: 'Call Log',  icon: '📋' },
    { id: 'pricing',  label: 'Pricing',   icon: '💰' },
  ];

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', background: T.s2, borderRadius: 10, padding: 3, border: `1px solid ${T.border}` }}>
          {[['month','This Month'], ['all','All Time']].map(([p, label]) => (
            <button key={p} onClick={() => changePeriod(p)} style={{
              padding: '6px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: period === p ? T.ice : 'transparent',
              color: period === p ? '#000' : T.text2,
              fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase', transition: 'all 0.15s',
            }}>{label}</button>
          ))}
        </div>
        <button onClick={() => load(period)} style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '8px 14px', borderRadius: 9,
          background: T.s2, border: `1px solid ${T.border}`,
          color: T.text2, cursor: 'pointer',
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}><RefreshCw size={12} /> Refresh</button>
        <div style={{ flex: 1 }} />
        <button onClick={exportCsv} disabled={!recent?.length} style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '8px 14px', borderRadius: 9,
          background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.25)',
          color: T.goal, cursor: recent?.length ? 'pointer' : 'not-allowed',
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          opacity: recent?.length ? 1 : 0.4,
        }}><Download size={12} /> Export CSV</button>
      </div>

      {/* Summary orbs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: period === 'month' ? 'Cost This Month' : 'Total Cost', value: fmtCost(summary?.totalCost),        icon: DollarSign, color: T.goal   },
          { label: 'API Calls',       value: summary?.totalCalls ?? '—',                                             icon: Zap,        color: T.ice    },
          { label: 'Successes',       value: summary?.successCount ?? '—',                                           icon: CheckCircle,color: T.goal   },
          { label: 'Errors',          value: summary?.errorCount ?? '—',                                             icon: XCircle,    color: T.penalty },
          { label: 'Avg Duration',    value: summary?.avgDurationMs ? `${(summary.avgDurationMs/1000).toFixed(1)}s` : '—', icon: Clock, color: T.puck },
          { label: 'Teams Using AI',  value: byTeam?.length ?? '—',                                                  icon: Users,      color: '#a78bfa' },
        ].map(s => (
          <Card key={s.label} style={{ padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.border}` }}>
              <s.icon size={13} color={s.color} />
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 10, color: T.text2, marginTop: 5, fontFamily: 'var(--font-display)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{s.label}</div>
          </Card>
        ))}
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: T.s2, padding: 4, borderRadius: 12, width: 'fit-content', border: `1px solid ${T.border}` }}>
        {SUB_TABS.map(st => (
          <button key={st.id} onClick={() => setSubTab(st.id)} style={{
            padding: '7px 16px', borderRadius: 9, border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            background: subTab === st.id ? T.ice : 'transparent',
            color: subTab === st.id ? '#000' : T.text2,
            transition: 'all 0.15s',
          }}>{st.icon} {st.label}</button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {subTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Card style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              <BarChart2 size={15} color={T.ice} /><Label style={{ color: T.ice }}>Cost by Provider</Label>
            </div>
            {!byProvider?.length
              ? <p style={{ color: T.text2, fontSize: 13 }}>No usage yet.</p>
              : byProvider.map(p => {
                  const maxCost = Math.max(...byProvider.map(x => x.cost ?? 0), 0.0001);
                  const pct = ((p.cost ?? 0) / maxCost * 100).toFixed(1);
                  const col = providerColor(p.provider);
                  return (
                    <div key={p.provider} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.text, textTransform: 'capitalize' }}>{p.provider}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: col }}>{fmtCost(p.cost)} <span style={{ color: T.text2, fontWeight: 400, fontSize: 11 }}>({p.calls} calls)</span></span>
                      </div>
                      <div style={{ height: 6, borderRadius: 3, background: T.s3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, borderRadius: 3, background: col, transition: 'width 0.4s ease' }} />
                      </div>
                      <div style={{ fontSize: 11, color: T.text2, marginTop: 3 }}>{fmtTokens(p.inputTokens)} in · {fmtTokens(p.outputTokens)} out</div>
                    </div>
                  );
                })
            }
          </Card>
          <Card style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              <Zap size={15} color={T.puck} /><Label style={{ color: T.puck }}>Cost by Feature</Label>
            </div>
            {!byFeature?.length
              ? <p style={{ color: T.text2, fontSize: 13 }}>No usage yet.</p>
              : byFeature.map(f => {
                  const maxCost = Math.max(...byFeature.map(x => x.cost ?? 0), 0.0001);
                  const pct = ((f.cost ?? 0) / maxCost * 100).toFixed(1);
                  return (
                    <div key={f.feature} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{FEATURE_LABELS[f.feature] || f.feature}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: T.puck }}>{fmtCost(f.cost)} <span style={{ color: T.text2, fontWeight: 400, fontSize: 11 }}>({f.calls} calls)</span></span>
                      </div>
                      <div style={{ height: 6, borderRadius: 3, background: T.s3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, borderRadius: 3, background: 'var(--puck)', transition: 'width 0.4s ease' }} />
                      </div>
                    </div>
                  );
                })
            }
          </Card>
        </div>
      )}

      {/* ── BY TEAM ── */}
      {subTab === 'teams' && (
        <Card>
          {!byTeam?.length
            ? <p style={{ padding: 24, color: T.text2, fontSize: 13 }}>No team usage data yet.</p>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                      {['Team', 'Calls', 'Input', 'Output', 'Cost (USD)', 'Last Used'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left' }}><Label>{h}</Label></th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...byTeam].sort((a, b) => (b.cost ?? 0) - (a.cost ?? 0)).map((t, i) => (
                      <tr key={t.teamId || i}
                        style={{ borderBottom: `1px solid ${T.border}`, transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.04)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{t.teamName || '—'}</div>
                          <div style={{ fontSize: 11, color: T.text2, fontFamily: 'var(--font-mono)', marginTop: 2 }}>{t.teamId}</div>
                        </td>
                        <td style={{ padding: '12px 16px', color: T.ice, fontWeight: 700 }}>{t.calls ?? '—'}</td>
                        <td style={{ padding: '12px 16px', color: T.text2, fontSize: 13 }}>{fmtTokens(t.inputTokens)}</td>
                        <td style={{ padding: '12px 16px', color: T.text2, fontSize: 13 }}>{fmtTokens(t.outputTokens)}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15,
                            color: (t.cost ?? 0) > 1 ? T.penalty : (t.cost ?? 0) > 0.1 ? T.puck : T.goal }}>
                            {fmtCost(t.cost)}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', color: T.text2, fontSize: 12 }}>
                          {t.lastUsed ? format(new Date(t.lastUsed), 'MMM d, yyyy') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: `2px solid ${T.border}` }}>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: T.text }}>TOTAL</td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: T.ice }}>{summary?.totalCalls ?? '—'}</td>
                      <td style={{ padding: '12px 16px', color: T.text2 }}>{fmtTokens(summary?.totalInputTokens)}</td>
                      <td style={{ padding: '12px 16px', color: T.text2 }}>{fmtTokens(summary?.totalOutputTokens)}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 800, color: T.goal, fontSize: 16, fontFamily: 'var(--font-display)' }}>{fmtCost(summary?.totalCost)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          }
        </Card>
      )}

      {/* ── CALL LOG ── */}
      {subTab === 'log' && (
        <CallLogTab recent={recent} logPage={logPage} setLogPage={setLogPage} />
      )}

      {/* ── PRICING REFERENCE ── */}
      {subTab === 'pricing' && (
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <DollarSign size={15} color={T.goal} />
            <Label style={{ color: T.goal }}>Model Pricing Reference</Label>
            <span style={{ fontSize: 11, color: T.text2, marginLeft: 4 }}>USD per 1M tokens</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  {['Model', 'Provider', 'Input / 1M', 'Output / 1M', 'Est. per Import*'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left' }}><Label>{h}</Label></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(MODEL_PRICING).map(([model, p]) => {
                  // ~8k input + ~1k output = typical arena permit import
                  const est = (8000 / 1e6) * p.input + (1000 / 1e6) * p.output;
                  const col = providerColor(p.provider);
                  return (
                    <tr key={model} style={{ borderBottom: `1px solid ${T.border}`, transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = T.s2}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px 14px', fontFamily: 'var(--font-mono)', fontSize: 12, color: T.text }}>{model}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: `${col}18`, color: col, textTransform: 'uppercase', fontFamily: 'var(--font-display)', letterSpacing: '0.06em' }}>{p.provider}</span>
                      </td>
                      <td style={{ padding: '10px 14px', color: T.ice, fontFamily: 'var(--font-mono)', fontSize: 13 }}>${p.input.toFixed(3)}</td>
                      <td style={{ padding: '10px 14px', color: T.puck, fontFamily: 'var(--font-mono)', fontSize: 13 }}>${p.output.toFixed(3)}</td>
                      <td style={{ padding: '10px 14px', color: T.goal, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13 }}>~${est.toFixed(4)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 11, color: T.text2, marginTop: 14, lineHeight: 1.6 }}>
            * Estimated per schedule import (~8,000 input + ~1,000 output tokens — typical for a KMAC-style 6-page arena permit).
            Actual costs vary by document complexity. Verify current rates at each provider before billing customers.
          </p>
        </Card>
      )}
    </div>
  );
}

function CallLogTab({ recent, logPage, setLogPage }) {
  const [errOnly, setErrOnly] = useState(false);
  const LOG_PER_PAGE = 20;
  const rows     = errOnly ? (recent || []).filter(r => r.status === 'error') : (recent || []);
  const logPages = Math.ceil(rows.length / LOG_PER_PAGE);
  const safePage = Math.min(logPage, Math.max(1, logPages));
  const logSlice = rows.slice((safePage - 1) * LOG_PER_PAGE, safePage * LOG_PER_PAGE);

  const fmtDur = (ms) => ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${(ms/1000).toFixed(1)}s`;

  return (
    <Card>
      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
        <Label>Show</Label>
        {[['all','All Calls'], ['err','Errors Only']].map(([k, label]) => (
          <button key={k} onClick={() => { setErrOnly(k === 'err'); setLogPage(1); }} style={{
            padding: '5px 12px', borderRadius: 7, border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            background: (k === 'err') === errOnly ? (k === 'err' ? 'rgba(255,59,59,0.15)' : T.ice) : T.s2,
            color: (k === 'err') === errOnly ? (k === 'err' ? T.penalty : '#000') : T.text2,
            transition: 'all 0.15s',
          }}>{label}</button>
        ))}
        <span style={{ fontSize: 12, color: T.text2, marginLeft: 'auto' }}>
          {rows.length} {errOnly ? 'error' : 'total'} call{rows.length !== 1 ? 's' : ''}
        </span>
      </div>

      {!rows.length
        ? <p style={{ padding: 24, color: T.text2, fontSize: 13 }}>{errOnly ? 'No errors logged yet.' : 'No API calls logged yet.'}</p>
        : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                    {['Status', 'Date', 'Team', 'Provider / Model', 'Feature', 'Tokens', 'Cost', 'Duration'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left' }}><Label>{h}</Label></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logSlice.map((r, i) => {
                    const isErr = r.status === 'error';
                    const col   = providerColor(r.provider);
                    return (
                      <Fragment key={r.id || i}>
                        <tr
                          style={{ borderBottom: r.errorMsg ? 'none' : `1px solid ${T.border}`, transition: 'background 0.1s',
                                   background: isErr ? 'rgba(255,59,59,0.04)' : 'transparent' }}
                          onMouseEnter={e => e.currentTarget.style.background = isErr ? 'rgba(255,59,59,0.07)' : 'rgba(0,212,255,0.03)'}
                          onMouseLeave={e => e.currentTarget.style.background = isErr ? 'rgba(255,59,59,0.04)' : 'transparent'}>
                          {/* Status badge */}
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            {isErr
                              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: 'rgba(255,59,59,0.15)', color: T.penalty, fontFamily: 'var(--font-display)', letterSpacing: '0.06em' }}><XCircle size={10} /> ERROR</span>
                              : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: 'rgba(0,255,136,0.12)', color: T.goal, fontFamily: 'var(--font-display)', letterSpacing: '0.06em' }}><CheckCircle size={10} /> OK</span>
                            }
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: 12, color: T.text2, whiteSpace: 'nowrap' }}>
                            {r.createdAt ? format(new Date(r.createdAt), 'MMM d HH:mm') : '—'}
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: 13, color: T.text, maxWidth: 140 }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.teamName || '—'}</div>
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 5px', borderRadius: 4, background: `${col}20`, color: col, textTransform: 'uppercase', fontFamily: 'var(--font-display)', letterSpacing: '0.06em', flexShrink: 0 }}>{r.provider}</span>
                              <span style={{ fontSize: 11, color: T.text2, fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>{r.model}</span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: 12, color: T.text2 }}>{FEATURE_LABELS[r.feature] || r.feature || '—'}</td>
                          <td style={{ padding: '10px 14px', fontSize: 12, color: T.text2, whiteSpace: 'nowrap' }}>
                            {isErr ? '—' : `${fmtTokens(r.inputTokens)} · ${fmtTokens(r.outputTokens)}`}
                          </td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: isErr ? T.penalty : T.goal, fontFamily: 'var(--font-display)', fontSize: 13 }}>
                            {isErr ? '—' : fmtCost(r.cost)}
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: 12, color: T.text2, whiteSpace: 'nowrap' }}>{fmtDur(r.durationMs)}</td>
                        </tr>
                        {/* Error message row */}
                        {isErr && r.errorMsg && (
                          <tr style={{ borderBottom: `1px solid ${T.border}`, background: 'rgba(255,59,59,0.04)' }}>
                            <td colSpan={8} style={{ padding: '0 14px 10px 14px' }}>
                              <div style={{
                                fontSize: 12, color: T.penalty, fontFamily: 'var(--font-mono)',
                                padding: '6px 10px', borderRadius: 6,
                                background: 'rgba(255,59,59,0.08)', border: '1px solid rgba(255,59,59,0.2)',
                                wordBreak: 'break-word',
                              }}>
                                {r.errorMsg}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {logPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 13, color: T.text2 }}>{rows.length} total · page {safePage} of {logPages}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setLogPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
                    style={{ padding: '6px 11px', borderRadius: 7, cursor: 'pointer', background: T.s2, border: `1px solid ${T.border}`, color: T.text2, opacity: safePage <= 1 ? 0.4 : 1 }}>
                    <ChevronLeft size={13} />
                  </button>
                  <button onClick={() => setLogPage(p => Math.min(logPages, p + 1))} disabled={safePage >= logPages}
                    style={{ padding: '6px 11px', borderRadius: 7, cursor: 'pointer', background: T.s2, border: `1px solid ${T.border}`, color: T.text2, opacity: safePage >= logPages ? 0.4 : 1 }}>
                    <ChevronRight size={13} />
                  </button>
                </div>
              </div>
            )}
          </>
        )
      }
    </Card>
  );
}

function Pagination({ pagination, onPage }) {
  if (pagination.pages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
      <span style={{ fontSize: 13, color: T.text2 }}>
        {pagination.total} total · page {pagination.page} of {pagination.pages}
      </span>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={() => onPage(pagination.page - 1)}
          disabled={pagination.page <= 1}
          style={{
            padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
            background: T.s2, border: `1px solid ${T.border}`, color: T.text2,
            opacity: pagination.page <= 1 ? 0.4 : 1,
          }}
        ><ChevronLeft size={14} /></button>
        <button
          onClick={() => onPage(pagination.page + 1)}
          disabled={pagination.page >= pagination.pages}
          style={{
            padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
            background: T.s2, border: `1px solid ${T.border}`, color: T.text2,
            opacity: pagination.page >= pagination.pages ? 0.4 : 1,
          }}
        ><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

// ─── Shared: Loading skeleton ─────────────────────────────────────────────────
function LoadingGrid({ rows = 3 }) {
  return (
    <div style={{ padding: 20 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{
          height: 48, borderRadius: 8, marginBottom: 8,
          background: `linear-gradient(90deg, ${T.s2} 0%, ${T.s3} 50%, ${T.s2} 100%)`,
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          opacity: 1 - i * 0.1,
        }} />
      ))}
    </div>
  );
}

// ─── TAB: Email Stats (SendGrid quota tracking) ──────────────────────────────
// May 4 2026 (v2.5.64) — admin (super-admin) email volume tracker.
// Coach feedback: "I'd like to keep track of how many emails are being
// sent out to compare against my SendGrid max per month total, which is
// 50,000 emails currently."
//
// Layout:
//   • Top: total emails sent in the selected window + SendGrid quota
//     progress bar (turns yellow at 70%, red at 90%)
//   • 12-month trend chart so admins can spot growth
//   • Three side-by-side breakdowns: by team, by customer, by trigger
//   • Window picker: 7d / 30d / 90d / Custom
//
// All counts default to 'sent' status (only sent emails count toward
// SendGrid quota). Admin can flip to 'all' if debugging delivery.
function EmailStatsTab() {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [windowDays, setWindowDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = new Date(Date.now() - windowDays * 24 * 60 * 60 * 1000).toISOString();
      const to   = new Date().toISOString();
      const { data } = await api.get('/admin/email-stats', { params: { from, to } });
      setStats(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load email stats');
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => { load(); }, [load]);

  // Current calendar month total drives the SendGrid quota progress bar.
  // We use the byMonth bucket for "this month" so the math is identical
  // to what SendGrid bills against (calendar month, not trailing 30d).
  const thisMonthKey = new Date().toISOString().slice(0, 7); // 'YYYY-MM'
  const thisMonthCount = (stats?.byMonth || [])
    .find(m => m.month === thisMonthKey)?.email_count || 0;
  const quota = stats?.sendgridMonthlyQuota || 50000;
  const quotaPct = Math.min(100, (thisMonthCount / quota) * 100);
  const quotaColor = quotaPct >= 90 ? T.penalty
                    : quotaPct >= 70 ? T.fire
                    : T.goal;

  if (loading && !stats) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: T.text2 }}>
        <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: 12 }}>Loading email stats…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, color: T.penalty, textAlign: 'center' }}>
        <AlertTriangle size={20} style={{ marginBottom: 8 }} />
        <p>{error}</p>
        <button onClick={load} style={{
          marginTop: 12, padding: '8px 16px', borderRadius: 8,
          background: T.s2, border: `1px solid ${T.border}`,
          color: T.text, cursor: 'pointer',
        }}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header — window picker + refresh */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { d: 7,   l: '7 days' },
            { d: 30,  l: '30 days' },
            { d: 90,  l: '90 days' },
          ].map(opt => (
            <button key={opt.d}
              onClick={() => setWindowDays(opt.d)}
              style={{
                padding: '6px 14px', borderRadius: 8,
                background: windowDays === opt.d ? `${T.ice}18` : T.s2,
                border: `1px solid ${windowDays === opt.d ? `${T.ice}40` : T.border}`,
                color: windowDays === opt.d ? T.ice : T.text2,
                cursor: 'pointer',
                fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
              {opt.l}
            </button>
          ))}
        </div>
        <button onClick={load} disabled={loading} style={{
          marginLeft: 'auto',
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 8,
          background: T.s2, border: `1px solid ${T.border}`,
          color: T.text2, cursor: loading ? 'wait' : 'pointer',
          fontSize: 12, fontWeight: 600,
        }}>
          <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* SendGrid quota card — the headline number admins came here for */}
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
        padding: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <p style={{
              fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase', color: T.text2,
              marginBottom: 4,
            }}>
              SendGrid · This calendar month
            </p>
            <p style={{ fontSize: 32, fontWeight: 800, color: T.text, lineHeight: 1, margin: 0 }}>
              {thisMonthCount.toLocaleString()}
              <span style={{ fontSize: 18, color: T.text2, fontWeight: 500 }}>
                {' '}/ {quota.toLocaleString()}
              </span>
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{
              fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800,
              color: quotaColor, lineHeight: 1, margin: 0,
            }}>
              {quotaPct.toFixed(1)}%
            </p>
            <p style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>
              of monthly plan
            </p>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{
          width: '100%', height: 10, borderRadius: 999,
          background: T.s2, overflow: 'hidden',
        }}>
          <div style={{
            width: `${quotaPct}%`, height: '100%',
            background: quotaColor, transition: 'width 0.3s ease',
          }} />
        </div>
        {quotaPct >= 70 && (
          <p style={{ fontSize: 12, color: quotaColor, marginTop: 10, fontWeight: 600 }}>
            {quotaPct >= 90 ? '⚠ Critical: ' : '⚠ Heads-up: '}
            you've used over {Math.floor(quotaPct)}% of your monthly SendGrid quota.
            {quotaPct >= 90 && ' Consider upgrading the plan or auditing top senders below.'}
          </p>
        )}
      </div>

      {/* Window total */}
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
        padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <p style={{
            fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.14em', textTransform: 'uppercase', color: T.text2,
            marginBottom: 4,
          }}>
            Window total · Last {windowDays} days
          </p>
          <p style={{ fontSize: 24, fontWeight: 800, color: T.text, margin: 0 }}>
            {(stats?.total || 0).toLocaleString()} emails
          </p>
        </div>
        <Mail size={24} color={T.text2} />
      </div>

      {/* 12-month trend */}
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
        padding: 16,
      }}>
        <p style={{
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.12em', textTransform: 'uppercase', color: T.text2,
          marginBottom: 12,
        }}>
          12-month trend
        </p>
        {(stats?.byMonth || []).length === 0 ? (
          <p style={{ fontSize: 13, color: T.text3, padding: '12px 0' }}>
            No email history in the last 12 months.
          </p>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 100 }}>
            {[...(stats?.byMonth || [])].reverse().map(m => {
              const maxCount = Math.max(...stats.byMonth.map(x => x.email_count), 1);
              const heightPct = (m.email_count / maxCount) * 100;
              const overQuota = m.email_count > quota;
              return (
                <div key={m.month}
                  title={`${m.month}: ${m.email_count.toLocaleString()} emails`}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
                    <div style={{
                      width: '100%',
                      height: `${heightPct}%`,
                      background: overQuota ? T.penalty : T.ice,
                      borderRadius: '4px 4px 0 0',
                      minHeight: m.email_count > 0 ? 2 : 0,
                    }} />
                  </div>
                  <p style={{ fontSize: 9, color: T.text3, fontFamily: 'var(--font-display)' }}>
                    {m.month.slice(5)}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Three breakdowns side-by-side on desktop, stacked on mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 16,
      }}>
        <BreakdownCard
          title="By Team"
          icon={<Shield size={14} />}
          rows={(stats?.byTeam || []).map(r => ({
            label: r.teamName,
            count: r.emailCount,
            sub: r.teamId ? null : 'system / unattributed',
          }))}
          total={stats?.total || 0}
        />
        <BreakdownCard
          title="By Customer (sender)"
          icon={<Users size={14} />}
          rows={(stats?.byCustomer || []).map(r => ({
            label: r.name,
            count: r.emailCount,
            sub: r.email,
          }))}
          total={stats?.total || 0}
        />
        <BreakdownCard
          title="By Trigger"
          icon={<Zap size={14} />}
          rows={(stats?.byTrigger || []).map(r => ({
            label: r.trigger,
            count: r.email_count,
            sub: null,
          }))}
          total={stats?.total || 0}
        />
      </div>
    </div>
  );
}

// Side-by-side breakdown card — used 3x in EmailStatsTab.
// Shows top 50 by count with horizontal bars proportional to the
// row's share of the window total. Rows with 0 emails are filtered
// out by the backend (GROUP BY only emits rows with rowcount).
function BreakdownCard({ title, icon, rows, total }) {
  const top = rows.slice(0, 50);
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
      padding: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <span style={{ color: T.text2 }}>{icon}</span>
        <p style={{
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.12em', textTransform: 'uppercase', color: T.text2,
          margin: 0,
        }}>
          {title}
        </p>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: T.text3 }}>
          {top.length}
        </span>
      </div>
      {top.length === 0 ? (
        <p style={{ fontSize: 13, color: T.text3, padding: '12px 0' }}>
          No data in this window.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 480, overflowY: 'auto' }}>
          {top.map((r, idx) => {
            const pct = total > 0 ? (r.count / total) * 100 : 0;
            return (
              <div key={idx} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                  <p style={{
                    fontSize: 13, fontWeight: 600, color: T.text,
                    flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {r.label}
                  </p>
                  <p style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: 'var(--font-mono)' }}>
                    {r.count.toLocaleString()}
                  </p>
                </div>
                {r.sub && (
                  <p style={{
                    fontSize: 10, color: T.text3, marginBottom: 4,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {r.sub}
                  </p>
                )}
                <div style={{
                  width: '100%', height: 4, borderRadius: 2,
                  background: T.s2,
                }}>
                  <div style={{
                    width: `${pct}%`, height: '100%',
                    background: T.ice, borderRadius: 2,
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── TAB: System Settings ─────────────────────────────────────────────────────
function SystemSettingsTab() {
  // Use the module-level T palette directly, same pattern as every other tab
  // in this file. (Previous code had `const T = useColors()` which referenced
  // an undefined helper — the tab threw "useColors is not defined" on open.)
  const [settings, setSettings]   = useState(null);
  const [saving,   setSaving]     = useState(false);

  useEffect(() => {
    api.get('/admin/system-settings')
      .then(r => setSettings(r.data))
      .catch(() => toast.error('Failed to load system settings'));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/system-settings', settings);
      toast.success('System settings saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (!settings) return <div style={{ color: T.text2, padding: 20 }}>Loading…</div>;

  const Card = ({ children }) => (
    <div style={{ background: T.s2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
      {children}
    </div>
  );

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 20, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        System Settings
      </h2>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <MapPin size={16} color={T.ice} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Arena Search Provider</h3>
        </div>
        <p style={{ fontSize: 12, color: T.text2, marginBottom: 16, lineHeight: 1.5 }}>
          Controls where the app looks up arena names and addresses. OSM Nominatim is free with no API key.
          Google Places provides better results but requires a Google Cloud API key set in the server's <code>.env</code> as <code>GOOGLE_PLACES_API_KEY</code>.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { key: 'nominatim', label: 'OpenStreetMap Nominatim', badge: 'Free · No key', desc: 'Community-sourced venue data. Works globally.' },
            { key: 'google',    label: 'Google Places API',        badge: 'Requires API key', desc: 'Best accuracy. Requires GOOGLE_PLACES_API_KEY in .env.' },
          ].map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSettings(s => ({ ...s, arena_search_provider: opt.key }))}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6,
                padding: '12px 14px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                background: settings.arena_search_provider === opt.key ? 'rgba(0,212,255,0.1)' : T.s3,
                border: `1.5px solid ${settings.arena_search_provider === opt.key ? 'var(--ice-a40)' : T.border}`,
                transition: 'all 0.15s',
              }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: settings.arena_search_provider === opt.key ? T.ice : T.text }}>
                {settings.arena_search_provider === opt.key ? '✓ ' : ''}{opt.label}
              </span>
              <span style={{ fontSize: 10, color: settings.arena_search_provider === opt.key ? T.ice : '#a78bfa', fontWeight: 700, letterSpacing: '0.06em' }}>
                {opt.badge}
              </span>
              <span style={{ fontSize: 11, color: T.text2, lineHeight: 1.4 }}>{opt.desc}</span>
            </button>
          ))}
        </div>

        {/* Server-side Places key — only relevant when Google is selected.
            Distinct from the browser "Google Maps Integration" key below:
            this one is used server-side by the arena-search proxy and is
            never sent to the browser, so use an unrestricted / IP-restricted
            key (NOT referrer-restricted). Takes effect immediately, no
            restart. The server .env GOOGLE_PLACES_API_KEY, if set, wins. */}
        {settings.arena_search_provider === 'google' && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>Places API key (server-side)</span>
              {settings.google_places_api_key_set && (
                <span style={{ fontSize: 11, color: T.goal, fontFamily: 'var(--font-mono)' }}>
                  ✓ {settings.google_places_api_key_masked}
                </span>
              )}
            </div>
            <input
              type="password"
              className="input-field"
              value={settings.google_places_api_key || ''}
              onChange={(e) => setSettings(s => ({ ...s, google_places_api_key: e.target.value }))}
              placeholder={settings.google_places_api_key_set ? 'Paste a new key to replace…' : 'AIza… (App restriction: None or IP; API: Places)'}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
            <p style={{ fontSize: 11, color: T.text2, marginTop: 6, lineHeight: 1.5 }}>
              Use a key with <strong>Application restriction = None or IP addresses</strong> (a referrer-restricted
              key fails server-side). Needs <strong>Places API</strong> enabled + billing on. Save here and it
              works right away — no deploy. (A <code>GOOGLE_PLACES_API_KEY</code> in the server .env overrides this.)
            </p>
          </div>
        )}
      </Card>

      {/* ── Recording retention (2026-10-19) ──────────────────────────
          How long a game's AUDIO is kept after the game. 0 = forever
          (today's default). The daily sweep removes only the audio —
          stats and play-by-play stay. Later this follows the package. */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Mic size={16} color={T.ice} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Recording retention</h3>
        </div>
        <p style={{ fontSize: 12, color: T.text2, marginBottom: 12, lineHeight: 1.5 }}>
          Days a game&rsquo;s audio is kept after the game before the nightly sweep removes it.
          The stats and play-by-play always stay; only the sound goes. <strong>0 = keep forever</strong> (the default).
          Recordings are already encrypted at rest, and the audio codecs don&rsquo;t compress further, so there is no
          re-zip step — deletion is the space saver. Later this becomes the per-package value
          (e.g. &ldquo;keep until 30 days after the last game&rdquo;).
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="number" min={0} max={3650} className="input-field" style={{ width: 110 }}
            value={settings.recording_retention_days ?? 0}
            onChange={(e) => setSettings(x => ({ ...x, recording_retention_days: e.target.value === '' ? 0 : Number(e.target.value) }))} />
          <span style={{ fontSize: 12, color: T.text2 }}>days ({Number(settings.recording_retention_days) > 0 ? `audio removed ~${settings.recording_retention_days} days after each game` : 'keep forever'})</span>
        </div>
      </Card>

      {/* ── Google Maps Integration (v2.5.126) ────────────────────────
          Stores the Google Maps JavaScript API key used by the
          AddressAutocomplete component (venues, player profiles).
          The key is delivered to authed browsers via
          /api/maps/client-key; security comes from HTTP-referrer
          restrictions configured on the Google Cloud Console side. */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <MapPin size={16} color={T.ice} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Google Maps Integration</h3>
          {settings.google_maps_api_key_set && (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: '0.12em',
              padding: '2px 7px', borderRadius: 999,
              background: 'rgba(52,211,153,0.15)', color: 'var(--goal)',
            }}>
              CONFIGURED
            </span>
          )}
        </div>
        <p style={{ fontSize: 12, color: T.text2, marginBottom: 12, lineHeight: 1.5 }}>
          Powers address autocomplete on venue addresses, player profile addresses, and other address inputs throughout the app.
          When configured, users see Google-suggested addresses as they type. When not configured, the inputs fall back to plain text.
        </p>
        <ol style={{ fontSize: 12, color: T.text2, marginBottom: 12, lineHeight: 1.6, paddingLeft: 18 }}>
          <li>Create a key at <span style={{ fontFamily: 'var(--font-mono)' }}>console.cloud.google.com/apis/credentials</span></li>
          <li>Enable <strong>Maps JavaScript API</strong> and <strong>Places API</strong></li>
          <li>Restrict the key: <strong>HTTP referrer</strong> → <span style={{ fontFamily: 'var(--font-mono)' }}>https://app.game-day.app/*</span></li>
          <li>Restrict API scope: <strong>Maps JS + Places</strong> only</li>
        </ol>
        {settings.google_maps_api_key_set && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
            padding: '8px 12px', background: T.s3, borderRadius: 8,
            border: `1px solid ${T.border}`,
          }}>
            <span style={{ fontSize: 11, color: T.text2, fontWeight: 600 }}>Current:</span>
            <code style={{ fontSize: 12, color: T.text, fontFamily: 'var(--font-mono)' }}>
              {settings.google_maps_api_key_masked}
            </code>
          </div>
        )}
        <label style={{ display: 'block', marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.text2, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>
            {settings.google_maps_api_key_set ? 'Replace key' : 'Set key'}
          </span>
          <input
            type="text"
            // v2.5.126 — controlled, but only carries the NEW key
            // (typed by the admin). Never echo the masked form
            // here, or the save handler would post the masked
            // value back as a "new" key. The placeholder reminds
            // the admin to paste a fresh AIza key.
            value={settings.google_maps_api_key || ''}
            onChange={(e) => setSettings(s => ({ ...s, google_maps_api_key: e.target.value }))}
            placeholder={settings.google_maps_api_key_set ? 'Paste a new key to replace…' : 'AIzaSy…'}
            style={{
              width: '100%', padding: '10px 12px',
              background: T.s3, color: T.text, border: `1px solid ${T.border}`,
              borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-mono)',
            }}
          />
        </label>
        {settings.google_maps_api_key_set && (
          <button
            type="button"
            onClick={() => {
              if (!window.confirm('Clear the Google Maps key? Address inputs will fall back to plain text.')) return;
              setSettings(s => ({ ...s, google_maps_api_key: '' }));
            }}
            style={{
              background: 'transparent', border: `1px solid ${T.border}`, color: T.text2,
              padding: '6px 12px', borderRadius: 8, fontSize: 12,
              cursor: 'pointer', marginRight: 8,
            }}>
            Clear key
          </button>
        )}
      </Card>

      {/* ── Stripe Mode ────────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <CreditCard size={16} color={T.ice} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Stripe Mode</h3>
        </div>
        <p style={{ fontSize: 12, color: T.text2, marginBottom: 16, lineHeight: 1.5 }}>
          Toggle between Stripe test and live mode. Keys live in the server's <code>.env</code> as <code>STRIPE_SECRET_KEY_TEST</code> / <code>STRIPE_SECRET_KEY_LIVE</code>
          and <code>STRIPE_WEBHOOK_SECRET_TEST</code> / <code>STRIPE_WEBHOOK_SECRET_LIVE</code>. Changes here take effect on the very next checkout; no restart required.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { key: 'test', label: 'Test Mode', badge: 'Safe · Fake cards', desc: 'Use 4242 4242 4242 4242 to simulate payments. Nothing is charged.' },
            { key: 'live', label: 'Live Mode', badge: 'Real money', desc: 'Real cards are charged. Only flip this once you\'ve tested thoroughly.' },
          ].map(opt => {
            const active = (settings.stripe_mode || 'test') === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSettings(s => ({ ...s, stripe_mode: opt.key }))}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6,
                  padding: '12px 14px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                  background: active ? (opt.key === 'live' ? 'rgba(16,185,129,0.12)' : 'rgba(0,212,255,0.1)') : T.s3,
                  border: `1.5px solid ${active ? (opt.key === 'live' ? '#10b981' : 'var(--ice-a40)') : T.border}`,
                  transition: 'all 0.15s',
                }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: active ? (opt.key === 'live' ? '#10b981' : T.ice) : T.text }}>
                  {active ? '✓ ' : ''}{opt.label}
                </span>
                <span style={{ fontSize: 10, color: active ? (opt.key === 'live' ? '#10b981' : T.ice) : '#a78bfa', fontWeight: 700, letterSpacing: '0.06em' }}>
                  {opt.badge}
                </span>
                <span style={{ fontSize: 11, color: T.text2, lineHeight: 1.4 }}>{opt.desc}</span>
              </button>
            );
          })}
        </div>
      </Card>

      {/* ── Email Provider ─────────────────────────────────────────── */}
      <EmailProviderCard settings={settings} setSettings={setSettings} Card={Card} />

      {/* ── Platform Fee ───────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <DollarSign size={16} color={T.ice} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Platform Fee (per spare payment)</h3>
        </div>
        <p style={{ fontSize: 12, color: T.text2, marginBottom: 16, lineHeight: 1.5 }}>
          Flat amount collected on every spare-fee payment. Applied in the payment's currency
          (${ (settings.platform_fee_cents ?? 100) / 100 } CAD / USD / EUR). Coaches choose per-team whether the spare pays this on top or the coach absorbs it.
          Set to 0 to disable platform fees entirely.
        </p>
        <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: T.text2, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
          Flat fee amount
        </label>
        <div style={{ position: 'relative', maxWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T.text2, fontSize: 13, pointerEvents: 'none' }}>$</span>
          <input
            type="number"
            min="0"
            max="100"
            step="0.25"
            value={((settings.platform_fee_cents ?? 100) / 100).toFixed(2)}
            onChange={e => {
              // Parse to cents, clamp 0–10000 (matches backend validation)
              const raw = parseFloat(e.target.value);
              if (!Number.isFinite(raw) || raw < 0) return;
              const cents = Math.min(10000, Math.round(raw * 100));
              setSettings(s => ({ ...s, platform_fee_cents: cents }));
            }}
            style={{
              width: '100%', padding: '8px 12px 8px 24px', borderRadius: 8,
              background: T.s3, border: `1px solid ${T.border}`, color: T.text,
              fontSize: 14, fontFamily: 'inherit',
            }}
          />
        </div>
        <p style={{ fontSize: 11, color: T.text2, marginTop: 10, lineHeight: 1.5 }}>
          Example: a coach charges $22 for a spare. With passthrough <strong>on</strong>, the spare is billed $23 (base + {((settings.platform_fee_cents ?? 100) / 100).toFixed(2)} fee),
          rounded up to the nearest $0.25 if enabled. With passthrough <strong>off</strong>, the spare pays exactly $22 and you net the fee from the coach's share.
        </p>

        {/* Live breakdown for admin reference: shows what a sample transaction
            actually costs, with Stripe's processing fee (~2.9% + 30¢) separated
            from the platform take. Coaches see a blended "processing fee" in
            their team settings, but the admin needs to see the split to know
            what the platform actually earns per transaction. */}
        {(() => {
          const platformFeeCents = settings.platform_fee_cents ?? 100;
          const STRIPE_PCT     = 0.029;
          const STRIPE_FIXED_C = 30;
          // Reference scenarios — coach bills $15, $25, $50 spare rates
          const rows = [15, 25, 50].map(dollars => {
            const baseCents    = dollars * 100;
            // Passthrough on: spare pays base + platform
            const spareCents   = baseCents + platformFeeCents;
            const stripeCents  = Math.ceil(spareCents * STRIPE_PCT) + STRIPE_FIXED_C;
            const totalCostCents = stripeCents + platformFeeCents;
            // Platform net = the full platform fee (spare covers it via passthrough)
            // Stripe always eats from the coach's payout regardless of passthrough.
            return {
              label: `$${dollars} base`,
              spare: spareCents,
              stripe: stripeCents,
              platform: platformFeeCents,
              total: totalCostCents,
            };
          });
          const fmt = c => `$${(c / 100).toFixed(2)}`;
          return (
            <div style={{
              marginTop: 14, padding: 12, borderRadius: 8,
              background: T.s3, border: `1px dashed ${T.border}`,
            }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: T.text2, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
                Stripe + Platform breakdown (admin reference)
              </p>
              {/* Jul 2026 — 5 auto-sized grid columns of monospace $ figures
                  can exceed a ~390px phone width once min-content is summed.
                  overflowX:auto lets it scroll horizontally instead of
                  clipping or squeezing digits unreadably. */}
              <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: '4px 12px', fontSize: 11, fontFamily: 'monospace', minWidth: 360 }}>
                  <span style={{ color: T.text2, fontWeight: 700 }}>Scenario</span>
                  <span style={{ color: T.text2, fontWeight: 700, textAlign: 'right' }}>Spare pays</span>
                  <span style={{ color: T.text2, fontWeight: 700, textAlign: 'right' }}>Stripe</span>
                  <span style={{ color: T.text2, fontWeight: 700, textAlign: 'right' }}>Platform</span>
                  <span style={{ color: T.text2, fontWeight: 700, textAlign: 'right' }}>Total cost</span>
                  {rows.map(r => (
                    <Fragment key={r.label}>
                      <span style={{ color: T.text }}>{r.label}</span>
                      <span style={{ color: T.text, textAlign: 'right' }}>{fmt(r.spare)}</span>
                      <span style={{ color: '#a78bfa', textAlign: 'right' }}>{fmt(r.stripe)}</span>
                      <span style={{ color: T.ice, textAlign: 'right' }}>{fmt(r.platform)}</span>
                      <span style={{ color: T.text, textAlign: 'right', fontWeight: 700 }}>{fmt(r.total)}</span>
                    </Fragment>
                  ))}
                </div>
              </div>
              <p style={{ fontSize: 10, color: T.text2, marginTop: 10, lineHeight: 1.5 }}>
                Stripe estimated at 2.9% + 30¢ (US/CA standard rate). International cards, alternate
                payment methods, and Stripe pricing plans may vary. Platform fee is what you keep per
                transaction after Stripe takes their cut.
              </p>
            </div>
          );
        })()}
      </Card>

      {/* ── Season Payment Fee ─────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <DollarSign size={16} color={T.ice} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Season Payment Fee (full-season upfront)</h3>
        </div>
        <p style={{ fontSize: 12, color: T.text2, marginBottom: 16, lineHeight: 1.5 }}>
          When a coach collects full-season fees (vs. per-game spare fees), the platform
          applies a combined flat + percentage fee per transaction. Structured like Stripe
          itself — a small flat covers the minimum revenue on small seasons, the percentage
          captures upside on larger ones.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, maxWidth: 420 }}>
          <div>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: T.text2, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
              Flat amount
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T.text2, fontSize: 13, pointerEvents: 'none' }}>$</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.25"
                value={((settings.season_fee_flat_cents ?? 200) / 100).toFixed(2)}
                onChange={e => {
                  const raw = parseFloat(e.target.value);
                  if (!Number.isFinite(raw) || raw < 0) return;
                  const cents = Math.min(10000, Math.round(raw * 100));
                  setSettings(s => ({ ...s, season_fee_flat_cents: cents }));
                }}
                style={{
                  width: '100%', padding: '8px 12px 8px 24px', borderRadius: 8,
                  background: T.s3, border: `1px solid ${T.border}`, color: T.text,
                  fontSize: 14, fontFamily: 'inherit',
                }}
              />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: T.text2, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
              Percentage
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                min="0"
                max="10"
                step="0.05"
                value={((settings.season_fee_percent_bps ?? 100) / 100).toFixed(2)}
                onChange={e => {
                  // UI is percent (e.g. 1.25) — store as basis points (125).
                  const raw = parseFloat(e.target.value);
                  if (!Number.isFinite(raw) || raw < 0) return;
                  const bps = Math.min(1000, Math.round(raw * 100));
                  setSettings(s => ({ ...s, season_fee_percent_bps: bps }));
                }}
                style={{
                  width: '100%', padding: '8px 28px 8px 12px', borderRadius: 8,
                  background: T.s3, border: `1px solid ${T.border}`, color: T.text,
                  fontSize: 14, fontFamily: 'inherit',
                }}
              />
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: T.text2, fontSize: 13, pointerEvents: 'none' }}>%</span>
            </div>
          </div>
        </div>

        {/* Live breakdown — same style as platform fee reference */}
        {(() => {
          const flatCents  = settings.season_fee_flat_cents ?? 200;
          const percentBps = settings.season_fee_percent_bps ?? 100;
          const STRIPE_PCT     = 0.029;
          const STRIPE_FIXED_C = 30;
          // Coach-side season fee scenarios
          const rows = [150, 300, 750, 1500].map(dollars => {
            const baseCents      = dollars * 100;
            // Platform take: flat + bps%. bps/10000 converts to decimal.
            const platformCents  = flatCents + Math.ceil(baseCents * percentBps / 10000);
            // Assume passthrough mode — player pays base + platform
            const playerCents    = baseCents + platformCents;
            const stripeCents    = Math.ceil(playerCents * STRIPE_PCT) + STRIPE_FIXED_C;
            const totalCostCents = stripeCents + platformCents;
            return {
              label: `$${dollars} season`,
              player: playerCents,
              stripe: stripeCents,
              platform: platformCents,
              total: totalCostCents,
            };
          });
          const fmt = c => `$${(c / 100).toFixed(2)}`;
          return (
            <div style={{
              marginTop: 16, padding: 12, borderRadius: 8,
              background: T.s3, border: `1px dashed ${T.border}`,
            }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: T.text2, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
                Reference breakdown (passthrough mode)
              </p>
              {/* Jul 2026 — same overflow guard as the Platform Fee card above:
                  5 auto-sized $ columns can exceed a ~390px phone width. */}
              <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: '4px 12px', fontSize: 11, fontFamily: 'monospace', minWidth: 360 }}>
                  <span style={{ color: T.text2, fontWeight: 700 }}>Scenario</span>
                  <span style={{ color: T.text2, fontWeight: 700, textAlign: 'right' }}>Player pays</span>
                  <span style={{ color: T.text2, fontWeight: 700, textAlign: 'right' }}>Stripe</span>
                  <span style={{ color: T.text2, fontWeight: 700, textAlign: 'right' }}>Platform</span>
                  <span style={{ color: T.text2, fontWeight: 700, textAlign: 'right' }}>Total cost</span>
                  {rows.map(r => (
                    <Fragment key={r.label}>
                      <span style={{ color: T.text }}>{r.label}</span>
                      <span style={{ color: T.text, textAlign: 'right' }}>{fmt(r.player)}</span>
                      <span style={{ color: '#a78bfa', textAlign: 'right' }}>{fmt(r.stripe)}</span>
                      <span style={{ color: T.ice, textAlign: 'right' }}>{fmt(r.platform)}</span>
                      <span style={{ color: T.text, textAlign: 'right', fontWeight: 700 }}>{fmt(r.total)}</span>
                    </Fragment>
                  ))}
                </div>
              </div>
              <p style={{ fontSize: 10, color: T.text2, marginTop: 10, lineHeight: 1.5 }}>
                Platform fee = {`$${(flatCents / 100).toFixed(2)}`} flat + {(percentBps / 100).toFixed(2)}% of base.
                Stripe estimated at 2.9% + 30¢. Set percentage to 0 for flat-only pricing, or flat to $0
                for percentage-only.
              </p>
            </div>
          );
        })()}
      </Card>
      {/* ── WhatsApp (2026-10-01, Game Day Stats) — read-only: the Meta
          credentials live in the server environment, never in the DB. */}
      <WhatsAppStatusCard Card={Card} />

      {/* ── League registration fee (2026-10-01) ──────────────────── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <DollarSign size={16} color={T.ice} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>League Registration Fee</h3>
        </div>
        <p style={{ fontSize: 12, color: T.text2, marginBottom: 16, lineHeight: 1.5 }}>
          Game Day&apos;s cut of each registration paid online through a league&apos;s Stripe account: a percentage
          of the amount, never less than the minimum or more than the maximum. It comes off the league&apos;s side
          (the player pays the registration amount), on top of Stripe&apos;s own processing fee. Cash and
          e-transfers the league records cost nothing.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          {[
            ['league_fee_percent_bps', 'Percent', '%', 100, 20, 0.25, 300],
            ['league_fee_min_cents',  'Minimum', '$', 100, 100, 0.25, 100],
            ['league_fee_max_cents',  'Maximum', '$', 100, 100, 0.25, 1000],
          ].map(([key, label, unit, div, max, step, dflt]) => (
            <div key={key}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: T.text2, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T.text2, fontSize: 13, pointerEvents: 'none' }}>{unit}</span>
                <input type="number" min="0" max={max} step={step}
                  value={((settings[key] ?? dflt) / div).toFixed(2)}
                  onChange={e => {
                    const raw = parseFloat(e.target.value);
                    if (!Number.isFinite(raw) || raw < 0) return;
                    setSettings(s => ({ ...s, [key]: Math.min(max * div, Math.round(raw * div)) }));
                  }}
                  style={{ width: '100%', padding: '8px 12px 8px 24px', borderRadius: 8, background: T.s3, border: `1px solid ${T.border}`, color: T.text, fontSize: 14, fontFamily: 'inherit' }} />
              </div>
            </div>
          ))}
        </div>
        {(() => {
          const bps = settings.league_fee_percent_bps ?? 300, mn = settings.league_fee_min_cents ?? 100, mx = settings.league_fee_max_cents ?? 1000;
          // Same math as computeLeagueFeeCents on the server.
          const fee = (c) => { if (!c) return 0; let f = Math.ceil(c * bps / 10000); f = Math.max(f, mn); if (mx > 0) f = Math.min(f, mx); return Math.min(c, f); };
          const fmt = c => `$${(c / 100).toFixed(2)}`;
          return (
            <p style={{ fontSize: 11, color: T.text2, marginTop: 12, lineHeight: 1.6 }}>
              {[2500, 15000, 32500, 40000].map(c => `${fmt(c)} registration → ${fmt(fee(c))}`).join(' · ')}
              <br />Set the maximum to 0 for no cap.
            </p>
          );
        })()}
      </Card>

      {/* Apr 2026 — Chat Attachments. Standalone card with its own
          save flow because the backend keeps a small in-memory
          cache of these values for fast feature-flag checks; using
          the dedicated endpoint busts that cache on every change. */}
      <ChatAttachmentsCard />

      {/* May 4 2026 (v2.5.68) — Misc UI experiments. Currently houses
          a single A/B toggle for "compact ToggleSetting descriptions"
          on TeamPage. When ON, every team-settings toggle hides its
          paragraph description and moves it into the help-icon
          tooltip — coach gets a tighter scan-friendly settings page.
          When OFF (default), the legacy verbose layout is preserved.
          Wrapped as a super-admin gate so we can A/B test the simpler
          layout without committing every team to it. */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 16 }}>🧪</span>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>UI Experiments (Misc)</h3>
        </div>
        <p style={{ fontSize: 12, color: T.text2, marginBottom: 16, lineHeight: 1.5 }}>
          Super-admin-only A/B toggles for layout experiments. Affects every team in the system once flipped.
        </p>
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '12px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 12,
          background: settings.wallet_require_admin_approval !== false ? 'rgba(0,212,255,0.10)' : T.s3,
          border: `1.5px solid ${settings.wallet_require_admin_approval !== false ? 'var(--ice-a40, rgba(0,212,255,0.4))' : T.border}`,
          transition: 'all 0.15s',
        }}>
          <input
            type="checkbox"
            checked={settings.wallet_require_admin_approval !== false}
            onChange={e => setSettings(s => ({ ...s, wallet_require_admin_approval: e.target.checked }))}
            style={{ marginTop: 2, accentColor: 'var(--ice)' }}
          />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>
              Budget trust-wallet payouts require my approval
            </p>
            <p style={{ fontSize: 11, color: T.text2, lineHeight: 1.5 }}>
              When ON (default): every Game Day-managed trust-wallet payout must be approved and marked
              sent by a Game Day admin (you). When OFF: a team coach/manager can approve and send their
              own wallet payouts. This never moves money itself — it only records the disbursement.
            </p>
          </div>
        </label>
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
          background: settings.compact_toggle_descriptions === true ? 'rgba(0,212,255,0.10)' : T.s3,
          border: `1.5px solid ${settings.compact_toggle_descriptions === true ? 'var(--ice-a40, rgba(0,212,255,0.4))' : T.border}`,
          transition: 'all 0.15s',
        }}>
          <input
            type="checkbox"
            checked={settings.compact_toggle_descriptions === true}
            onChange={e => setSettings(s => ({ ...s, compact_toggle_descriptions: e.target.checked }))}
            style={{ marginTop: 2, accentColor: 'var(--ice)' }}
          />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>
              Compact ToggleSetting descriptions on Team Settings
            </p>
            <p style={{ fontSize: 11, color: T.text2, lineHeight: 1.5 }}>
              When ON: every toggle on Team Settings shows only its title; the description paragraph
              is hidden and moved into the help-icon tooltip. Tighter, scan-friendly layout.
              When OFF (default): the original verbose layout with the description paragraph below the title.
              <br/><br/>
              <strong>Affects every team.</strong> Once flipped, all coaches see the new layout immediately
              after their next page reload. Flip back to revert if coaches report missing context.
            </p>
          </div>
        </label>

        {/* v2.5.100 — Schedule availability icon size A/B picker.
            Coach feedback: "For the bigger schedule availability
            icons, can you put the super admin area where I can
            toggle them from 16 point. I did one for 18 point, add
            one for 20, 24, 28, 30 point. Then I can at least toggle
            them from the super admin area to increase the size to
            A/B test."
            Whitelist values: 12 (legacy pre-v2.5.97), 16 (current
            default), 18, 20, 24, 28, 30. Backend validates the
            same list (admin.js); SchedulePage applies via inline
            style. Affects every team — flip back if too big.
            Selected value gets a tinted background; everyone else
            looks plain so the active choice stands out. */}
        <div style={{
          marginTop: 12,
          padding: '12px 14px', borderRadius: 10,
          background: T.s3, border: `1.5px solid ${T.border}`,
        }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6 }}>
            Schedule availability icon size
          </p>
          <p style={{ fontSize: 11, color: T.text2, lineHeight: 1.5, marginBottom: 10 }}>
            Controls the font size of the ✅ ❌ ⏳ 🤔 chips in the schedule list.
            <strong> Affects every team.</strong> 16 is the post-v2.5.97 default;
            12 reverts to the pre-v2.5.97 baseline. Pick a larger value
            (18–30) to A/B test for accessibility.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[12, 16, 18, 20, 24, 28, 30].map(sz => {
              const current = settings.schedule_availability_icon_size === sz
                || (settings.schedule_availability_icon_size === undefined && sz === 16);
              return (
                <button
                  key={sz}
                  type="button"
                  onClick={() => setSettings(s => ({ ...s, schedule_availability_icon_size: sz }))}
                  style={{
                    appearance: 'none',
                    padding: '8px 14px', borderRadius: 8,
                    background: current ? 'rgba(0,212,255,0.15)' : T.s2,
                    color: current ? 'var(--ice)' : T.text,
                    border: `1.5px solid ${current ? 'var(--ice)' : T.border}`,
                    fontSize: 12, fontWeight: 700,
                    fontFamily: 'var(--font-display)',
                    cursor: 'pointer',
                  }}>
                  {sz}px {current ? '·' : ''}
                  <span style={{ fontSize: sz, marginLeft: 6, fontWeight: 700 }}>✅</span>
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      <button
        onClick={save}
        disabled={saving}
        style={{
          padding: '10px 24px', borderRadius: 10, fontFamily: 'var(--font-display)',
          fontWeight: 700, fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase',
          background: 'var(--ice)', color: '#000', border: 'none', cursor: 'pointer',
          opacity: saving ? 0.7 : 1,
        }}>
        {saving ? 'Saving…' : 'Save Settings'}
      </button>
    </div>
  );
}

// ─── Chat Attachments admin card (Apr 2026) ──────────────────────────────────
// Lives inside System Settings. Three controls:
//   • Master toggle — enables/disables file uploads platform-wide.
//     Coaches and players don't see the paperclip until this is on.
//   • Retention days — global. Files older than this are reaped
//     daily by the retention cron. Default 365.
//   • Per-team caps & usage — table of the top consumers with their
//     current usage and an editable cap (in MB).
// Saves are individual (per-control) and immediate, with a toast for
// each. This avoids a giant "save everything" button that's easy to
// click without realizing you've changed something.
function ChatAttachmentsCard() {
  const [data, setData] = useState(null);
  const [savingFlag,      setSavingFlag]      = useState(false);
  const [savingRetention, setSavingRetention] = useState(false);
  const [savingTeamId,    setSavingTeamId]    = useState(null);
  const [retentionDraft,  setRetentionDraft]  = useState('');
  const [capDrafts,       setCapDrafts]       = useState({});

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/chat-settings');
      setData(data);
      setRetentionDraft(String(data.retentionDays));
      // Seed cap drafts from server-side values so each row's input
      // shows the current cap.
      const drafts = {};
      for (const t of (data.usage?.topTeams || [])) {
        drafts[t.id] = t.capMb == null ? '' : String(t.capMb);
      }
      setCapDrafts(drafts);
    } catch (err) {
      toast.error('Failed to load chat settings');
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const toggleEnabled = async (next) => {
    setSavingFlag(true);
    try {
      const { data: res } = await api.put('/admin/chat-settings', { enabled: next });
      setData(d => ({ ...d, enabled: res.enabled }));
      toast.success(next ? 'Chat attachments enabled' : 'Chat attachments disabled');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
    setSavingFlag(false);
  };

  const saveRetention = async () => {
    const n = parseInt(retentionDraft);
    if (!Number.isFinite(n) || n < 1 || n > 3650) {
      toast.error('Retention must be between 1 and 3650 days');
      return;
    }
    setSavingRetention(true);
    try {
      await api.put('/admin/chat-settings', { retentionDays: n });
      setData(d => ({ ...d, retentionDays: n }));
      toast.success(`Retention set to ${n} days`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
    setSavingRetention(false);
  };

  const saveCap = async (teamId) => {
    const raw = capDrafts[teamId];
    let capMb;
    if (raw === '' || raw === null) {
      capMb = null;
    } else {
      const n = parseInt(raw);
      if (!Number.isFinite(n) || n < 1 || n > 10000) {
        toast.error('Cap must be a number between 1 and 10000 (or empty for unlimited)');
        return;
      }
      capMb = n;
    }
    setSavingTeamId(teamId);
    try {
      await api.put(`/admin/teams/${teamId}/chat-quota`, { capMb });
      toast.success('Team quota updated');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
    setSavingTeamId(null);
  };

  if (!data) {
    return (
      <div style={{
        background: T.s2, border: `1px solid ${T.border}`,
        borderRadius: 14, padding: 20, marginBottom: 16,
        color: T.text2,
      }}>Loading chat attachment settings…</div>
    );
  }

  const formatBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };

  return (
    <div style={{
      background: T.s2, border: `1px solid ${T.border}`,
      borderRadius: 14, padding: 20, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <span style={{ fontSize: 16 }}>📎</span>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>
          Chat Attachments
        </h3>
      </div>
      <p style={{ fontSize: 12, color: T.text2, marginBottom: 16, lineHeight: 1.5 }}>
        File uploads in team chat. Allowed types: PNG, JPEG, GIF, WebP, PDF.
        Per-file hard limit: 10 MB.
      </p>

      {/* Master enable toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '10px 14px',
        background: data.enabled ? `${T.ice}10` : T.s3,
        border: `1px solid ${data.enabled ? `${T.ice}30` : T.border}`,
        borderRadius: 10, marginBottom: 16,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
            File uploads {data.enabled ? 'enabled' : 'disabled'}
          </div>
          <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>
            When off, the paperclip button is hidden and uploads return 403.
          </div>
        </div>
        <button
          disabled={savingFlag}
          onClick={() => toggleEnabled(!data.enabled)}
          style={{
            padding: '7px 18px', borderRadius: 8,
            background: data.enabled ? T.penalty : T.ice,
            color: '#000', border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: 12,
            fontFamily: 'var(--font-display)', letterSpacing: '0.06em',
            textTransform: 'uppercase',
            opacity: savingFlag ? 0.6 : 1, flexShrink: 0,
          }}>
          {savingFlag ? '…' : (data.enabled ? 'Turn Off' : 'Turn On')}
        </button>
      </div>

      {/* Retention days */}
      <div style={{ marginBottom: 18 }}>
        <label style={{
          display: 'block', fontSize: 10, fontWeight: 700, color: T.text2,
          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6,
        }}>
          Retention (days)
        </label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', maxWidth: 360 }}>
          <input
            type="number"
            min="1" max="3650"
            value={retentionDraft}
            onChange={e => setRetentionDraft(e.target.value)}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 8,
              background: T.s3, border: `1px solid ${T.border}`, color: T.text,
              fontSize: 14, fontFamily: 'inherit',
            }}
          />
          <button
            disabled={savingRetention || retentionDraft === String(data.retentionDays)}
            onClick={saveRetention}
            style={{
              padding: '8px 16px', borderRadius: 8,
              background: T.ice, color: '#000', border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: 12,
              opacity: (savingRetention || retentionDraft === String(data.retentionDays)) ? 0.5 : 1,
            }}>
            {savingRetention ? '…' : 'Save'}
          </button>
        </div>
        <p style={{ fontSize: 11, color: T.text2, marginTop: 6, lineHeight: 1.5 }}>
          Files older than this are deleted nightly at 03:30 UTC. Default: 365 days.
        </p>
      </div>

      {/* Per-team usage table */}
      <div>
        <div style={{
          fontSize: 10, fontWeight: 700, color: T.text2,
          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8,
        }}>
          Storage usage by team
        </div>
        <p style={{ fontSize: 11, color: T.text2, marginBottom: 10, lineHeight: 1.5 }}>
          Default cap: {data.defaultCapMb} MB. Total used across all teams:{' '}
          <strong>{formatBytes(data.usage?.totalBytes || 0)}</strong> ({data.usage?.teamsWithAttachments || 0} team{(data.usage?.teamsWithAttachments) === 1 ? '' : 's'} have uploaded files).
          Leave a cap blank for unlimited.
        </p>
        {(data.usage?.topTeams || []).length === 0 ? (
          <div style={{
            padding: 14, background: T.s3, border: `1px solid ${T.border}`,
            borderRadius: 8, fontSize: 12, color: T.text2, textAlign: 'center',
          }}>
            No team has uploaded any files yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.usage.topTeams.map(t => {
              const usedMb    = (t.usedBytes || 0) / 1024 / 1024;
              const capMb     = t.capMb;
              const pct       = capMb ? Math.min(100, (usedMb / capMb) * 100) : null;
              const draft     = capDrafts[t.id] ?? '';
              const isDirty   = draft !== (capMb == null ? '' : String(capMb));
              const isSaving  = savingTeamId === t.id;
              return (
                <div key={t.id} style={{
                  padding: 10, background: T.s3,
                  border: `1px solid ${T.border}`, borderRadius: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>
                        Used: <strong>{formatBytes(t.usedBytes)}</strong>
                        {capMb != null && ` of ${capMb} MB${pct != null ? ` (${pct.toFixed(0)}%)` : ''}`}
                        {capMb == null && ' · unlimited'}
                      </div>
                      {pct != null && (
                        <div style={{
                          marginTop: 6, height: 4, borderRadius: 2,
                          background: T.s2, overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${pct}%`, height: '100%',
                            background: pct > 90 ? T.penalty : pct > 70 ? T.puck : T.ice,
                          }} />
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      <input
                        type="number"
                        min="1" max="10000"
                        placeholder="MB"
                        value={draft}
                        onChange={e => setCapDrafts(d => ({ ...d, [t.id]: e.target.value }))}
                        style={{
                          width: 80, padding: '6px 8px', borderRadius: 6,
                          background: T.surface, border: `1px solid ${T.border}`, color: T.text,
                          fontSize: 13, fontFamily: 'inherit',
                        }}
                        title="Leave blank for unlimited"
                      />
                      <button
                        disabled={!isDirty || isSaving}
                        onClick={() => saveCap(t.id)}
                        style={{
                          padding: '6px 12px', borderRadius: 6,
                          background: T.ice, color: '#000', border: 'none', cursor: 'pointer',
                          fontWeight: 700, fontSize: 11,
                          opacity: (!isDirty || isSaving) ? 0.5 : 1,
                        }}>
                        {isSaving ? '…' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Email Provider Card (inside System Settings tab) ────────────────────────
// Lets the super-admin swap the email transport at runtime:
//   - 'sendgrid' — HTTP API keyed by SENDGRID_API_KEY env var
//   - 'smtp'     — Office 365 / Exchange / generic SMTP via SMTP_HOST/USER/PASS
// Credentials live in .env only. This toggle just picks which client the
// backend builds outbound sends through. Includes a "Send test email" input
// so the admin can verify delivery before flipping fully to a new provider.
function WhatsAppStatusCard({ Card }) {
  const [st, setSt] = useState(null);
  useEffect(() => { let c = false; api.get('/admin/whatsapp').then(({ data }) => { if (!c) setSt(data); }).catch(() => { if (!c) setSt({ enabled: false }); }); return () => { c = true; }; }, []);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const Row = ({ k, v, ok }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, padding: '4px 0', borderBottom: `1px solid ${T.border}` }}>
      <span style={{ color: T.text2 }}>{k}</span><span style={{ color: ok === false ? T.fire : T.text, fontFamily: 'monospace' }}>{v}</span>
    </div>
  );
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <MessageSquare size={16} color="#25D366" />
        <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>WhatsApp game updates</h3>
      </div>
      <p style={{ fontSize: 12, color: T.text2, marginBottom: 12, lineHeight: 1.5 }}>
        Teams send goals, periods and the final score to subscribed parents over the WhatsApp Business Platform. The
        credentials are set in <code>backend/.env</code> (WHATSAPP_ACCESS_TOKEN, WHATSAPP_PHONE_NUMBER_ID, WHATSAPP_APP_SECRET,
        WHATSAPP_VERIFY_TOKEN, WHATSAPP_DISPLAY_NUMBER) and the one message template must exist in Meta Business Manager.
      </p>
      {!st ? <div style={{ fontSize: 12, color: T.text2 }}>Checking…</div> : (
        <div>
          <Row k="Sending" v={st.enabled ? 'configured' : 'not configured'} ok={st.enabled} />
          <Row k="Number parents see" v={st.display || '—'} ok={!!st.display} />
          <Row k="Template" v={`${st.template || 'gameday_update'} (${st.lang || 'en_US'})`} />
          <Row k="Webhook URL (paste in Meta)" v={`${origin}${st.webhookPath || '/api/hooks/whatsapp'}`} />
          <Row k="Webhook verify token" v={st.webhookVerify ? 'set' : 'missing'} ok={st.webhookVerify} />
          <Row k="Webhook signature check" v={st.webhookSignature ? 'on (app secret set)' : 'OFF — set WHATSAPP_APP_SECRET'} ok={st.webhookSignature} />
          <Row k="Graph API" v={st.version || 'v21.0'} />
        </div>
      )}
      <p style={{ fontSize: 11, color: T.text2, marginTop: 10, lineHeight: 1.5 }}>
        Template to create (UTILITY, {st?.lang || 'en_US'}): name <code>{st?.template || 'gameday_update'}</code>, body <code>{'{{1}}: {{2}}'}</code>,
        footer "Reply STOP to leave these updates." Parameter 1 is the team name, 2 the update. Subscribe the webhook to the
        <code> messages</code> field.
      </p>
    </Card>
  );
}

function EmailProviderCard({ settings, setSettings, Card }) {
  const [testEmail,   setTestEmail]   = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult,  setTestResult]  = useState(null); // { sent, provider?, error?, code? }
  // Apr 2026 — provider config status from the server (which providers
  // have credentials in .env, plus the FROM mismatch flag for HVE).
  // Fetched once on mount + after each test, since a test might
  // invalidate stale conclusions ("oh, the password did work after all").
  const [providerStatus, setProviderStatus] = useState(null);

  const active = settings.email_provider || 'sendgrid';

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/email-status');
      setProviderStatus(data);
    } catch (err) {
      // Non-fatal — UI just hides the status pills if the endpoint
      // isn't reachable. Won't block toggling/saving.
      console.warn('email-status fetch failed:', err.message);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const sendTest = async () => {
    const trimmed = testEmail.trim();
    if (!trimmed) { toast.error('Enter an email address'); return; }
    setTestSending(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/admin/email-test', { to: trimmed });
      setTestResult(data);
      if (data.sent) {
        toast.success(`📧 Test email sent via ${data.provider}`);
      } else {
        toast.error(`Send failed: ${data.error || 'unknown error'}`);
      }
    } catch (err) {
      setTestResult({ sent: false, error: err.response?.data?.error || err.message });
      toast.error(err.response?.data?.error || 'Test failed');
    }
    setTestSending(false);
    // Refresh in case the admin just edited .env and restarted the
    // container — the new state should appear without a page refresh.
    loadStatus();
  };

  // Apr 2026 — Authenticated SMTP providers typically require the
  // From: address to exactly match SMTP_USER. If they differ, the
  // server rejects with "550 5.7.60 Sender does not match SMTP
  // authenticated user" and the failures look mysterious. Surface
  // it prominently so the admin fixes it BEFORE flipping the toggle.
  const smtpFromMismatch = providerStatus?.smtp?.fromMismatch
    && active === 'smtp'; // only relevant when SMTP is the active choice
  // Apr 30 2026 — Azure ACS Email also requires the sender address to
  // match a verified MailFrom in the resource. Same shape as SMTP
  // mismatch — show a yellow warning banner when the active toggle
  // is azure AND the addresses disagree.
  const azureFromMismatch = providerStatus?.azure?.fromMismatch
    && active === 'azure';

  // Status pill helper — renders a small badge under each option.
  const StatusPill = ({ ok, label }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 10, fontWeight: 700, padding: '2px 7px',
      borderRadius: 999, marginTop: 4, alignSelf: 'flex-start',
      background: ok ? 'rgba(16,185,129,0.12)' : 'rgba(255,68,68,0.10)',
      color: ok ? '#10b981' : '#ff6b6b',
      border: `1px solid ${ok ? 'rgba(16,185,129,0.3)' : 'rgba(255,68,68,0.25)'}`,
    }}>
      {ok ? '✓' : '⚠'} {label}
    </span>
  );

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Mail size={16} color={T.ice} />
        <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Email Provider</h3>
      </div>
      <p style={{ fontSize: 12, color: T.text2, marginBottom: 16, lineHeight: 1.5 }}>
        Which service sends invites, RSVP reminders, and coach messages. Secrets live
        in the server's <code>.env</code> file — this toggle just picks which one to use.
        The change takes effect on the next send. Remember to save the settings page
        after flipping this toggle.
      </p>

      {/* Apr 30 2026 — switched from fixed `1fr 1fr` (2 columns) to a
          responsive auto-fit so the now-three providers (SendGrid /
          Microsoft SMTP / Azure ACS) lay out cleanly: 3 across on wide
          screens, wrapping to 2 or 1 on narrow. minmax(220, 1fr) keeps
          each card readable; smaller would squeeze the description. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 14 }}>
        {[
          {
            key: 'sendgrid',
            label: 'SendGrid',
            badge: 'HTTP API',
            desc: 'Via SENDGRID_API_KEY. Counts against your plan; iCloud users may see HM08 blocks unless your sending IP has good reputation.',
          },
          {
            key: 'smtp',
            label: 'Microsoft SMTP',
            badge: 'Exchange Online',
            // Apr 2026 — originally labelled "SMTP (O365)", briefly mis-labelled
            // "Microsoft HVE" before we learned HVE is internal-recipients only
            // (won't deliver to @icloud.com / @gmail.com etc.). The code path
            // is plain SMTP AUTH client submission via smtp.office365.com:587,
            // which works for external recipients but requires:
            //   1. A LICENSED mailbox (Business Basic ~$7/mo or higher)
            //   2. Per-mailbox SMTP AUTH enabled (Set-CASMailbox …)
            //   3. Tenant-wide SMTP AUTH not blocked (Set-TransportConfig …)
            // Microsoft is deprecating SMTP AUTH basic auth ~Dec 2026 in favor
            // of OAuth. After that, this path will need to migrate to OAuth or
            // to Azure Communication Services Email. Documented for whoever
            // (probably future-me) revisits this card.
            desc: 'Generic SMTP via Exchange Online. Requires a licensed mailbox + SMTP AUTH enabled. Note: Microsoft is deprecating basic auth in late 2026.',
          },
          {
            key: 'azure',
            label: 'Azure Communication Services',
            badge: 'ACS Email',
            // Apr 30 2026 — wired up but not the default. Pricing as of
            // Apr 30 2026: ~$0.00035 USD/email + ~$0.00017 USD/MB
            // transferred (Canada Central). At 50K emails/mo, roughly
            // matches SendGrid Essentials in cost. Becomes attractive
            // when volume forces a SendGrid Pro upgrade ($89.95/mo)
            // or above. Microsoft's modern, supported alternative for
            // when SMTP basic auth is deprecated.
            desc: 'Microsoft\'s pay-per-email service. ~$0.00035 USD per email. Requires a verified MailFrom domain in Azure and AZURE_COMMUNICATION_CONNECTION_STRING in .env.',
          },
        ].map(opt => {
          const isActive = active === opt.key;
          // Pull config status for this option so we can show the pill.
          const ps = providerStatus?.[opt.key];
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSettings(s => ({ ...s, email_provider: opt.key }))}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6,
                padding: '12px 14px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                background: isActive ? 'rgba(0,212,255,0.1)' : T.s3,
                border: `1.5px solid ${isActive ? 'var(--ice-a40)' : T.border}`,
                transition: 'all 0.15s',
              }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: isActive ? T.ice : T.text }}>
                {isActive ? '✓ ' : ''}{opt.label}
              </span>
              <span style={{ fontSize: 10, color: isActive ? T.ice : '#a78bfa', fontWeight: 700, letterSpacing: '0.06em' }}>
                {opt.badge}
              </span>
              <span style={{ fontSize: 11, color: T.text2, lineHeight: 1.4 }}>{opt.desc}</span>
              {ps && <StatusPill ok={ps.configured} label={ps.configured ? 'Configured' : 'Missing creds'} />}
            </button>
          );
        })}
      </div>

      {/* Apr 2026 — SMTP-AUTH-specific FROM/SMTP_USER mismatch warning.
          When using Microsoft SMTP (and most other authenticated SMTP
          providers), the From: address must match the authenticated
          mailbox or the server rejects with "550 5.7.60 Sender does
          not match SMTP authenticated user". */}
      {smtpFromMismatch && (
        <div style={{
          marginBottom: 14, padding: '10px 12px', borderRadius: 8, fontSize: 12,
          background: 'rgba(255,189,69,0.10)', border: '1px solid rgba(255,189,69,0.35)',
          color: '#ffa726', lineHeight: 1.55,
        }}>
          <strong>⚠ This SMTP provider will likely reject these sends.</strong>
          <div style={{ color: T.text, marginTop: 4 }}>
            Most authenticated SMTP providers (Microsoft Exchange, Gmail,
            etc.) require the <code>From:</code> address to exactly match
            the SMTP user. Right now they're different:
          </div>
          <ul style={{ margin: '6px 0 0 18px', color: T.text2 }}>
            <li>From: <code>{providerStatus?.fromEmail}</code></li>
            <li>SMTP_USER: <code>{providerStatus?.smtp?.user}</code></li>
          </ul>
          <div style={{ marginTop: 6, color: T.text2 }}>
            Set <code>EMAIL_FROM_EMAIL</code> in <code>backend/.env</code> to match
            <code> SMTP_USER</code>, then restart the backend.
          </div>
        </div>
      )}

      {/* Apr 30 2026 — analogous warning for Azure ACS Email. The ACS
          resource requires the senderAddress to match a verified
          MailFrom in the configured domain. Mismatch causes sends to
          fail with "sender domain not verified" — surface it before
          the user discovers it via failed sends. */}
      {azureFromMismatch && (
        <div style={{
          marginBottom: 14, padding: '10px 12px', borderRadius: 8, fontSize: 12,
          background: 'rgba(255,189,69,0.10)', border: '1px solid rgba(255,189,69,0.35)',
          color: '#ffa726', lineHeight: 1.55,
        }}>
          <strong>⚠ Azure ACS will reject these sends.</strong>
          <div style={{ color: T.text, marginTop: 4 }}>
            Azure Communication Services Email requires the sender
            address to match a verified MailFrom in the resource's
            domain. Right now they don't match:
          </div>
          <ul style={{ margin: '6px 0 0 18px', color: T.text2 }}>
            <li>From: <code>{providerStatus?.fromEmail}</code></li>
            <li>AZURE_SENDER_ADDRESS: <code>{providerStatus?.azure?.sender}</code></li>
          </ul>
          <div style={{ marginTop: 6, color: T.text2 }}>
            Either change <code>EMAIL_FROM_EMAIL</code> to match
            <code> AZURE_SENDER_ADDRESS</code>, or update the latter
            to a verified address in your ACS resource. Restart the
            backend after.
          </div>
        </div>
      )}

      {/* Apr 2026 — env-var setup quickref. Shown collapsed by default
          (just the heading) so it doesn't bury the controls; expand for
          the exact .env block to paste. */}
      <details style={{
        marginBottom: 14, padding: '10px 12px', borderRadius: 8,
        background: T.s3, border: `1px solid ${T.border}`,
      }}>
        <summary style={{ fontSize: 12, fontWeight: 700, color: T.text, cursor: 'pointer' }}>
          Server <code>.env</code> setup reference
        </summary>
        <div style={{ marginTop: 10, fontSize: 11, color: T.text2, lineHeight: 1.55 }}>
          <p style={{ margin: '0 0 8px' }}>
            For <strong>SendGrid</strong>:
          </p>
          <pre style={{
            background: '#0d1117', color: '#c9d1d9', padding: '8px 10px',
            borderRadius: 6, fontSize: 11, overflow: 'auto', margin: 0,
          }}>
{`SENDGRID_API_KEY=SG.…
EMAIL_FROM_EMAIL=noreply@game-day.app
EMAIL_FROM_NAME=Game Day`}
          </pre>
          <p style={{ margin: '12px 0 8px' }}>
            For <strong>Microsoft SMTP</strong> (Exchange Online, requires licensed mailbox):
          </p>
          <pre style={{
            background: '#0d1117', color: '#c9d1d9', padding: '8px 10px',
            borderRadius: 6, fontSize: 11, overflow: 'auto', margin: 0,
          }}>
{`SMTP_HOST=smtp.office365.com
SMTP_PORT=587
SMTP_USER=noreply@game-day.app
SMTP_PASS=<password from Bitwarden>
EMAIL_FROM_EMAIL=noreply@game-day.app
EMAIL_FROM_NAME=Game Day`}
          </pre>
          <p style={{ margin: '12px 0 8px' }}>
            For <strong>Azure Communication Services</strong> (Email):
          </p>
          <pre style={{
            background: '#0d1117', color: '#c9d1d9', padding: '8px 10px',
            borderRadius: 6, fontSize: 11, overflow: 'auto', margin: 0,
          }}>
{`AZURE_COMMUNICATION_CONNECTION_STRING=endpoint=https://<resource>.communication.azure.com/;accesskey=<key from Azure portal>
AZURE_SENDER_ADDRESS=noreply@game-day.app
EMAIL_FROM_EMAIL=noreply@game-day.app
EMAIL_FROM_NAME=Game Day`}
          </pre>
          <p style={{ margin: '8px 0 0', fontSize: 10.5, color: T.text2, lineHeight: 1.5 }}>
            <strong style={{ color: T.text }}>Setup:</strong> create an Azure Communication
            Services resource, add an Email Communication Service, verify your domain
            (or use the Azure-managed subdomain for testing), then copy the connection
            string from the resource's Keys page. <code>AZURE_SENDER_ADDRESS</code> must
            match a verified MailFrom address.
          </p>
          <p style={{ margin: '12px 0 0', color: T.text2 }}>
            After editing <code>.env</code>, restart the backend container so it picks
            up the new vars: <code>docker compose -f docker-compose.prod.yml restart backend</code>.
          </p>
        </div>
      </details>

      {/* Test email row */}
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, marginTop: 4 }}>
        <p style={{ fontSize: 12, color: T.text2, marginBottom: 10, lineHeight: 1.5 }}>
          Send a test email using the currently-saved provider.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="email"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="your@email.com"
            disabled={testSending}
            style={{
              flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 13,
              background: T.s3, color: T.text, border: `1px solid ${T.border}`,
              outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={sendTest}
            disabled={testSending || !testEmail.trim()}
            style={{
              padding: '9px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
              background: testSending ? T.s3 : 'var(--ice-a12)',
              color: T.ice, border: '1px solid var(--ice-a30)',
              cursor: testSending ? 'wait' : 'pointer', whiteSpace: 'nowrap',
            }}>
            {testSending ? 'Sending…' : 'Send Test'}
          </button>
        </div>

        {testResult && (
          <div style={{
            marginTop: 10, padding: '10px 12px', borderRadius: 8, fontSize: 12,
            background: testResult.sent ? 'rgba(16,185,129,0.1)' : 'rgba(255,68,68,0.08)',
            border: `1px solid ${testResult.sent ? 'rgba(16,185,129,0.3)' : 'rgba(255,68,68,0.25)'}`,
            color: testResult.sent ? '#10b981' : '#ff6b6b',
            lineHeight: 1.5,
          }}>
            {testResult.sent
              ? `✓ Delivered via ${testResult.provider}. Check your inbox (and spam folder).`
              : `✗ Failed${testResult.code ? ` [${testResult.code}]` : ''}: ${testResult.error || 'Unknown error'}`}
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Main AdminPage ───────────────────────────────────────────────────────────
// ─── TAB: Banned Emails ───────────────────────────────────────────────────────
function BannedEmailsTab() {
  const [bans,           setBans]           = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [newEmail,       setNewEmail]       = useState('');
  const [newReason,      setNewReason]      = useState('');
  const [adding,         setAdding]         = useState(false);
  const [unbanConfirmId, setUnbanConfirmId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/banned-emails');
      setBans(data);
    } catch { toast.error('Failed to load banned emails'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addBan = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setAdding(true);
    try {
      await api.post('/admin/banned-emails', { email, reason: newReason.trim() || null });
      toast.success(`${email} banned`);
      setNewEmail(''); setNewReason('');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to ban email'); }
    setAdding(false);
  };

  const removeBan = async (id, email) => {
    if (unbanConfirmId !== id) { setUnbanConfirmId(id); return; }
    setUnbanConfirmId(null);
    try {
      await api.delete(`/admin/banned-emails/${id}`);
      setBans(prev => prev.filter(b => b.id !== id));
      toast.success(`${email} unbanned`);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to remove ban'); }
  };

  return (
    <div>
      {/* Add ban form */}
      <Card style={{ padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Ban size={16} style={{ color: 'var(--puck)' }} />
          <Label>Ban an Email Address</Label>
        </div>
        <form onSubmit={addBan} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 2, minWidth: 200 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: T.text2, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Email *</label>
            <input type="email" required value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="bad@actor.com"
              style={{ width: '100%', padding: '9px 12px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 9, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: 2, minWidth: 180 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: T.text2, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Reason</label>
            <input type="text" value={newReason} onChange={e => setNewReason(e.target.value)} placeholder="Spam, abuse, fraud…"
              style={{ width: '100%', padding: '9px 12px', background: T.s2, border: `1px solid ${T.border}`, borderRadius: 9, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <button type="submit" disabled={adding} style={{ padding: '9px 20px', borderRadius: 9, background: 'rgba(255,149,0,0.18)', border: '1px solid rgba(255,149,0,0.4)', color: 'var(--puck)', cursor: 'pointer', fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            <Ban size={13} style={{ display: 'inline', marginRight: 5 }} />{adding ? 'Banning…' : 'Ban Email'}
          </button>
        </form>
      </Card>

      {/* Bans list */}
      <Card>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Label>Banned Emails ({bans.length})</Label>
          <button onClick={load} style={{ background: 'none', border: 'none', color: T.text2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={12} /> <span style={{ fontSize: 11 }}>Refresh</span>
          </button>
        </div>
        {loading ? <LoadingGrid rows={3} /> : bans.length === 0 ? (
          <p style={{ padding: 24, color: T.text2, fontSize: 13 }}>No banned emails yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  {['Email', 'Reason', 'Banned By', 'Date', ''].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left' }}><Label>{h}</Label></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bans.map(b => (
                  <tr key={b.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontSize: 13, color: T.text }}>{b.email}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: T.text2 }}>{b.reason || '—'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: T.text2 }}>{b.bannedBy}</td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: T.text2 }}>
                      {b.bannedAt ? formatDistanceToNow(new Date(b.bannedAt), { addSuffix: true }) : '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={() => removeBan(b.id, b.email)}
                        title={unbanConfirmId === b.id ? 'Tap again to confirm' : 'Remove ban'}
                        style={{
                          padding: '5px 10px', borderRadius: 7, cursor: 'pointer',
                          fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-display)',
                          letterSpacing: '0.06em',
                          background: unbanConfirmId === b.id ? T.goal : `${T.goal}12`,
                          border: `1px solid ${T.goal}40`,
                          color: unbanConfirmId === b.id ? '#000' : T.goal,
                        }}>
                        {unbanConfirmId === b.id ? 'Confirm?' : 'Unban'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── TAB: Stripe Diagnostics ─────────────────────────────────────────────────
// v2.5.80 (May 7 2026) — replaces the "paste fetch into devtools" workflow
// for Stripe Connect debugging. Shows envPresence (which secrets are set,
// prefix+length only — never echoes secret material) and the result of
// independent test/live mode checks: balance.retrieve (auth) and
// accounts.list (Connect enabled). Each error includes type/message/code
// so admins can copy-paste into a support ticket without having to read
// devtools network responses.
// ─────────────────────────────────────────────────────────────────────────
// Finance report (2026-10-21, per Gilles) — every spare payment: what the
// spare paid, Stripe's cut, what the coach received, what the platform
// kept. Rows without an exact Stripe fee (older payments, before the
// fee-reversal fix) show an estimate until "Sync from Stripe" fills them.
// ─────────────────────────────────────────────────────────────────────────
function FinanceReportCard() {
  const [days, setDays] = useState(365);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get('/admin/finance/spares', { params: { days } }); setData(d); }
    catch (e) { setData({ error: e.response?.data?.error || 'Could not load' }); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setBusy(true);
    try {
      let total = 0, again = true;
      while (again) {
        const { data: r } = await api.post('/admin/finance/sync-fees');
        total += r.synced || 0; again = !!r.remainingBatch && (r.synced || 0) > 0;
      }
      toast.success(total ? `Exact fees pulled from Stripe for ${total} payment${total === 1 ? '' : 's'}` : 'Everything already exact');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Sync failed'); }
    finally { setBusy(false); }
  };

  const $ = (c) => `$${((Number(c) || 0) / 100).toFixed(2)}`;
  const cell = { padding: '6px 8px', fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: `1px solid ${T.border}`, fontVariantNumeric: 'tabular-nums' };
  const left = { ...cell, textAlign: 'left' };
  const head = { ...cell, fontSize: 10, fontWeight: 700, color: T.text2, textTransform: 'uppercase', letterSpacing: '0.05em' };
  const btn = { background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, padding: '4px 10px', color: T.text2, cursor: 'pointer', fontSize: 11 };

  const t = data?.totals;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <DollarSign size={16} color={T.ice} />
        <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Finance report — spare payments</h3>
        <span style={{ fontSize: 12, color: T.text2 }}>what the spare paid → Stripe&rsquo;s cut → the coach&rsquo;s share → what you kept</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <select value={days} onChange={e => setDays(Number(e.target.value))} style={{ ...btn, cursor: 'pointer' }}>
            {[30, 90, 180, 365, 730].map(d => <option key={d} value={d}>last {d} days</option>)}
          </select>
          <button type="button" style={btn} onClick={sync} disabled={busy}>{busy ? 'Syncing…' : 'Sync exact fees from Stripe'}</button>
          <button type="button" style={btn} onClick={load}>Refresh</button>
        </div>
      </div>
      {!data && <div style={{ color: T.text2, fontSize: 13 }}>Loading…</div>}
      {data?.error && <div style={{ color: T.penalty || '#f66', fontSize: 13 }}>{data.error}</div>}
      {t && (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 10 }}>
          {[['Collected from spares', t.grossCents], ['Stripe took', t.stripeFeeCents],
            ['Paid to coaches', t.coachCents], ['You kept', t.platformNetCents]].map(([l, v]) => (
            <div key={l}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.text2 }}>{l}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: l === 'You kept' ? T.ice : T.text, fontVariantNumeric: 'tabular-nums' }}>{$(v)}</div>
            </div>
          ))}
          <div style={{ alignSelf: 'end', fontSize: 11, color: T.text2 }}>
            {t.count} paid payment{t.count === 1 ? '' : 's'}
            {t.estimatedRows > 0 && <> · <span style={{ color: '#ff8a00' }}>{t.estimatedRows} with estimated Stripe fees — press Sync</span></>}
          </div>
        </div>
      )}
      {data?.rows?.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...head, textAlign: 'left' }}>Date</th>
              <th style={{ ...head, textAlign: 'left' }}>Team</th>
              <th style={{ ...head, textAlign: 'left' }}>Spare</th>
              <th style={head}>Spare paid</th>
              <th style={head}>Stripe fee</th>
              <th style={head}>Coach got</th>
              <th style={head}>You kept</th>
              <th style={{ ...head, textAlign: 'left' }}>Status</th>
            </tr></thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.id}>
                  <td style={left}>{r.paidAt ? new Date(r.paidAt).toLocaleDateString() : '—'}</td>
                  <td style={left}>{r.team}</td>
                  <td style={left}>{r.payer || '—'}</td>
                  <td style={cell}>{$(r.grossCents)}</td>
                  <td style={cell} title={r.stripeFeeExact ? 'Exact (from Stripe)' : 'Estimated 2.9% + 30¢ — press Sync for the exact figure'}>
                    {$(r.stripeFeeCents)}{r.stripeFeeExact ? '' : ' ~'}
                  </td>
                  <td style={cell}>{$(r.coachCents)}</td>
                  <td style={{ ...cell, color: r.platformNetCents >= r.platformFeeCents ? T.ice : '#ff8a00', fontWeight: 700 }}
                    title={r.routing === 'destination (legacy)' ? 'Legacy routing: Stripe\u2019s fee came out of your dollar on this payment' : r.routing}>
                    {$(r.platformNetCents)}
                  </td>
                  <td style={left}>{r.status}{r.mode === 'test' ? ' · test' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && !data.error && !(data.rows || []).length && <div style={{ color: T.text2, fontSize: 13 }}>No spare payments in this period.</div>}
    </div>
  );
}

function StripeDiagnosticTab() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // v2.5.83 — connected-teams browser state. Pulls every team with
  // a Stripe Connect link so a platform admin can see them all and
  // trigger Reset on any (regardless of admin's own team membership).
  // Backed by GET /api/admin/stripe-connected-teams.
  const [teamsList,    setTeamsList]    = useState(null);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError,   setTeamsError]   = useState(null);
  // resetTarget = the team row being reset, or null. Drives the modal.
  const [resetTarget,      setResetTarget]      = useState(null);
  const [resetConfirmName, setResetConfirmName] = useState('');
  const [resetting,        setResetting]        = useState(false);

  const runDiagnostic = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: result } = await api.get('/admin/stripe-diagnose');
      setData(result);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Diagnostic failed');
    }
    setLoading(false);
  };

  // v2.5.83 — load the connected-teams list. Refreshable independently
  // of the main diagnostic.
  const loadConnectedTeams = async () => {
    setTeamsLoading(true);
    setTeamsError(null);
    try {
      const { data: result } = await api.get('/admin/stripe-connected-teams');
      setTeamsList(result.teams || []);
    } catch (err) {
      setTeamsError(err.response?.data?.error || err.message || 'Failed to load connected teams');
    }
    setTeamsLoading(false);
  };

  // v2.5.83 — perform the reset. Calls the same admin-only endpoint
  // the per-team button uses. On success, refreshes the teams list so
  // the row disappears (since stripe_account_id is now NULL and the
  // listing only includes IS NOT NULL rows).
  const closeResetModal = () => {
    if (resetting) return;
    setResetTarget(null);
    setResetConfirmName('');
  };
  const performReset = async () => {
    if (!resetTarget?.name || !resetTarget?.id) return;
    if ((resetConfirmName || '').trim() !== resetTarget.name.trim()) return;
    setResetting(true);
    try {
      const { data: rr } = await api.delete(`/teams/${resetTarget.id}/stripe-connect/account`);
      // v2.5.274 — a SHARED account (mig 111) is only unlinked from this
      // team; the Stripe account stays for the other teams using it.
      toast.success(rr?.unlinkedOnly
        ? `${resetTarget.name}: unlinked. The Stripe account stays — ${rr.sharedWith} other team${rr.sharedWith === 1 ? '' : 's'} still use it.`
        : `${resetTarget.name}: Stripe Connect link reset.`);
      setResetTarget(null);
      setResetConfirmName('');
      await loadConnectedTeams();
    } catch (err) {
      const errBody = err.response?.data;
      if (err.response?.status === 409) {
        toast.error(errBody?.error || 'Reset blocked: account has charge history.', { duration: 8000 });
      } else {
        toast.error(errBody?.error || 'Failed to reset Stripe Connect.');
      }
    } finally {
      setResetting(false);
    }
  };

  // Auto-run on mount so admins see results immediately when they open
  // the tab. Avoids a "click to start" extra step.
  // v2.5.83 — also load the connected-teams list in parallel.
  useEffect(() => {
    runDiagnostic();
    loadConnectedTeams();
  }, []);

  // Helper: render a colored status pill so success/failure is visually
  // distinct without reading text.
  const Pill = ({ ok, label }) => (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 100,
      fontSize: 11,
      fontWeight: 700,
      background: ok ? `${T.zen}25` : `${T.fire}25`,
      color:      ok ? T.zen      : T.fire,
      border: `1px solid ${ok ? T.zen : T.fire}45`,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    }}>
      {ok ? '✓' : '✗'} {label}
    </span>
  );

  // Helper: copy result JSON to clipboard for support tickets / pasting
  // back to whoever's helping debug. Prevents needing devtools entirely.
  const copyJson = () => {
    if (!data) return;
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success('Diagnostic JSON copied to clipboard');
  };

  if (loading && !data) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: T.text2 }}>
        Running Stripe diagnostic…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: 16, borderRadius: 12,
        background: `${T.fire}15`, border: `1px solid ${T.fire}45`,
        color: T.fire,
      }}>
        <strong>Diagnostic failed:</strong> {error}
      </div>
    );
  }

  if (!data) return null;

  // Compute the human-friendly summary first so we can lead with it. The
  // most common bug pattern: live mode key resolves and balance call
  // works, but accounts.list fails → Connect not enabled in live mode.
  // Other patterns: key doesn't resolve at all (env var missing or wrong),
  // balance call fails (key wrong/expired/revoked).
  const live = data.liveMode || {};
  const test = data.testMode || {};
  const summary = (() => {
    const m = data.activeMode === 'live' ? live : test;
    const modeName = data.activeMode === 'live' ? 'Live' : 'Test';

    if (!m.keyResolved) {
      return {
        level: 'error',
        text: `No ${modeName.toLowerCase()}-mode key found. Set STRIPE_SECRET_KEY${
          data.activeMode === 'live' ? '_LIVE or STRIPE_SECRET_KEY (legacy fallback)' : '_TEST'
        } in backend/.env and restart the backend container.`,
      };
    }
    if (!m.balanceCallOk) {
      return {
        level: 'error',
        text: `${modeName}-mode key was rejected by Stripe. The key is set but invalid (wrong account, expired, or revoked). Check ${m.balanceError?.type || 'the error below'} for details.`,
      };
    }
    if (!m.connectCallOk) {
      return {
        level: 'error',
        text: `${modeName}-mode auth works, but Connect is not enabled for this Stripe platform account. Visit dashboard.stripe.com/connect (in ${modeName.toLowerCase()} mode — toggle in the upper-right) and complete Connect platform setup. Test and live mode are SEPARATE setups in Stripe.`,
      };
    }
    return {
      level: 'ok',
      text: `${modeName} mode is fully operational. Auth works and Connect is enabled. If onboarding is still failing for a specific team, the failure is something else — check Logs > System Events or Network tab for the actual Stripe error message.`,
    };
  })();

  const Card = ({ title, children }) => (
    <div style={{
      background: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: 14, padding: 16, marginBottom: 16,
    }}>
      <h3 style={{
        fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
        letterSpacing: '0.1em', textTransform: 'uppercase',
        color: T.text2, margin: '0 0 12px',
      }}>{title}</h3>
      {children}
    </div>
  );

  const renderModeBlock = (label, m) => (
    <div style={{
      padding: 12, borderRadius: 10,
      background: T.s2, border: `1px solid ${T.border}`,
      marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong style={{ color: T.text }}>{label}</strong>
        {m.keyResolved
          ? <Pill ok={m.balanceCallOk && m.connectCallOk} label={m.balanceCallOk && m.connectCallOk ? 'Operational' : 'Issues'} />
          : <Pill ok={false} label="Not Configured" />}
      </div>
      {!m.keyResolved && (
        <p style={{ fontSize: 13, color: T.text2, margin: 0 }}>
          {m.error || 'No key configured for this mode.'}
        </p>
      )}
      {m.keyResolved && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pill ok={!!m.balanceCallOk} label={m.balanceCallOk ? 'Auth OK' : 'Auth Failed'} />
            <span style={{ color: T.text2 }}>
              balance.retrieve()
              {m.livemode != null && ` — Stripe reports livemode: ${String(m.livemode)}`}
            </span>
          </div>
          {m.balanceError && (
            <pre style={{
              fontSize: 11, padding: 8, margin: '4px 0',
              background: T.bg, border: `1px solid ${T.border}`,
              borderRadius: 6, overflow: 'auto',
              color: T.fire, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
{`Type:    ${m.balanceError.type}
Message: ${m.balanceError.message}
Code:    ${m.balanceError.code || '(none)'}`}
            </pre>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <Pill ok={!!m.connectCallOk} label={m.connectCallOk ? 'Connect OK' : 'Connect Failed'} />
            <span style={{ color: T.text2 }}>accounts.list (Connect platform check)</span>
          </div>
          {m.connectError && (
            <pre style={{
              fontSize: 11, padding: 8, margin: '4px 0',
              background: T.bg, border: `1px solid ${T.border}`,
              borderRadius: 6, overflow: 'auto',
              color: T.fire, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
{`Type:    ${m.connectError.type}
Message: ${m.connectError.message}
Code:    ${m.connectError.code || '(none)'}`}
            </pre>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Action bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={runDiagnostic} disabled={loading} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 10,
          background: T.s2, border: `1px solid ${T.border}`,
          color: T.text2, cursor: loading ? 'wait' : 'pointer',
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          Re-run
        </button>
        <button onClick={copyJson} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 10,
          background: T.s2, border: `1px solid ${T.border}`,
          color: T.text2, cursor: 'pointer',
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          <Download size={13} />
          Copy JSON
        </button>
        <span style={{ marginLeft: 'auto', color: T.text2, fontSize: 13 }}>
          Active mode: <strong style={{ color: T.text }}>{data.activeMode || '(unknown)'}</strong>
        </span>
      </div>

      {/* Summary card — leads with the verdict for the active mode */}
      <div style={{
        padding: 14, borderRadius: 12, marginBottom: 16,
        background: summary.level === 'ok' ? `${T.zen}15` : `${T.fire}15`,
        border:     `1px solid ${summary.level === 'ok' ? T.zen : T.fire}45`,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {summary.level === 'ok'
            ? <CheckCircle  size={20} color={T.zen}  style={{ flexShrink: 0, marginTop: 2 }} />
            : <AlertTriangle size={20} color={T.fire} style={{ flexShrink: 0, marginTop: 2 }} />}
          <p style={{
            margin: 0, fontSize: 14, lineHeight: 1.5,
            color: summary.level === 'ok' ? T.zen : T.fire,
          }}>
            <strong>Summary:</strong> {summary.text}
          </p>
        </div>
      </div>

      {/* Env presence card */}
      <Card title="Env presence (backend/.env)">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, fontSize: 13 }}>
          {Object.entries(data.envPresence || {}).map(([k, v]) => {
            const present = v != null && v !== false;
            return (
              <Fragment key={k}>
                <code style={{ color: T.text2, fontSize: 12 }}>{k}</code>
                <span style={{
                  fontSize: 11, fontFamily: 'var(--font-mono)',
                  color: present ? T.zen : T.text3,
                }}>
                  {v == null || v === false
                    ? '(not set)'
                    : v === true
                      ? '(set)'
                      : `${v.prefix}… (${v.length} chars)`}
                </span>
              </Fragment>
            );
          })}
        </div>
        <p style={{ fontSize: 11, color: T.text3, marginTop: 10, marginBottom: 0 }}>
          Only key prefix + length is shown — actual secret material never leaves the backend.
        </p>
      </Card>

      {/* Live + Test mode cards */}
      <Card title="Mode tests">
        {renderModeBlock('Live mode', live)}
        {renderModeBlock('Test mode', test)}
      </Card>

      {/* Help */}
      <Card title="What now">
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7, color: T.text2 }}>
          <li><strong>Connect Failed in live but OK in test?</strong> Stripe requires Connect to be enabled separately for live and test modes. Visit dashboard.stripe.com/connect, switch to live mode (toggle upper-right), and complete Connect setup there.</li>
          <li><strong>Auth Failed?</strong> The key in env doesn't match a real Stripe account, or has been revoked. Generate a new live secret key in Stripe → Developers → API keys, paste into backend/.env (STRIPE_SECRET_KEY_LIVE or the legacy STRIPE_SECRET_KEY), restart the backend container.</li>
          <li><strong>Not Configured?</strong> The env var isn't set at all. Check backend/.env in your production stack and confirm STRIPE_SECRET_KEY (live) and/or STRIPE_SECRET_KEY_TEST are present.</li>
          <li><strong>Both modes fully operational but onboard still fails?</strong> The error is something else. Click "Copy JSON" above and check the Network tab response when you click Connect Stripe Account on a team — admin users see <code>stripeErrorMessage</code> in that response.</li>
        </ul>
      </Card>

      {/* v2.5.83 — Connected teams browser. Lists every team with a
          Stripe Connect link, with a Reset action per team. Pairs
          with the per-team "Admin: Reset Connect" button shipped in
          v2.5.82, which is gated to admins who are also team members.
          This listing has no team-membership requirement, just the
          admin role check (enforced by the backend endpoint). */}
      <Card title={`Connected teams${teamsList ? ` · ${teamsList.length}` : ''}`}>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: T.text2, lineHeight: 1.55 }}>
          Teams with a Stripe Connect link, newest first. <strong>Reset</strong> deletes
          the connected account on Stripe and clears the team's link — useful when
          onboarding is in a bad state (wrong email, wrong person started it, etc).
          Reset is blocked if the account has charge history.
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <button onClick={loadConnectedTeams} disabled={teamsLoading} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', borderRadius: 8,
            background: T.s2, border: `1px solid ${T.border}`,
            color: T.text2, cursor: teamsLoading ? 'wait' : 'pointer',
            fontSize: 12, fontWeight: 600,
          }}>
            {teamsLoading ? 'Loading…' : 'Refresh list'}
          </button>
        </div>

        {teamsError && (
          <div style={{
            padding: 10, borderRadius: 8, marginBottom: 12,
            background: `${T.fire}15`, border: `1px solid ${T.fire}45`,
            color: T.fire, fontSize: 12,
          }}>
            <strong>Error:</strong> {teamsError}
          </div>
        )}

        {!teamsLoading && teamsList && teamsList.length === 0 && (
          <p style={{ margin: 0, fontSize: 13, color: T.text2, fontStyle: 'italic' }}>
            No teams have connected Stripe accounts yet.
          </p>
        )}

        {teamsList && teamsList.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {teamsList.map(t => {
              // Status interpretation mirrors StripeConnectPanel logic on
              // the team page. fully = green; partial = amber; not started
              // = grey. Used for the row's accent color.
              const fully = t.stripeChargesEnabled && t.stripePayoutsEnabled && t.stripeDetailsSubmitted;
              const restricted = t.stripeDetailsSubmitted && !(t.stripeChargesEnabled && t.stripePayoutsEnabled);
              const accent = fully ? T.zen : (restricted || t.stripeDetailsSubmitted) ? '#e8a200' : T.text2;
              return (
                <div key={t.id} style={{
                  padding: 12, borderRadius: 10,
                  background: T.s2, border: `1px solid ${T.border}`,
                  borderLeft: `3px solid ${accent}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <strong style={{ color: T.text, fontSize: 14 }}>{t.name}</strong>
                        <span style={{
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                          padding: '2px 8px', borderRadius: 100,
                          background: t.stripeAccountMode === 'live' ? `${T.zen}25` : `${T.text2}25`,
                          color:      t.stripeAccountMode === 'live' ? T.zen      : T.text2,
                          border:    `1px solid ${t.stripeAccountMode === 'live' ? T.zen : T.text2}45`,
                        }}>
                          {t.stripeAccountMode || 'unknown'}
                        </span>
                        {t.stripeAccountCountry && (
                          <span style={{ fontSize: 11, color: T.text2 }}>{t.stripeAccountCountry}</span>
                        )}
                      </div>
                      <code style={{ fontSize: 11, color: T.text2, display: 'block', marginBottom: 6, wordBreak: 'break-all' }}>
                        {t.stripeAccountId}
                      </code>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        <Pill ok={t.stripeChargesEnabled}    label="Charges"  />
                        <Pill ok={t.stripePayoutsEnabled}    label="Payouts"  />
                        <Pill ok={t.stripeDetailsSubmitted}  label="Onboarded" />
                        {/* v2.5.274 — one coach account shared across teams (mig 111) */}
                        {t.sharedWith > 0 && (
                          <span title={t.stripeAccountLinkedFrom ? 'This team reuses another team\'s account' : 'Other teams reuse this team\'s account'}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 100, background: `${T.zen}20`, color: T.zen, border: `1px solid ${T.zen}45` }}>
                            🔗 shared with {t.sharedWith} other team{t.sharedWith === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>

                      {/* v2.5.85 — Stripe-side identity. Helps admins
                          identify *who* a connected account belongs to
                          when investigating a problem. email is from
                          accounts.retrieve.email; individualName is
                          first+last from accounts.retrieve.individual,
                          populated only after the coach completes
                          Express onboarding. If the Stripe retrieve
                          itself failed (account deleted out-of-band,
                          etc), we surface that instead so the admin
                          isn't confused by missing data. */}
                      {t.stripeRetrieveOk === false ? (
                        <div style={{
                          fontSize: 11, color: T.fire, marginBottom: 8,
                          padding: '4px 8px', borderRadius: 6,
                          background: `${T.fire}10`, border: `1px solid ${T.fire}30`,
                        }}>
                          <strong>Could not load Stripe details:</strong> {t.stripeRetrieveError || 'unknown'}
                          {' '}— account may have been deleted on Stripe directly. Reset will still clear the team's link.
                        </div>
                      ) : (t.stripeEmail || t.stripeIndividualName) && (
                        <div style={{
                          fontSize: 12, color: T.text2, marginBottom: 8,
                          padding: '6px 10px', borderRadius: 6,
                          background: T.bg, border: `1px solid ${T.border}`,
                        }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                                        textTransform: 'uppercase', color: T.text3, marginBottom: 4 }}>
                            Stripe account holder
                          </div>
                          {t.stripeIndividualName && (
                            <div style={{ color: T.text, marginBottom: 2 }}>{t.stripeIndividualName}</div>
                          )}
                          {t.stripeEmail && (
                            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{t.stripeEmail}</div>
                          )}
                          {!t.stripeIndividualName && (
                            <div style={{ fontSize: 11, color: T.text3, fontStyle: 'italic', marginTop: 2 }}>
                              No personal info on file — onboarding not yet completed.
                            </div>
                          )}
                        </div>
                      )}

                      {/* v2.5.85 — team coaches/managers. Helps admins
                          contact the right person when a connected
                          account is in a bad state. Empty list (no
                          coaches assigned) is rare but valid — we
                          show a hint instead of blank space. */}
                      <div style={{
                        fontSize: 12, color: T.text2,
                        padding: '6px 10px', borderRadius: 6,
                        background: T.bg, border: `1px solid ${T.border}`,
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                                      textTransform: 'uppercase', color: T.text3, marginBottom: 4 }}>
                          Team coaches & managers
                          {t.coaches?.length > 0 && (
                            <span style={{ marginLeft: 6, color: T.text2 }}>· {t.coaches.length}</span>
                          )}
                        </div>
                        {t.coaches?.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {t.coaches.map(c => (
                              <div key={c.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ color: T.text }}>{c.name}</span>
                                <span style={{
                                  fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                                  padding: '1px 6px', borderRadius: 100,
                                  background: c.memberRole === 'coach' ? `${T.ice}20` : `${T.puck}20`,
                                  color:      c.memberRole === 'coach' ? T.ice      : T.puck,
                                }}>{c.memberRole}</span>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: T.text2 }}>{c.email}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: 11, color: T.text3, fontStyle: 'italic' }}>
                            No coaches or managers assigned to this team.
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => { setResetTarget(t); setResetConfirmName(''); }}
                      disabled={resetting}
                      style={{
                        padding: '8px 14px', borderRadius: 8,
                        background: 'rgba(255,68,68,0.10)',
                        border: '1px solid rgba(255,68,68,0.40)',
                        color: T.fire, cursor: resetting ? 'wait' : 'pointer',
                        fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                      }}>
                      Reset Connect
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* v2.5.83 — Reset confirmation modal. Mirrors the Delete Team
          and v2.5.82 per-team Reset patterns: type the team name to
          enable the destructive button. Backend re-validates admin
          role + blocks if charges exist. */}
      {resetTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.60)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16, zIndex: 500,
        }}>
          <div style={{
            background: T.surface, borderRadius: 14,
            border: `1px solid ${T.border}`,
            padding: 24, maxWidth: 460, width: '100%',
            // Jul 2026 — this card has a text input plus a long warning
            // paragraph/list; on a short phone screen with the keyboard
            // open it could run off the bottom with no way to reach the
            // confirm button. Cap against --mobile-h (keyboard-aware,
            // set by useCapacitorPlugins.js) and let it scroll internally.
            maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto',
          }}>
            <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>⚠️</div>
            <h2 style={{
              fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700,
              color: T.fire, textAlign: 'center', margin: '0 0 8px',
            }}>
              Reset Stripe Connect for {resetTarget.name}?
            </h2>
            {resetTarget.sharedWith > 0 ? (
              <>
                <p style={{ fontSize: 13, color: T.text2, textAlign: 'center', margin: '0 0 12px', lineHeight: 1.55 }}>
                  This account is <strong>shared with {resetTarget.sharedWith} other team{resetTarget.sharedWith === 1 ? '' : 's'}</strong>,
                  so it will only be <strong>unlinked</strong> from this team. The Stripe account itself stays.
                </p>
                <ul style={{ fontSize: 12, color: T.text2, margin: '0 0 12px', paddingLeft: 18, lineHeight: 1.6 }}>
                  <li>Stripe account <code style={{ fontSize: 11 }}>{resetTarget.stripeAccountId}</code> is NOT deleted — the other team{resetTarget.sharedWith === 1 ? '' : 's'} keep using it.</li>
                  <li>This team's payout settings reset to "Not connected."</li>
                  <li>The coach can re-link it, or onboard a separate account, at any time.</li>
                </ul>
              </>
            ) : (
              <>
                <p style={{ fontSize: 13, color: T.text2, textAlign: 'center', margin: '0 0 12px', lineHeight: 1.55 }}>
                  This will <strong>delete the connected Stripe account</strong> and clear
                  the team's payout linkage. The team itself stays; only the Stripe
                  connection is removed.
                </p>
                <ul style={{ fontSize: 12, color: T.text2, margin: '0 0 12px', paddingLeft: 18, lineHeight: 1.6 }}>
                  <li>Stripe connected account <code style={{ fontSize: 11 }}>{resetTarget.stripeAccountId}</code> will be deleted on Stripe.</li>
                  <li>The team's payout settings reset to "Not connected."</li>
                  <li>A coach (or admin) can immediately start fresh onboarding.</li>
                  <li><strong>Blocked if the account has any charge history</strong> — refunds and disputes must be preserved.</li>
                </ul>
              </>
            )}
            <p style={{ fontSize: 12, color: T.fire, textAlign: 'center', margin: '0 0 16px', fontWeight: 600 }}>
              This cannot be undone.
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.text2, marginBottom: 6 }}>
                Type <span style={{ color: T.fire, fontFamily: 'var(--font-mono)' }}>{resetTarget.name}</span> to confirm:
              </label>
              <input
                type="text"
                autoFocus
                value={resetConfirmName}
                onChange={e => setResetConfirmName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') closeResetModal();
                  if (e.key === 'Enter' && resetTarget.name && (resetConfirmName || '').trim() === resetTarget.name.trim()) {
                    e.preventDefault();
                    performReset();
                  }
                }}
                placeholder="Team name"
                style={{
                  width: '100%', padding: '8px 12px', fontSize: 13,
                  background: T.bg, border: `1px solid ${T.border}`,
                  borderRadius: 8, color: T.text,
                }} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={closeResetModal} disabled={resetting} style={{
                flex: 1, padding: '10px 16px', borderRadius: 8,
                background: T.s2, border: `1px solid ${T.border}`,
                color: T.text2, cursor: resetting ? 'wait' : 'pointer',
                fontSize: 13, fontWeight: 600,
              }}>
                Cancel
              </button>
              <button
                onClick={performReset}
                disabled={resetting || !resetTarget.name || (resetConfirmName || '').trim() !== resetTarget.name.trim()}
                style={{
                  flex: 1, padding: '10px 16px', borderRadius: 8,
                  background: 'rgba(255,68,68,0.18)',
                  border: '1px solid rgba(255,68,68,0.40)',
                  color: T.fire,
                  cursor: resetting ? 'wait' : 'pointer',
                  fontSize: 13, fontWeight: 700,
                  opacity: (resetTarget.name && (resetConfirmName || '').trim() === resetTarget.name.trim()) ? 1 : 0.5,
                }}>
                {resetting ? 'Resetting…' : 'Reset Connect'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SmsAdminTab — v2.5.135 SMS-A
// ─────────────────────────────────────────────────────────────────
// Three sub-sections: Provider settings (RC + Twilio creds + active
// provider toggle + rate config), Subscriptions (grant/revoke per
// user), Audit log (recent messages with provider/status).
//
// A "Test send" button validates the provider integration end-to-end
// before any team has SMS enabled.
function SmsAdminTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('providers'); // 'providers' | 'subscriptions' | 'log'

  useEffect(() => {
    let cancelled = false;
    api.get('/admin/sms-settings')
      .then(({ data }) => { if (!cancelled) setSettings(data.settings); })
      .catch(() => { if (!cancelled) setSettings(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div style={{ padding: 16, color: T.text3 }}>Loading SMS settings…</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <SmsSubTabBtn active={section === 'providers'} onClick={() => setSection('providers')}>
          Providers
        </SmsSubTabBtn>
        <SmsSubTabBtn active={section === 'subscriptions'} onClick={() => setSection('subscriptions')}>
          Subscriptions
        </SmsSubTabBtn>
        <SmsSubTabBtn active={section === 'log'} onClick={() => setSection('log')}>
          Audit log
        </SmsSubTabBtn>
      </div>
      {section === 'providers' && (
        <SmsProvidersSection settings={settings} onSaved={setSettings} />
      )}
      {section === 'subscriptions' && <SmsSubscriptionsSection />}
      {section === 'log' && <SmsLogSection />}
    </div>
  );
}

function SmsSubTabBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
            style={{
              padding: '6px 14px',
              background: active ? T.ice : 'transparent',
              color: active ? '#000' : T.text3,
              border: `1px solid ${active ? T.ice : T.border}`,
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 12, fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}>
      {children}
    </button>
  );
}

function SmsProvidersSection({ settings, onSaved }) {
  // The redacted shape: ringcentral.*_set booleans + the plain
  // server_url + from_number. We always show empty inputs (never
  // pre-fill secret values). The manager pastes new credentials
  // when they want to update.
  const [draft, setDraft] = useState({
    active_provider: settings.active_provider,
    user_monthly_cents: settings.user_monthly_cents,
    user_annual_cents: settings.user_annual_cents,
    ringcentral: {
      client_id: '',
      client_secret: '',
      jwt: '',
      // v2.5.138 — SMS-C inbound webhook validation token.
      webhook_validation_token: '',
      server_url: settings.ringcentral.server_url || 'https://platform.ringcentral.com',
      from_number: settings.ringcentral.from_number || '',
    },
    twilio: {
      account_sid: '',
      auth_token: '',
      from_number: settings.twilio.from_number || '',
    },
    // 2026-06-12 — MyOwn Telco (low-cost). Secrets (user_id, key)
    // are blank inputs; plain config pre-fills from the redacted read.
    myowntelco: {
      user_id: '',
      key: '',
      did_number: settings.myowntelco?.did_number || '',
      dncl_id: settings.myowntelco?.dncl_id || '0',
      dncl_override: settings.myowntelco?.dncl_override || 'no',
    },
  });
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testBody, setTestBody] = useState('Test from Game Day SMS-A.');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  async function save() {
    setSaving(true);
    try {
      // Only send the fields the manager actually filled.
      // Empty strings mean "leave alone"; null means "clear".
      const payload = {
        active_provider: draft.active_provider,
        user_monthly_cents: Number(draft.user_monthly_cents) || 0,
        user_annual_cents: Number(draft.user_annual_cents) || 0,
        ringcentral: filledOnly(draft.ringcentral),
        twilio: filledOnly(draft.twilio),
        // 2026-06-12 — MyOwn Telco. did_number/dncl_* are plain and
        // always sent (they're not secrets); user_id/key sent only if
        // re-entered. filledOnly drops empty secret strings ("leave
        // alone"), but we want did_number/dncl_* to persist even when
        // unchanged, so include them explicitly.
        myowntelco: {
          ...filledOnly(draft.myowntelco),
          did_number: draft.myowntelco.did_number || '',
          dncl_id: draft.myowntelco.dncl_id || '0',
          dncl_override: draft.myowntelco.dncl_override || 'no',
        },
      };
      const { data } = await api.patch('/admin/sms-settings', payload);
      onSaved(data.settings);
      // Clear the secret inputs so the user can't accidentally
      // re-save the same plaintext on a subsequent click.
      setDraft(d => ({
        ...d,
        ringcentral: { ...d.ringcentral, client_id: '', client_secret: '', jwt: '', webhook_validation_token: '' },
        twilio: { ...d.twilio, account_sid: '', auth_token: '' },
        myowntelco: { ...d.myowntelco, user_id: '', key: '' },
      }));
      toast.success('SMS settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function runTest() {
    if (!testTo) { toast.error('Pick a user'); return; }
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post('/admin/sms-test', { userId: testTo, body: testBody });
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: err?.response?.data?.error || 'Test failed' });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>

        {/* Active provider */}
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: T.text }}>Active provider</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['ringcentral', 'twilio', 'myowntelco', 'off'].map(opt => (
              <button key={opt} type="button"
                onClick={() => setDraft(d => ({ ...d, active_provider: opt }))}
                style={{
                  padding: '6px 14px',
                  background: draft.active_provider === opt ? T.ice : 'transparent',
                  color: draft.active_provider === opt ? '#000' : T.text3,
                  border: `1px solid ${draft.active_provider === opt ? T.ice : T.border}`,
                  borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                {opt}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: T.text3, marginTop: 8 }}>
            Failover: if the active provider has a transient error, we automatically retry once with the other configured provider.
            "off" disables all SMS globally.
          </div>
        </div>

        {/* Rate config */}
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: T.text }}>Per-user Messaging plan rate</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <SmsRateInput label="Monthly (cents)"
              value={draft.user_monthly_cents}
              onChange={(v) => setDraft(d => ({ ...d, user_monthly_cents: v }))} />
            <SmsRateInput label="Annual (cents)"
              value={draft.user_annual_cents}
              onChange={(v) => setDraft(d => ({ ...d, user_annual_cents: v }))} />
          </div>
          <div style={{ fontSize: 11, color: T.text3, marginTop: 6 }}>
            Default $2.00 monthly / $20.00 annual. SMS-B (Stripe billing) reads these. Doesn't affect SMS-A admin-granted comps.
          </div>
        </div>

        {/* RingCentral */}
        <ProviderCard
          title="RingCentral"
          configured={settings.ringcentral.client_id_set && settings.ringcentral.client_secret_set && settings.ringcentral.jwt_set}>
          <SecretInput label="Client ID"     stored={settings.ringcentral.client_id_set}     value={draft.ringcentral.client_id}     onChange={(v) => setDraft(d => ({ ...d, ringcentral: { ...d.ringcentral, client_id: v } }))} />
          <SecretInput label="Client Secret" stored={settings.ringcentral.client_secret_set} value={draft.ringcentral.client_secret} onChange={(v) => setDraft(d => ({ ...d, ringcentral: { ...d.ringcentral, client_secret: v } }))} />
          <SecretInput label="JWT"           stored={settings.ringcentral.jwt_set}           value={draft.ringcentral.jwt}           onChange={(v) => setDraft(d => ({ ...d, ringcentral: { ...d.ringcentral, jwt: v } }))} />
          <PlainInput label="Server URL"   value={draft.ringcentral.server_url}              onChange={(v) => setDraft(d => ({ ...d, ringcentral: { ...d.ringcentral, server_url: v } }))} />
          <PlainInput label="From number (E.164)" placeholder="+15195551234" value={draft.ringcentral.from_number} onChange={(v) => setDraft(d => ({ ...d, ringcentral: { ...d.ringcentral, from_number: v } }))} />
          {/* v2.5.138 — SMS-C: webhook validation token. Required for
              the inbound webhook endpoint to verify RC's POSTs. Admin
              generates a secret string (any sufficiently-random value),
              uses it when creating the webhook subscription in RC's
              dashboard, and pastes the SAME value here. */}
          <SecretInput label="Webhook validation token (SMS-C inbound)"
                       stored={settings.ringcentral.webhook_validation_token_set}
                       value={draft.ringcentral.webhook_validation_token}
                       onChange={(v) => setDraft(d => ({ ...d, ringcentral: { ...d.ringcentral, webhook_validation_token: v } }))} />
        </ProviderCard>

        {/* Twilio */}
        <ProviderCard
          title="Twilio (fallback)"
          configured={settings.twilio.account_sid_set && settings.twilio.auth_token_set}>
          <SecretInput label="Account SID"  stored={settings.twilio.account_sid_set} value={draft.twilio.account_sid}  onChange={(v) => setDraft(d => ({ ...d, twilio: { ...d.twilio, account_sid: v } }))} />
          <SecretInput label="Auth Token"   stored={settings.twilio.auth_token_set}  value={draft.twilio.auth_token}   onChange={(v) => setDraft(d => ({ ...d, twilio: { ...d.twilio, auth_token: v } }))} />
          <PlainInput label="From number (E.164)" placeholder="+15195551234" value={draft.twilio.from_number} onChange={(v) => setDraft(d => ({ ...d, twilio: { ...d.twilio, from_number: v } }))} />
        </ProviderCard>

        {/* MyOwn Telco — 2026-06-12. Low-cost provider. SMS only (their
            v3 REST API has no fax action). userId + key are secrets;
            DID is the sending number. dncl_override 'no' respects their
            STOP list. */}
        <ProviderCard
          title="MyOwn Telco (low-cost)"
          configured={settings.myowntelco?.user_id_set && settings.myowntelco?.key_set && !!settings.myowntelco?.did_number}>
          <SecretInput label="Account ID (userId)" stored={settings.myowntelco?.user_id_set} value={draft.myowntelco.user_id} onChange={(v) => setDraft(d => ({ ...d, myowntelco: { ...d.myowntelco, user_id: v } }))} />
          <SecretInput label="API Key"             stored={settings.myowntelco?.key_set}     value={draft.myowntelco.key}     onChange={(v) => setDraft(d => ({ ...d, myowntelco: { ...d.myowntelco, key: v } }))} />
          <PlainInput label="Sending DID number" placeholder="4506704567" value={draft.myowntelco.did_number} onChange={(v) => setDraft(d => ({ ...d, myowntelco: { ...d.myowntelco, did_number: v } }))} />
          <PlainInput label="DNC list id (0 = all)" placeholder="0" value={draft.myowntelco.dncl_id} onChange={(v) => setDraft(d => ({ ...d, myowntelco: { ...d.myowntelco, dncl_id: v } }))} />
          <label style={{ display: 'block', fontSize: 11, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 8 }}>
            Override do-not-contact list
            <select value={draft.myowntelco.dncl_override}
              onChange={(e) => setDraft(d => ({ ...d, myowntelco: { ...d.myowntelco, dncl_override: e.target.value } }))}
              style={{ ...textInputStyle, marginTop: 4 }}>
              <option value="no">No — respect STOP / DNC list (recommended)</option>
              <option value="yes">Yes — override (use with caution)</option>
            </select>
          </label>
          <div style={{ fontSize: 11, color: T.text3, marginTop: 8 }}>
            SMS only — MyOwn Telco's API has no fax endpoint. Inbound replies aren't pushed by
            their API (poll-only); reply handling would be a separate add-on.
          </div>
        </ProviderCard>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={save} disabled={saving}
                  style={{
                    padding: '8px 18px',
                    background: T.ice, color: '#000',
                    border: 'none', borderRadius: 8, fontWeight: 700,
                    cursor: saving ? 'wait' : 'pointer',
                  }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {/* Test send */}
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: T.text }}>Test send</div>
          <div style={{ fontSize: 11, color: T.text3, marginBottom: 10 }}>
            Sends a one-off message via the active provider (with failover). Bypasses team + subscription gates so you can test before provisioning. The target user must have a verified primary number with consent_at set.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
            <PlainInput label="Target user ID" value={testTo} onChange={setTestTo} placeholder="uuid" />
            <PlainInput label="Body" value={testBody} onChange={setTestBody} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <button type="button" onClick={runTest} disabled={testing}
                    style={{
                      padding: '6px 14px',
                      background: T.s2, color: T.text,
                      border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12,
                      cursor: testing ? 'wait' : 'pointer', fontWeight: 600,
                    }}>
              {testing ? 'Sending…' : 'Send test'}
            </button>
          </div>
          {testResult && (
            <div style={{
              marginTop: 10, padding: 10,
              background: testResult.ok ? `${T.goal}18` : `${T.fire}18`,
              border: `1px solid ${testResult.ok ? T.goal : T.fire}50`,
              borderRadius: 8, fontSize: 12,
            }}>
              <strong style={{ color: testResult.ok ? T.goal : T.fire }}>
                {testResult.ok ? '✓ Sent' : '✗ Failed'}
              </strong>{' '}
              {testResult.error && <span>— {testResult.error}</span>}
              {testResult.provider && <span> · provider={testResult.provider}</span>}
              {testResult.smsMessageId && <span> · msg={testResult.smsMessageId.slice(0, 8)}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SmsRateInput({ label, value, onChange }) {
  return (
    <label style={{ display: 'block', fontSize: 11, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
      <input type="number" min="0" value={value}
             onChange={(e) => onChange(e.target.value)}
             style={{ ...textInputStyle, marginTop: 4 }} />
    </label>
  );
}

function ProviderCard({ title, configured, children }) {
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{title}</div>
        <span style={{
          fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 700,
          background: configured ? `${T.goal}28` : `${T.text3}28`,
          color: configured ? T.goal : T.text3,
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          {configured ? 'Configured' : 'Not configured'}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  );
}

function SecretInput({ label, stored, value, onChange }) {
  return (
    <label style={{ display: 'block', fontSize: 11, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {label}
        {stored && (
          <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3,
                         background: `${T.goal}28`, color: T.goal,
                         textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
            set
          </span>
        )}
      </span>
      <input type="password" autoComplete="off"
             value={value}
             placeholder={stored ? '••••••• (leave blank to keep)' : 'Enter value'}
             onChange={(e) => onChange(e.target.value)}
             style={{ ...textInputStyle, marginTop: 4, fontFamily: 'var(--font-mono)' }} />
    </label>
  );
}

function PlainInput({ label, placeholder, value, onChange }) {
  return (
    <label style={{ display: 'block', fontSize: 11, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
      <input type="text" value={value}
             placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)}
             style={{ ...textInputStyle, marginTop: 4 }} />
    </label>
  );
}

function filledOnly(o) {
  const out = {};
  for (const [k, v] of Object.entries(o)) {
    if (v && String(v).trim() !== '') out[k] = v;
  }
  return out;
}

function SmsSubscriptionsSection() {
  const [subs, setSubs] = useState(null);
  const [grantUserId, setGrantUserId] = useState('');
  // v2.5.137 — filter state. Persists in component state; not URL
  // since the admin tab is a single-page experience.
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'past_due' | 'trialing' | 'canceled' | 'granted'
  const [stripeMode, setStripeMode] = useState(null); // 'test' | 'live'
  const [syncingId, setSyncingId] = useState(null);

  const refresh = async () => {
    try {
      // Translate filter to query params.
      const params = new URLSearchParams();
      if (filter === 'active')   params.set('active', 'true');
      if (filter === 'past_due') { params.set('status', 'past_due'); }
      if (filter === 'trialing') { params.set('status', 'trialing'); }
      if (filter === 'canceled') { params.set('status', 'canceled'); }
      if (filter === 'payment_failed') { params.set('payment_failed', 'true'); }
      if (filter === 'granted')  params.set('plan', 'granted');
      const qs = params.toString();
      const { data } = await api.get(`/admin/sms-subscriptions${qs ? `?${qs}` : ''}`);
      setSubs(data.subscriptions || []);
    } catch {
      setSubs([]);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  // Pull current Stripe mode once so we can build correctly-flavored
  // dashboard deep-links (test mode customer URLs differ from live).
  // The /admin/system-settings endpoint returns the settings object
  // directly at the top level (not nested under `settings`).
  useEffect(() => {
    api.get('/admin/system-settings')
      .then(({ data }) => setStripeMode(data?.stripe_mode === 'live' ? 'live' : 'test'))
      .catch(() => setStripeMode('test'));
  }, []);

  async function grant() {
    if (!grantUserId.trim()) return;
    try {
      await api.post(`/admin/sms-subscriptions/${grantUserId.trim()}`);
      toast.success('Granted');
      setGrantUserId('');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Grant failed');
    }
  }
  async function revoke(userId) {
    if (!window.confirm('Revoke SMS subscription for this user?')) return;
    try {
      await api.delete(`/admin/sms-subscriptions/${userId}`);
      toast.success('Revoked');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Revoke failed');
    }
  }
  async function sync(userId) {
    setSyncingId(userId);
    try {
      const { data } = await api.post(`/admin/sms-subscriptions/${userId}/sync`);
      toast.success(`Synced from Stripe (status=${data.stripe_status})`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Sync failed');
    } finally {
      setSyncingId(null);
    }
  }

  return (
    <div style={{ maxWidth: 880 }}>
      {/* ── Grant (admin-comp) card ── */}
      <div style={cardStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: T.text }}>Grant SMS to user (admin comp)</div>
        <div style={{ fontSize: 11, color: T.text3, marginBottom: 10 }}>
          For testing or staff. Real customer subscriptions go through Stripe Checkout from the user's profile.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="text" placeholder="user UUID" value={grantUserId}
                 onChange={(e) => setGrantUserId(e.target.value)}
                 style={{ ...textInputStyle, flex: 1 }} />
          <button type="button" onClick={grant}
                  style={{
                    padding: '6px 14px', background: T.ice, color: '#000',
                    border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
                  }}>
            Grant
          </button>
        </div>
      </div>

      {/* ── Filter row ── */}
      <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
        {[
          { id: 'all',            label: 'All' },
          { id: 'active',         label: 'Active' },
          { id: 'trialing',       label: 'On trial' },
          { id: 'past_due',       label: 'Past due' },
          { id: 'payment_failed', label: 'Payment failed' },
          { id: 'canceled',       label: 'Canceled' },
          { id: 'granted',        label: 'Comped' },
        ].map(f => (
          <button key={f.id} type="button" onClick={() => setFilter(f.id)}
                  style={{
                    padding: '4px 10px', fontSize: 11, fontWeight: 600,
                    background: filter === f.id ? T.ice : 'transparent',
                    color: filter === f.id ? '#000' : T.text3,
                    border: `1px solid ${filter === f.id ? T.ice : T.border}`,
                    borderRadius: 6, cursor: 'pointer',
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* ── List ── */}
      <div style={{ marginTop: 14 }}>
        {!subs ? <div style={{ color: T.text3 }}>Loading…</div> :
          subs.length === 0 ? (
            <div style={{ color: T.text3, padding: 14 }}>
              {filter === 'all' ? 'No subscriptions yet.' : 'No subscriptions match this filter.'}
            </div>
          ) :
          subs.map(s => (
            <SubscriptionRow key={s.user_id} sub={s} stripeMode={stripeMode}
                             onRevoke={revoke}
                             onSync={sync}
                             syncing={syncingId === s.user_id} />
          ))
        }
      </div>
    </div>
  );
}

// v2.5.137 — Single-row card. Pulled out so the parent stays
// readable and conditional rendering is local.
function SubscriptionRow({ sub, stripeMode, onRevoke, onSync, syncing }) {
  const isStripeManaged = !!sub.stripe_subscription_id;
  const stripeStatus = sub.status; // 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete' | null
  const paymentFailed = !!sub.last_payment_failed_at &&
    (!sub.last_payment_succeeded_at || new Date(sub.last_payment_failed_at) > new Date(sub.last_payment_succeeded_at));

  // Build the Stripe customer dashboard URL. Test mode uses
  // dashboard.stripe.com/test/customers/...; live mode drops the
  // /test segment.
  const stripeCustomerUrl = sub.stripe_customer_id
    ? `https://dashboard.stripe.com/${stripeMode === 'live' ? '' : 'test/'}customers/${sub.stripe_customer_id}`
    : null;

  // Top-line status badge
  const badge = (() => {
    if (sub.plan === 'granted') return { label: 'COMPED', color: T.ice };
    if (paymentFailed)          return { label: 'PAYMENT FAILED', color: T.fire };
    if (sub.cancel_at_period_end) return { label: 'CANCELING', color: T.penalty };
    if (stripeStatus === 'trialing') return { label: 'TRIAL', color: T.ice };
    if (stripeStatus === 'active')   return { label: 'ACTIVE', color: T.goal };
    if (stripeStatus === 'past_due') return { label: 'PAST DUE', color: T.fire };
    if (stripeStatus === 'canceled') return { label: 'CANCELED', color: T.text3 };
    if (stripeStatus === 'incomplete') return { label: 'INCOMPLETE', color: T.penalty };
    if (sub.is_active) return { label: 'ACTIVE', color: T.goal };
    return { label: 'INACTIVE', color: T.text3 };
  })();

  return (
    <div style={{ ...cardStyle, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name + email line */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ color: T.text }}>{sub.user_name || '(unknown user)'}</strong>
            <span style={{
              fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
              background: `${badge.color}28`, color: badge.color,
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>{badge.label}</span>
            {sub.plan && sub.plan !== 'granted' && (
              <span style={{ fontSize: 10, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                {sub.plan}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: T.text3, fontFamily: 'var(--font-mono)', marginTop: 2 }}>
            {sub.user_email || sub.user_id.slice(0, 8)}
          </div>

          {/* Period / trial / failure details */}
          <div style={{ fontSize: 11, color: T.text3, marginTop: 6, lineHeight: 1.5 }}>
            {stripeStatus === 'trialing' && sub.trial_end && (
              <div>Trial ends {new Date(sub.trial_end).toLocaleDateString()}</div>
            )}
            {stripeStatus === 'active' && sub.current_period_end && !sub.cancel_at_period_end && (
              <div>Renews {new Date(sub.current_period_end).toLocaleDateString()}</div>
            )}
            {sub.cancel_at_period_end && sub.current_period_end && (
              <div>Cancels {new Date(sub.current_period_end).toLocaleDateString()}</div>
            )}
            {paymentFailed && (
              <div style={{ color: T.fire }}>
                Last payment failed {new Date(sub.last_payment_failed_at).toLocaleDateString()}
              </div>
            )}
            {sub.plan === 'granted' && sub.granted_at && (
              <div>Comped {new Date(sub.granted_at).toLocaleDateString()}</div>
            )}
            {sub.cancelled_at && (
              <div>Canceled {new Date(sub.cancelled_at).toLocaleDateString()}
                {sub.cancellation_reason ? ` — ${sub.cancellation_reason}` : ''}
              </div>
            )}
          </div>
        </div>

        {/* Action column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
          {/* Stripe deep link — only when we have a stripe customer */}
          {stripeCustomerUrl && (
            <a href={stripeCustomerUrl} target="_blank" rel="noopener noreferrer"
               style={{
                 padding: '4px 10px', fontSize: 11, fontWeight: 600,
                 background: 'transparent', color: T.ice,
                 border: `1px solid ${T.ice}40`, borderRadius: 6,
                 textDecoration: 'none', whiteSpace: 'nowrap',
                 display: 'inline-flex', alignItems: 'center', gap: 4,
               }}
               title={`Open in Stripe dashboard (${stripeMode} mode)`}>
              Stripe ↗
            </a>
          )}
          {/* Sync — only for Stripe-managed rows */}
          {isStripeManaged && (
            <button type="button" onClick={() => onSync(sub.user_id)} disabled={syncing}
                    style={{
                      padding: '4px 10px', fontSize: 11, fontWeight: 600,
                      background: 'transparent', color: T.text2,
                      border: `1px solid ${T.border}`, borderRadius: 6, cursor: syncing ? 'wait' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    title="Re-fetch from Stripe and update local state">
              {syncing ? '…' : 'Sync'}
            </button>
          )}
          {/* Revoke — only when actually active. For Stripe-managed
              subs, this just sets is_active=false locally (the next
              webhook will overwrite). The right way to cancel a
              Stripe sub is for the USER to open the Customer Portal.
              Admin revoke is for SMS-A comped grants. */}
          {sub.is_active && sub.plan === 'granted' && (
            <button type="button" onClick={() => onRevoke(sub.user_id)}
                    style={{
                      padding: '4px 10px', fontSize: 11,
                      background: 'transparent', color: T.fire,
                      border: `1px solid ${T.fire}40`, borderRadius: 6, cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}>
              Revoke
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SmsLogSection() {
  // v2.5.138 — Two-pane log: Outbound (sms_messages) + Inbound
  // (sms_inbound_messages). Default to Outbound since that's the
  // bulk of the audit traffic; Inbound is the SMS-C compliance view.
  const [pane, setPane] = useState('outbound'); // 'outbound' | 'inbound'

  return (
    <div style={{ maxWidth: 920 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[
          { id: 'outbound', label: 'Outbound' },
          { id: 'inbound',  label: 'Inbound (SMS-C)' },
        ].map(p => (
          <button key={p.id} type="button" onClick={() => setPane(p.id)}
                  style={{
                    padding: '4px 10px', fontSize: 11, fontWeight: 600,
                    background: pane === p.id ? T.ice : 'transparent',
                    color: pane === p.id ? '#000' : T.text3,
                    border: `1px solid ${pane === p.id ? T.ice : T.border}`,
                    borderRadius: 6, cursor: 'pointer',
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
            {p.label}
          </button>
        ))}
      </div>
      {pane === 'outbound' && <OutboundLog />}
      {pane === 'inbound'  && <InboundLog />}
    </div>
  );
}

function OutboundLog() {
  const [messages, setMessages] = useState(null);
  useEffect(() => {
    api.get('/admin/sms-messages?limit=50')
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => setMessages([]));
  }, []);
  if (!messages) return <div style={{ color: T.text3 }}>Loading…</div>;
  if (messages.length === 0) return <div style={{ color: T.text3 }}>No SMS sent yet.</div>;
  return (
    <div>
      {messages.map(m => (
        <div key={m.id} style={{ ...cardStyle, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
              background: m.status === 'sent' || m.status === 'delivered' ? `${T.goal}28` : `${T.fire}28`,
              color: m.status === 'sent' || m.status === 'delivered' ? T.goal : T.fire,
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>{m.status}</span>
            <span style={{ fontSize: 11, color: T.text3 }}>{m.provider}</span>
            <span style={{ fontSize: 11, color: T.text3, fontFamily: 'var(--font-mono)' }}>
              {new Date(m.created_at).toLocaleString()}
            </span>
          </div>
          <div style={{ fontSize: 13, marginTop: 6, color: T.text }}>{m.body_truncated}</div>
          {m.error_message && (
            <div style={{ fontSize: 11, marginTop: 4, color: T.fire }}>
              {m.error_code && <code style={{ marginRight: 6 }}>{m.error_code}</code>}
              {m.error_message}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function InboundLog() {
  // v2.5.138 — SMS-C inbound audit log. Same shape as OutboundLog but
  // reads /admin/sms-inbound. Filter chip row lets the admin focus
  // on STOPs (compliance check) or RSVPs.
  const [messages, setMessages] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'stop' | 'help' | 'rsvp_in' | 'rsvp_out' | 'unknown'

  const refresh = async () => {
    try {
      const qs = filter === 'all' ? '' : `?classified_as=${filter}`;
      const { data } = await api.get(`/admin/sms-inbound${qs}`);
      setMessages(data.inbound || []);
    } catch {
      setMessages([]);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {[
          { id: 'all',      label: 'All' },
          { id: 'stop',     label: 'Opt-outs' },
          { id: 'start',    label: 'Opt-ins' },
          { id: 'help',     label: 'Help' },
          { id: 'rsvp_in',  label: 'RSVP IN' },
          { id: 'rsvp_out', label: 'RSVP OUT' },
          { id: 'unknown',  label: 'Unknown' },
        ].map(f => (
          <button key={f.id} type="button" onClick={() => setFilter(f.id)}
                  style={{
                    padding: '4px 10px', fontSize: 11, fontWeight: 600,
                    background: filter === f.id ? T.ice : 'transparent',
                    color: filter === f.id ? '#000' : T.text3,
                    border: `1px solid ${filter === f.id ? T.ice : T.border}`,
                    borderRadius: 6, cursor: 'pointer',
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
            {f.label}
          </button>
        ))}
      </div>
      {!messages ? <div style={{ color: T.text3 }}>Loading…</div> :
        messages.length === 0 ? <div style={{ color: T.text3 }}>
          {filter === 'all' ? 'No inbound messages yet.' : 'No matching inbound messages.'}
        </div> :
        messages.map(m => {
          const isStop = m.classified_as === 'stop';
          const badgeColor = isStop ? T.fire
            : m.classified_as === 'rsvp_in' ? T.goal
            : m.classified_as === 'rsvp_out' ? T.penalty
            : m.classified_as === 'help' ? T.ice
            : T.text3;
          return (
            <div key={m.id} style={{ ...cardStyle, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
                  background: `${badgeColor}28`, color: badgeColor,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>{m.classified_as}</span>
                <span style={{ fontSize: 11, color: T.text3 }}>{m.provider}</span>
                <span style={{ fontSize: 11, color: T.text3, fontFamily: 'var(--font-mono)' }}>
                  {new Date(m.created_at).toLocaleString()}
                </span>
                {m.action_taken && (
                  <span style={{ fontSize: 11, color: T.text2 }}>{m.action_taken}</span>
                )}
              </div>
              <div style={{ fontSize: 13, marginTop: 6, color: T.text, fontFamily: 'var(--font-mono)' }}>
                {m.body}
              </div>
              {m.user_id && (
                <div style={{ fontSize: 11, marginTop: 4, color: T.text3, fontFamily: 'var(--font-mono)' }}>
                  user {m.user_id.slice(0, 8)}
                </div>
              )}
            </div>
          );
        })
      }
    </div>
  );
}

const cardStyle = {
  padding: 14,
  background: 'var(--surface-2, #11131c)',
  border: `1px solid var(--border, #2a2f3e)`,
  borderRadius: 10,
};
const textInputStyle = {
  width: '100%',
  padding: '7px 10px',
  background: 'var(--surface, #0c0e16)',
  color: 'var(--text, #e9ecf3)',
  border: `1px solid var(--border, #2a2f3e)`,
  borderRadius: 6,
  fontSize: 13,
  outline: 'none',
};


export default function AdminPage() {
  const { user } = useAuth();
  // 2026-09-28 — ?tab=<id> deep link (the demo-league banner links to
  // /admin?tab=demo). Unknown values fall back to System Health.
  const [activeTab,  setActiveTab]  = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return ['health', 'users', 'teams', 'ai', 'ai-usage', 'bans', 'audit', 'emails', 'events',
              'stripe', 'sms', 'tester', 'demo', 'recordings', 'sys'].includes(t) ? t : 'health';
    } catch { return 'health'; }
  });
  const [stats,      setStats]      = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  // Apr 2026 — track unresolved system event counts at the page level
  // so the tab badge can show a live count even when the events tab
  // isn't open. The SystemEventsTab calls onCountChange to update it
  // both from initial load and after resolve/reopen actions. We also
  // poll once on mount so the badge is accurate before the user
  // clicks the tab.
  const [eventCounts, setEventCounts] = useState({ info: 0, warning: 0, error: 0, critical: 0 });

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data } = await api.get('/admin/stats');
      setStats(data);
      setLastRefresh(new Date());
    } catch (err) {
      toast.error('Failed to load system stats');
    }
    setStatsLoading(false);
  }, []);

  // Apr 2026 — fetch event counts once on mount so the badge is
  // populated before the user opens the Events tab. Lightweight: just
  // pulls page 1 with limit 1 to get the unresolvedCounts payload.
  const loadEventCounts = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/system-events?limit=1&resolved=false');
      setEventCounts(data.unresolvedCounts || { info: 0, warning: 0, error: 0, critical: 0 });
    } catch {
      // Silent — the Events tab will retry when opened.
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadEventCounts(); }, [loadEventCounts]);

  // Total unresolved count for the tab badge. We surface ALL severities
  // so the badge reflects "things to look at"; the panel itself does
  // severity filtering.
  const totalUnresolved = (eventCounts.critical || 0) + (eventCounts.error || 0)
                        + (eventCounts.warning || 0) + (eventCounts.info || 0);
  // For visual urgency, the tab itself goes red when there are any
  // critical or error events (handled inline in tabs[] below).
  const hasUrgent = (eventCounts.critical || 0) + (eventCounts.error || 0) > 0;

  const tabs = [
    { id: 'health',   label: 'System Health', icon: Activity },
    { id: 'users',    label: 'Users',          icon: Users,      count: stats?.users.total },
    { id: 'teams',    label: 'Teams',          icon: Shield,     count: stats?.teams.total },
    { id: 'ai',       label: 'AI Settings',    icon: Zap },
    { id: 'ai-usage', label: 'AI Usage',       icon: DollarSign },
    { id: 'bans',     label: 'Banned',         icon: Ban },
    { id: 'audit',    label: 'Logs',           icon: Terminal },
    // May 4 2026 (v2.5.64) — admin (super-admin) can monitor email
    // volume per customer/team/trigger to compare against their
    // SendGrid plan quota (50k/month). Live counts updated in
    // real-time as the EmailStatsTab fetches.
    { id: 'emails',   label: 'Emails',         icon: Mail },
    // Apr 2026 — system events tab. count shows total unresolved so
    // admins notice silent failures at a glance from any tab.
    { id: 'events',   label: 'Events',         icon: AlertTriangle, count: totalUnresolved || undefined, urgent: hasUrgent },
    // v2.5.80 — Stripe Connect diagnostic tab. Replaces the
    // "paste fetch into devtools" workflow for debugging
    // live-mode payouts. Self-runs on tab-open.
    { id: 'stripe',   label: 'Stripe',         icon: CreditCard },
    // v2.5.135 — SMS-A admin tab. Provider config + audit log +
    // subscription grants. Manager-tested before any team is
    // provisioned.
    { id: 'sms',      label: 'SMS',            icon: MessageSquare },
    // v2.5.144 — Email + SMS template tester. Admin-only preview-
    // send tool so we can see what any template looks like without
    // waiting for the cron / event to fire.
    { id: 'tester',   label: 'Tester',         icon: Send },
    // 2026-09-28 — League demo mode: generate / remove a fictional
    // league (seasons, registrations, games, playoffs) for demos.
    { id: 'demo',     label: 'Demo',           icon: FlaskConical },
    // 2026-10-08 — every voice recording, downloadable (audio / debug bundle / bulk zip).
    { id: 'recordings', label: 'Recordings',   icon: Mic },
    // 2026-10-18 — the stats AI (Gemma on the 5090 via meadowpark2's broker)
    // and the speech-to-text GPU: kept in the database, super admin only.
    { id: 'stats-ai', label: 'Stats AI',       icon: Cpu },
    // 2026-10-22 (per Gilles) — the Press Box console: clients, teams,
    // games, bulk re-runs, activity trail, and the WhatsApp integration.
    { id: 'press-box', label: 'Press Box',     icon: Radio },
    { id: 'sys',      label: 'System',         icon: Settings },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: `${T.fire}18`, border: `1px solid ${T.fire}35`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Shield size={18} color={T.fire} />
            </div>
            <h1 style={{
              fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 800,
              letterSpacing: '0.06em', textTransform: 'uppercase', color: T.text,
              margin: 0,
            }}>Admin Dashboard</h1>
            <Badge color={T.fire}>Superadmin</Badge>
          </div>
          <p style={{ fontSize: 13, color: T.text2, margin: 0 }}>
            Logged in as <strong style={{ color: T.text }}>{user?.name || user?.email}</strong>
            {lastRefresh && (
              <> · Last refreshed {formatDistanceToNow(lastRefresh, { addSuffix: true })}</>
            )}
          </p>
        </div>
        <button onClick={loadStats} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 10,
          background: T.s2, border: `1px solid ${T.border}`,
          color: T.text2, cursor: 'pointer',
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          <RefreshCw size={13} style={{ animation: statsLoading ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap',
        padding: '6px', background: T.surface, borderRadius: 14,
        border: `1px solid ${T.border}`, width: 'fit-content',
      }}>
        {tabs.map(t => (
          <TabButton
            key={t.id}
            active={activeTab === t.id}
            onClick={() => setActiveTab(t.id)}
            icon={t.icon}
            label={t.label}
            count={t.count}
            urgent={t.urgent}
          />
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'health' && (
        <HealthTab stats={stats} loading={statsLoading} onRefresh={loadStats} />
      )}
      {activeTab === 'users' && (
        <UsersTab currentUserId={user?.id} />
      )}
      {activeTab === 'teams' && (
        <TeamsTab />
      )}
      {activeTab === 'ai' && (
        <AiSettingsTab />
      )}
      {activeTab === 'ai-usage' && (
        <AiUsageTab />
      )}
      {activeTab === 'bans' && (
        <BannedEmailsTab />
      )}
      {activeTab === 'audit' && (
        <LogsTab />
      )}
      {activeTab === 'emails' && (
        <EmailStatsTab />
      )}
      {activeTab === 'events' && (
        <SystemEventsTab onCountChange={setEventCounts} />
      )}
      {activeTab === 'stripe' && (
        <>
          <FinanceReportCard />
          <StripeDiagnosticTab />
        </>
      )}
      {activeTab === 'sms' && (
        <SmsAdminTab />
      )}
      {activeTab === 'tester' && (
        <TemplateTesterTab />
      )}
      {activeTab === 'demo' && (
        <LeagueDemoPanel />
      )}
      {activeTab === 'recordings' && (
        <RecordingsTab T={T} />
      )}
      {activeTab === 'stats-ai' && (
        <StatsAiTab T={T} />
      )}
      {activeTab === 'press-box' && (
        <PressBoxAdminTab T={T} />
      )}
      {activeTab === 'sys' && (
        <SystemSettingsTab />
      )}

    </div>
  );
}

// v2.5.144 — Template tester tab. Admin picks an email template
// (or SMS body), optionally picks a variant, optionally overrides
// the recipient (default = admin's own email/phone), and sends
// a real preview. Useful for design tweaks + customer support
// dry-runs without burning real notifications.
function TemplateTesterTab() {
  const [catalog, setCatalog] = useState(null);
  const [adminEmail, setAdminEmail] = useState('');

  // Email tester state
  const [emailTemplate, setEmailTemplate] = useState('');
  const [emailVariant, setEmailVariant] = useState('');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailPreviewing, setEmailPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  // SMS tester state
  const [smsBodyId, setSmsBodyId] = useState('');
  const [smsVariant, setSmsVariant] = useState('');
  const [smsRecipient, setSmsRecipient] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsBodyPreview, setSmsBodyPreview] = useState('');

  // Mode: which tester is open
  const [mode, setMode] = useState('email'); // 'email' | 'sms' | 'sparelink'

  // ── Live spare-link test state (v2.5.162) ──
  const [spareGames, setSpareGames] = useState(null);   // null=loading, []=none
  const [spareGameId, setSpareGameId] = useState('');
  const [spareScenario, setSpareScenario] = useState('paid'); // 'paid'|'free'|'decline'
  const [spareSending, setSpareSending] = useState(false);
  const [spareResult, setSpareResult] = useState(null);

  useEffect(() => {
    api.get('/admin/email-test/templates')
      .then(({ data }) => {
        setCatalog(data);
        if (data?.adminEmail) {
          setAdminEmail(data.adminEmail);
          // Pre-fill recipient field too so the admin sees what
          // address it'll go to (and can override if they want).
          // Only set if the field is still empty — don't clobber
          // anything the admin typed before the fetch resolved.
          setEmailRecipient(prev => prev || data.adminEmail);
        }
        if (data?.templates?.length) {
          setEmailTemplate(prev => prev || data.templates[0].name);
        }
        if (data?.smsBodies?.length) {
          setSmsBodyId(prev => prev || data.smsBodies[0].id);
        }
      })
      .catch(() => setCatalog({ templates: [], smsBodies: [] }));
  }, []);

  // Selected-template metadata (variant options, description).
  const emailTemplateDef = catalog?.templates?.find(t => t.name === emailTemplate);
  const smsBodyDef = catalog?.smsBodies?.find(b => b.id === smsBodyId);

  // Reset variant when template changes — make sure it's valid for the new template.
  useEffect(() => {
    if (emailTemplateDef?.variants?.length) {
      setEmailVariant(emailTemplateDef.variants[0].id);
    } else {
      setEmailVariant('');
    }
  }, [emailTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (smsBodyDef?.variants?.length) {
      setSmsVariant(smsBodyDef.variants[0].id);
    } else {
      setSmsVariant('');
    }
  }, [smsBodyId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function previewEmail() {
    setEmailPreviewing(true);
    setPreview(null);
    try {
      const { data } = await api.post('/admin/email-test/preview', {
        templateName: emailTemplate,
        variant: emailVariant || null,
      });
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Preview failed');
    } finally {
      setEmailPreviewing(false);
    }
  }

  async function sendEmail() {
    setEmailSending(true);
    try {
      const { data } = await api.post('/admin/email-test/send', {
        templateName: emailTemplate,
        variant: emailVariant || null,
        recipientOverride: emailRecipient && emailRecipient !== adminEmail ? emailRecipient : null,
      });
      toast.success(`Sent ${data.template}${data.variant ? ` (${data.variant})` : ''} to ${data.sentTo}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Send failed');
    } finally {
      setEmailSending(false);
    }
  }

  async function sendSms() {
    setSmsSending(true);
    try {
      const { data } = await api.post('/admin/sms-test/preview-send', {
        bodyId: smsBodyId,
        variant: smsVariant || null,
        recipientPhoneOverride: smsRecipient || null,
      });
      setSmsBodyPreview(data.bodyPreview);
      toast.success(`Sent SMS '${data.bodyId}'${data.variant ? ` (${data.variant})` : ''} to ${data.sentTo}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'SMS send failed');
    } finally {
      setSmsSending(false);
    }
  }

  // ── Live spare-link test (v2.5.162) ──
  // Lazy-load the admin's testable games the first time they open the
  // Spare Links tab.
  useEffect(() => {
    if (mode !== 'sparelink' || spareGames !== null) return;
    api.get('/admin/spare-link-test/games')
      .then(({ data }) => {
        const list = data?.games || [];
        setSpareGames(list);
        if (list.length) setSpareGameId(prev => prev || list[0].gameId);
      })
      .catch(() => setSpareGames([]));
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedSpareGame = (spareGames || []).find(g => g.gameId === spareGameId) || null;

  async function sendSpareLinkTest() {
    setSpareSending(true);
    setSpareResult(null);
    try {
      const { data } = await api.post('/admin/spare-link-test/send', {
        gameId: spareGameId,
        scenario: spareScenario,
        recipientOverride: emailRecipient && emailRecipient !== adminEmail ? emailRecipient : null,
      });
      setSpareResult(data);
      if (data?.skipped) {
        toast.error('Email provider not configured — nothing actually sent.');
      } else {
        toast.success(`Sent ${data.scenario} spare test to ${data.sentTo}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Spare-link test failed');
    } finally {
      setSpareSending(false);
    }
  }

  const labelStyle = { fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.08em', color: T.text3, textTransform: 'uppercase', marginBottom: 6 };
  const inputStyle = { ...textInputStyle, width: '100%' };
  const selectStyle = { ...inputStyle, appearance: 'auto' };
  const btnPrimary = { padding: '10px 18px', background: T.ice, color: '#000',
    border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
    fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.08em',
    textTransform: 'uppercase' };
  const btnSecondary = { ...btnPrimary, background: 'transparent', color: T.text2,
    border: `1px solid ${T.border}` };

  return (
    <div style={{ maxWidth: 920 }}>
      {/* Mode switcher */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {[{ id: 'email', label: 'Email Templates' }, { id: 'sms', label: 'SMS Bodies' }, { id: 'sparelink', label: '🔗 Live Spare Links' }].map(m => (
          <button key={m.id} type="button" onClick={() => setMode(m.id)}
                  style={{
                    padding: '6px 14px', fontSize: 11, fontWeight: 600,
                    background: mode === m.id ? T.ice : 'transparent',
                    color: mode === m.id ? '#000' : T.text3,
                    border: `1px solid ${mode === m.id ? T.ice : T.border}`,
                    borderRadius: 8, cursor: 'pointer',
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                  }}>
            {m.label}
          </button>
        ))}
      </div>

      {!catalog ? (
        <div style={{ color: T.text3 }}>Loading…</div>
      ) : mode === 'email' ? (
        // ─── EMAIL TESTER ─────────────────────────────────────
        <div style={cardStyle}>
          <h3 style={{ ...labelStyle, fontSize: 13, marginBottom: 14, color: T.text }}>📧 Email Template Tester</h3>

          <div style={{ marginBottom: 12 }}>
            <div style={labelStyle}>Template</div>
            <select value={emailTemplate} onChange={(e) => setEmailTemplate(e.target.value)} style={selectStyle}>
              {catalog.templates.map(t => (
                <option key={t.name} value={t.name}>{t.name} — {t.description}</option>
              ))}
            </select>
          </div>

          {emailTemplateDef?.variants?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={labelStyle}>Variant</div>
              <select value={emailVariant} onChange={(e) => setEmailVariant(e.target.value)} style={selectStyle}>
                {emailTemplateDef.variants.map(v => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <div style={labelStyle}>Recipient email</div>
            <input type="email" value={emailRecipient} onChange={(e) => setEmailRecipient(e.target.value)}
                   placeholder={adminEmail || 'you@example.com'} style={inputStyle} />
            <div style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>
              Defaults to your admin email. Change if you want to test on a different inbox (e.g. phone).
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={previewEmail} disabled={emailPreviewing || !emailTemplate}
                    style={{ ...btnSecondary, opacity: emailPreviewing ? 0.5 : 1 }}>
              {emailPreviewing ? 'Rendering…' : 'Preview (no send)'}
            </button>
            <button type="button" onClick={sendEmail} disabled={emailSending || !emailTemplate}
                    style={{ ...btnPrimary, opacity: emailSending ? 0.5 : 1 }}>
              {emailSending ? 'Sending…' : 'Send to recipient'}
            </button>
          </div>

          {preview && (
            <div style={{ marginTop: 18, padding: 12, background: T.s2, borderRadius: 8, border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 11, color: T.text3, marginBottom: 4 }}>Subject</div>
              <div style={{ fontSize: 14, color: T.text, marginBottom: 12, fontFamily: 'var(--font-mono)' }}>{preview.subject || '(no subject)'}</div>
              <div style={{ fontSize: 11, color: T.text3, marginBottom: 4 }}>HTML preview</div>
              <iframe
                title="email-preview"
                srcDoc={preview.html || '(no html)'}
                sandbox=""
                style={{ width: '100%', height: 600, border: `1px solid ${T.border}`, borderRadius: 6, background: '#fff' }}
              />
            </div>
          )}
        </div>
      ) : mode === 'sms' ? (
        // ─── SMS TESTER ───────────────────────────────────────
        <div style={cardStyle}>
          <h3 style={{ ...labelStyle, fontSize: 13, marginBottom: 14, color: T.text }}>💬 SMS Body Tester</h3>

          <div style={{ marginBottom: 12 }}>
            <div style={labelStyle}>SMS body</div>
            <select value={smsBodyId} onChange={(e) => setSmsBodyId(e.target.value)} style={selectStyle}>
              {catalog.smsBodies.map(b => (
                <option key={b.id} value={b.id}>{b.id} — {b.description}</option>
              ))}
            </select>
          </div>

          {smsBodyDef?.variants?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={labelStyle}>Variant</div>
              <select value={smsVariant} onChange={(e) => setSmsVariant(e.target.value)} style={selectStyle}>
                {smsBodyDef.variants.map(v => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <div style={labelStyle}>Recipient phone (E.164, optional)</div>
            <input type="tel" value={smsRecipient} onChange={(e) => setSmsRecipient(e.target.value)}
                   placeholder="+15195551234 — leave blank to use your verified phone" style={inputStyle} />
            <div style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>
              Defaults to your verified phone (set in your profile). Override to test on a different number.
              Format: <code>+15195551234</code>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={sendSms} disabled={smsSending || !smsBodyId}
                    style={{ ...btnPrimary, opacity: smsSending ? 0.5 : 1 }}>
              {smsSending ? 'Sending…' : 'Send SMS to recipient'}
            </button>
          </div>

          {smsBodyPreview && (
            <div style={{ marginTop: 18, padding: 12, background: T.s2, borderRadius: 8, border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 11, color: T.text3, marginBottom: 4 }}>Body sent ({smsBodyPreview.length} chars)</div>
              <div style={{ fontSize: 13, color: T.text, fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap' }}>{smsBodyPreview}</div>
            </div>
          )}

          <div style={{ marginTop: 14, padding: 10, background: T.s2, borderRadius: 6, fontSize: 11, color: T.text3, lineHeight: 1.5 }}>
            <strong style={{ color: T.text2 }}>Note:</strong> SMS test sends bypass user consent gates
            (treated as admin-purpose, not a notification). They WILL count against your provider's
            quota and rate limits. The body shown matches what the scheduler/inbound handler
            actually builds in production.
          </div>
        </div>
      ) : (
        // ─── LIVE SPARE-LINK TESTER (v2.5.162) ────────────────
        <div style={cardStyle}>
          <h3 style={{ ...labelStyle, fontSize: 13, marginBottom: 6, color: T.text }}>🔗 Live Spare-Link Tester</h3>
          <p style={{ fontSize: 12, color: T.text3, lineHeight: 1.6, marginBottom: 16 }}>
            Sends a <strong style={{ color: T.text2 }}>real</strong> spare email to you, built with your own account and a
            real upcoming game — so the deep links actually work when you tap them. This lets you verify
            the full round-trip (tap → confirm/pay → game page) end-to-end. It only ever changes
            <strong style={{ color: T.text2 }}> your own</strong> RSVP on the chosen game; undo it any time by tapping
            "Mark me out" or changing your RSVP in the app.
          </p>

          {spareGames === null ? (
            <div style={{ color: T.text3 }}>Loading your upcoming games…</div>
          ) : spareGames.length === 0 ? (
            <div style={{ padding: 12, background: T.s2, borderRadius: 8, fontSize: 12, color: T.text3, lineHeight: 1.5 }}>
              No upcoming games found on teams you belong to. Join a team with an upcoming game (or create one)
              to run a live spare-link test.
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>Game</div>
                <select value={spareGameId} onChange={(e) => setSpareGameId(e.target.value)} style={selectStyle}>
                  {spareGames.map(g => {
                    const d = g.gameDate ? String(g.gameDate).slice(0, 10) : '';
                    return (
                      <option key={g.gameId} value={g.gameId}>
                        {d} — {g.teamName}{g.sparePayRequired ? ` (charges $${(g.spareFeeCents/100).toFixed(2)})` : ' (no fee)'}
                      </option>
                    );
                  })}
                </select>
                {selectedSpareGame && (
                  <div style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>
                    {selectedSpareGame.sparePayRequired
                      ? `This team requires spare payment — good for testing the PAID flow (pending payment → Pay Now).`
                      : `This team does NOT charge spares — taps will confirm you straight in. Pick a paying team to test the pay flow.`}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>Scenario</div>
                <select value={spareScenario} onChange={(e) => setSpareScenario(e.target.value)} style={selectStyle}>
                  <option value="paid">Paid spare invite (exercises the pay gate if the team charges)</option>
                  <option value="free">Free invite (coach waives the fee)</option>
                  <option value="decline">Decline (tap "Mark me out")</option>
                </select>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={labelStyle}>Recipient email</div>
                <input type="email" value={emailRecipient} onChange={(e) => setEmailRecipient(e.target.value)}
                       placeholder={adminEmail || 'you@example.com'} style={inputStyle} />
                <div style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>
                  Defaults to your admin email. The links act as <strong style={{ color: T.text2 }}>you</strong> regardless of
                  which inbox you send to, so you can send to your phone and tap there.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={sendSpareLinkTest} disabled={spareSending || !spareGameId}
                        style={{ ...btnPrimary, opacity: spareSending ? 0.5 : 1 }}>
                  {spareSending ? 'Sending…' : 'Send live test email'}
                </button>
              </div>

              {spareResult && (
                <div style={{ marginTop: 18, padding: 14, background: T.s2, borderRadius: 8, border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 6 }}>
                    {spareResult.skipped ? '⚠️ Not sent (provider unconfigured)' : `✅ Sent to ${spareResult.sentTo}`}
                  </div>
                  <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.6 }}>
                    <strong>What to expect when you tap:</strong><br />
                    {spareResult.expectation}
                  </div>
                </div>
              )}
            </>
          )}

          <div style={{ marginTop: 14, padding: 10, background: T.s2, borderRadius: 6, fontSize: 11, color: T.text3, lineHeight: 1.5 }}>
            <strong style={{ color: T.text2 }}>Tip:</strong> to test the paid flow, pick a game whose team has
            "spare must pay" turned on (shown as "charges $…" above). For the free flow, any game works —
            the free invite waives the fee regardless of team settings.
          </div>
        </div>
      )}
    </div>
  );
}
